import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import Hls from 'hls.js';
import { useSettings } from '../../contexts/SettingsContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTimezone } from '../../contexts/TimezoneContext';
import { useSyncContext } from '../../contexts/SyncContext';
import { useInterviewGeneration } from '../../contexts/InterviewGenerationContext';
import aiAutomationApi from '../../utils/aiAutomationApi';
import coreApi from '../../utils/coreApi';
import { getMetadata, getCid, getAuthToken } from '../../utils/encryptedStorage';
import { isMailProviderAuthError, handleMailProviderReconnect, getGooglePermissionError } from '../../utils/gmailOAuth';
import { RESUME_LIBRARY_LABEL } from '../../utils/uxLabels';
import Button from '../../components/Button';
import InputBox from '../../components/InputBox';
import MeetingAnalysisModal from '../../components/MeetingAnalysisModal';
import RejectCandidateModal from '../../components/InterviewScheduler/RejectCandidateModal';
import DropdownBox from '../../components/DropdownBox';
import DraftMessageModal from './DraftMessageModal';
import Toast from '../../components/Toast';
import InterviewScheduler from '../../components/InterviewScheduler';
import { MessageHistorySkeleton } from '../../components/SkeletonLoader';
import { formatTime12hr } from '../../utils/timeFormat';
import ScoreDetailsInfo from '../../components/ScoreDetailsInfo';
import { canUseCompanyFeature } from '../../utils/companyFeatureAccess';

const assertOutreachEmailSent = (response) => {
  const failedResult = response?.results?.find((result) => result?.success === false);
  const errorCode = response?.error_code || failedResult?.error_code;
  const emailSent = Number(response?.emailSent ?? response?.totalSent ?? 0);
  const sendFailed = response?.success === false || emailSent < 1;

  if (!sendFailed) return response;

  const error = new Error(
    failedResult?.error
      || (errorCode === 'GMAIL_TOKEN_EXPIRED'
        ? 'Gmail authorization has expired. Please reconnect Gmail.'
        : errorCode === 'OUTLOOK_TOKEN_EXPIRED'
          ? 'Outlook authorization has expired. Please reconnect Outlook.'
        : 'Failed to send outreach email.')
  );
  error.response = {
    data: {
      ...response,
      error_code: errorCode,
    },
  };
  throw error;
};

/**
 * CandidateDetail — Full candidate detail view per Figma design
 * Shows breadcrumb, header, 4 info cards, 4 detail sections,
 * conversation history timeline, funnel stage, suggested next step, action buttons.
 * 
 * Renders inside the ChatPage content area (no AppHeader — parent handles that).
 */
const CandidateDetail = ({
  candidate: rawCandidate,
  searchId,
  sessionId,
  onBack,
  onCandidateUpdate,
  onRejected,
  jobTitle = '',
  inline = false,
  backLabel = 'Candidate List',
  featureAccess = null,
}) => {
  const { settings } = useSettings();
  const { isDark } = useTheme();
  const { ianaTimezone } = useTimezone();
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const secondaryColor = settings.theme?.colors?.secondary || '#20C997';
  const canUseManualInterview = canUseCompanyFeature(featureAccess, 'manual_interview');
  const canUseAiInterview = canUseCompanyFeature(featureAccess, 'ai_interview');
  const canUseInterviews = canUseManualInterview || canUseAiInterview;
  const canUseOutreach = canUseCompanyFeature(featureAccess, 'outreach');

  // ── Normalize candidate data (skills may come as string from raw API) ──
  const parseSkills = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') { try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; } }
    return [];
  };
  const candidate = {
    ...rawCandidate,
    skills: parseSkills(rawCandidate?.skills),
    education: (() => {
      const ed = rawCandidate?.education_records || rawCandidate?.education;
      if (!ed) return [];
      if (Array.isArray(ed)) return ed;
      if (typeof ed === 'string') { try { const p = JSON.parse(ed); return Array.isArray(p) ? p : []; } catch { return []; } }
      return [];
    })(),
    experience: (() => {
      const ex = rawCandidate?.experience_records || rawCandidate?.experience;
      if (!ex) return [];
      if (Array.isArray(ex)) return ex;
      if (typeof ex === 'string') { try { const p = JSON.parse(ex); return Array.isArray(p) ? p : []; } catch { return []; } }
      return [];
    })(),
    name: rawCandidate?.name || 'Unknown',
    email: rawCandidate?.email || '',
    title: rawCandidate?.headline || rawCandidate?.current_role || rawCandidate?.title || '',
    company: rawCandidate?.current_company || rawCandidate?.company || '',
    location: rawCandidate?.location || '',
    phone: rawCandidate?.phone || rawCandidate?.mobile_number || '',
    linkedin_profile_url: rawCandidate?.linkedin_profile_url || rawCandidate?.profile_url || '',
    experience_years: rawCandidate?.experience_years || rawCandidate?.years_of_experience || '',
    summary: rawCandidate?.summary || rawCandidate?.summary_text || '',
    highlights: (() => {
      const h = rawCandidate?.highlights;
      if (!h) return [];
      if (Array.isArray(h)) return h;
      if (typeof h === 'string') { try { const p = JSON.parse(h); return Array.isArray(p) ? p : []; } catch { return []; } }
      return [];
    })(),
    match_breakdown: (() => {
      const mb = rawCandidate?.match_breakdown;
      if (!mb) return null;
      if (typeof mb === 'object' && !Array.isArray(mb)) return mb;
      if (typeof mb === 'string') { try { return JSON.parse(mb); } catch { return null; } }
      return null;
    })(),
    score_details: (() => {
      const details = rawCandidate?.score_details;
      if (!details) return {};
      if (typeof details === 'object' && !Array.isArray(details)) return details;
      if (typeof details === 'string') { try { return JSON.parse(details); } catch { return {}; } }
      return {};
    })(),
    source: (() => {
      // Use source_type from database and map to display label
      const sourceType = (rawCandidate?.source_type || rawCandidate?._source_type || '').toString().toLowerCase();
      const platform = (rawCandidate?.platforms || '').toString().toLowerCase();
      
      if (sourceType === 'google' || sourceType === 'google_search') {
        return 'Online Sourcing';
      }
      if (sourceType === 'linkedin' || sourceType === 'linkedin_paid') {
        return 'LinkedIn';
      }
      if (sourceType === 'naukri') {
        return 'Naukri';
      }
      if (sourceType === 'resume_box') {
        return RESUME_LIBRARY_LABEL;
      }
      if (sourceType === 'imported') {
        return 'Imported';
      }
      if (sourceType === 'job_post') {
        // For job_post, show "Job Post - LinkedIn" or "Job Post - Naukri" based on platform
        if (platform.includes('linkedin')) {
          return 'Job Post - LinkedIn';
        } else if (platform.includes('naukri')) {
          return 'Job Post - Naukri';
        }
        return 'Job Post';
      }
      
      // Fallback to platforms array with proper mapping
      const platforms = rawCandidate?.platforms;
      let platformsList = [];
      if (Array.isArray(platforms)) {
        platformsList = platforms;
      } else if (typeof platforms === 'string') {
        try { platformsList = JSON.parse(platforms); } catch { platformsList = []; }
      }
      if (platformsList.length > 0) {
        const firstPlatform = (platformsList[0] || '').toString().toLowerCase();
        if (firstPlatform.includes('linkedin')) return 'LinkedIn';
        if (firstPlatform.includes('google')) return 'Online Sourcing';
        if (firstPlatform.includes('naukri')) return 'Naukri';
        if (firstPlatform.includes('resume_box')) return RESUME_LIBRARY_LABEL;
        return platformsList[0]; // Return raw value if no mapping
      }
      
      return rawCandidate?.source || 'Unknown';
    })(),
  };
  const email = candidate?.email || '';
  const candidateRecordId = candidate?.id || rawCandidate?.id || '';
  const name = candidate?.name || 'Unknown';
  const getResumeRequestJobValue = useCallback((values, placeholder) => {
    const value = values.find((item) => {
      if (Array.isArray(item)) return item.length > 0;
      return item !== undefined && item !== null && String(item).trim();
    });
    if (Array.isArray(value)) {
      const joined = value.map((item) => typeof item === 'string' ? item : item?.name || item?.skill || '').filter(Boolean).join(', ');
      return joined || placeholder;
    }
    return value ? String(value).trim() : placeholder;
  }, []);
  const buildResumeRequestDraft = useCallback(() => {
    const role = getResumeRequestJobValue([
      jobTitle,
      rawCandidate?.job_title,
      rawCandidate?.jobTitle,
      rawCandidate?.position_title,
    ], '[JOB_TITLE]');
    const company = getResumeRequestJobValue([
      rawCandidate?.job_company_name,
      rawCandidate?.company_name,
      rawCandidate?.companyName,
      rawCandidate?.hiring_company,
    ], '[COMPANY_NAME]');
    const location = getResumeRequestJobValue([
      rawCandidate?.job_location,
      rawCandidate?.jobLocation,
      rawCandidate?.work_location,
    ], '[JOB_LOCATION]');
    const employmentType = getResumeRequestJobValue([
      rawCandidate?.employment_type,
      rawCandidate?.job_type,
    ], '[EMPLOYMENT_TYPE]');
    const experience = getResumeRequestJobValue([
      rawCandidate?.experience_range,
      rawCandidate?.required_experience,
      rawCandidate?.min_experience && rawCandidate?.max_experience ? `${rawCandidate.min_experience}-${rawCandidate.max_experience} years` : '',
    ], '[EXPERIENCE_RANGE]');
    const skills = getResumeRequestJobValue([
      rawCandidate?.job_skills,
      rawCandidate?.required_skills,
      rawCandidate?.primary_skills,
    ], '[KEY_SKILLS]');

    return {
      subject: `Resume requested for ${role} at ${company}`,
      body: `Hi ${name || '[CANDIDATE_NAME]'},\n\nWe are reviewing profiles for ${role} at ${company}. To continue with your application, please reply to this email with your latest resume.\n\nJob details:\n- Role: ${role}\n- Company: ${company}\n- Location: ${location}\n- Employment type: ${employmentType}\n- Experience: ${experience}\n- Key skills: ${skills}\n\nPlease share your resume as a PDF, DOC, or DOCX attachment.\n\nRegards,\n${company} Hiring Team`,
    };
  }, [getResumeRequestJobValue, jobTitle, name, rawCandidate]);

  // State
  const [replies, setReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [notes, setNotes] = useState([]);

  // Draft modal state (used for both Scene 2 reply & Scene 3 followup)
  const [draftModal, setDraftModal] = useState({
    isOpen: false,
    title: '',
    subject: '',
    body: '',
    candidateQuestion: '',
    scene: '', // 'reply' | 'followup'
    draftId: null,
  });
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftSending, setDraftSending] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'info', visible: false });

  const showToast = useCallback((message, type = 'error') => {
    setToast({ message, type, visible: true });
  }, []);
  const closeToast = useCallback(() => {
    setToast((prev) => ({ ...prev, visible: false }));
  }, []);

  // Whether this candidate has a question mail (draft exists)
  const [hasQuestionDraft, setHasQuestionDraft] = useState(false);
  // Whether the candidate's latest reply is a question we haven't drafted yet
  const [hasCandidateQuestion, setHasCandidateQuestion] = useState(false);
  // Followup cooldown warning state
  const [followupWarningModal, setFollowupWarningModal] = useState({ isOpen: false, lastSentAt: null });
  // Manual followup cooldown from mail settings (default: 48h = 172800s)
  const [followupCooldownHours, setFollowupCooldownHours] = useState(48);

  // Resume Request Modal state (for Mark As Priority flow)
  const [resumeRequestModal, setResumeRequestModal] = useState({
    isOpen: false,
    draftSubject: '',
    draftBody: '',
    loading: false,
  });
  const [priorityLoading, setPriorityLoading] = useState(false);
  const [isPriority, setIsPriority] = useState(candidate?.priority || false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [resumeCheckModalOpen, setResumeCheckModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null); // for message detail modal
  
  // AI Notes state
  const [aiNotes, setAiNotes] = useState([]);
  const [loadingAiNotes, setLoadingAiNotes] = useState(false);
  const [selectedAiNote, setSelectedAiNote] = useState(null);
  const [meetingAnalysisModalOpen, setMeetingAnalysisModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [fetchedRejectionReason, setFetchedRejectionReason] = useState('');
  const [downloadingRecordingForInterviewId, setDownloadingRecordingForInterviewId] = useState(null);
  const [fetchingRecordingUrlForInterviewId, setFetchingRecordingUrlForInterviewId] = useState(null);
  const [aiAnalysisMap, setAiAnalysisMap] = useState({}); // { interview_id: analysis_data }
  const [analysisLoadingMap, setAnalysisLoadingMap] = useState({}); // { interview_id: boolean }
  const [meetingAssetsMap, setMeetingAssetsMap] = useState({}); // { interview_id: { video_url, highlights } }
  const [meetingAssetsLoadingMap, setMeetingAssetsLoadingMap] = useState({}); // { interview_id: boolean }
  const [showConversationMap, setShowConversationMap] = useState({}); // { interview_id: boolean }
  const [showSummaryMap, setShowSummaryMap] = useState({}); // { interview_id: boolean }
  const [showTranscriptMap, setShowTranscriptMap] = useState({}); // { interview_id: boolean }
  const [selectedHighlightCategoryMap, setSelectedHighlightCategoryMap] = useState({}); // { interview_id: 'all' | category }
  const [activeHighlightModal, setActiveHighlightModal] = useState(null); // { interviewId, title, videoUrl, startTime, endTime, timestampLabel }
  const [joinMeetLoading, setJoinMeetLoading] = useState(false);
  const highlightModalVideoRef = useRef(null);
  const highlightModalHlsRef = useRef(null);
  const onCandidateUpdateRef = useRef(onCandidateUpdate);

  useEffect(() => {
    onCandidateUpdateRef.current = onCandidateUpdate;
  }, [onCandidateUpdate]);

  const syncCandidateStageLocally = useCallback((nextStage, extraUpdates = {}) => {
    if (!onCandidateUpdateRef.current || !email) return;
    onCandidateUpdateRef.current(email, {
      funnel_stage: nextStage,
      status: nextStage,
      stage: nextStage,
      candidate_status: nextStage,
      pipeline_status: nextStage,
      updated_at: new Date().toISOString(),
      ...extraUpdates,
    });
  }, [email]);

  const parseJsonValue = useCallback((value, fallback) => {
    if (value == null) return fallback;
    if (typeof value !== 'string') return value;
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }, []);

  const normalizeTranscript = useCallback((value) => {
    if (!value) return '';
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return '';
      const parsed = parseJsonValue(trimmed, trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => {
            if (typeof item === 'string') return item;
            if (item && typeof item === 'object') {
              return item.text || item.content || item.transcript || JSON.stringify(item);
            }
            return String(item || '');
          })
          .filter(Boolean)
          .join('\n');
      }
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        if (typeof parsed.transcript === 'string') return parsed.transcript;
        if (Array.isArray(parsed.transcript)) return normalizeTranscript(parsed.transcript);
      }
      return trimmed;
    }
    if (Array.isArray(value)) {
      return value.map((item) => normalizeTranscript(item)).filter(Boolean).join('\n');
    }
    if (typeof value === 'object') {
      if (typeof value.transcript === 'string' || Array.isArray(value.transcript)) {
        return normalizeTranscript(value.transcript);
      }
      return '';
    }
    return String(value);
  }, [parseJsonValue]);

  const normalizeSpeakers = useCallback((value) => {
    const parsed = parseJsonValue(value, value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((speaker, index) => {
        if (!speaker || typeof speaker !== 'object') return null;
        const rawContributions = parseJsonValue(speaker.contributions, speaker.contributions);
        const contributions = Array.isArray(rawContributions)
          ? rawContributions.flatMap((item) => {
              if (typeof item === 'string') return item.trim() ? [item] : [];
              if (item && typeof item === 'object') {
                const text = item.text || item.content || item.transcript || '';
                return text ? [String(text)] : [];
              }
              return [];
            })
          : typeof rawContributions === 'string' && rawContributions.trim()
            ? [rawContributions]
            : [];
        return {
          name: speaker.name || `Speaker ${index + 1}`,
          role: speaker.role || '',
          contributions,
        };
      })
      .filter((speaker) => speaker && speaker.contributions.length > 0);
  }, [parseJsonValue]);

  const normalizeMeetingNotes = useCallback((notes) => {
    const parsed = parseJsonValue(notes, notes);
    const noteObject = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    return {
      ...noteObject,
      executive_summary: typeof noteObject.executive_summary === 'string' ? noteObject.executive_summary : normalizeTranscript(noteObject.executive_summary),
      transcript: normalizeTranscript(noteObject.transcript),
      speakers: normalizeSpeakers(noteObject.speakers),
    };
  }, [normalizeSpeakers, normalizeTranscript, parseJsonValue]);

  const formatSecondsAsTimestamp = useCallback((value) => {
    const totalSeconds = Number.isFinite(Number(value)) ? Math.max(0, Math.floor(Number(value))) : 0;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
      return [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':');
    }
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }, []);

  const normalizeMeetingHighlights = useCallback((highlights) => {
    if (!Array.isArray(highlights)) return [];
    return highlights
      .map((highlight, index) => {
        if (!highlight || typeof highlight !== 'object') return null;
        const rawTimestamp = Number(highlight.timestamp);
        const timestamp = Number.isFinite(rawTimestamp) ? rawTimestamp : 0;
        const rawClipStart = Number(highlight.clip_start);
        const rawClipEnd = Number(highlight.clip_end);
        const clipStart = Number.isFinite(rawClipStart) ? Math.max(0, rawClipStart) : Math.max(0, timestamp - 5);
        const clipEnd = Number.isFinite(rawClipEnd) ? Math.max(clipStart + 1, rawClipEnd) : Math.max(clipStart + 1, timestamp + 20);
        return {
          id: highlight.id || `${highlight.timestamp_formatted || timestamp}-${index}`,
          title: String(highlight.title || 'Interview highlight').trim(),
          description: String(highlight.description || 'Key interview moment').trim(),
          category: String(highlight.category || 'general').trim().toLowerCase(),
          timestamp,
          clipStart,
          clipEnd,
          clipDuration: Math.max(1, clipEnd - clipStart),
          timestampLabel: highlight.timestamp_formatted || formatSecondsAsTimestamp(timestamp),
        };
      })
      .filter(Boolean);
  }, [formatSecondsAsTimestamp]);

  const extractConversationEntries = useCallback((notesData) => {
    const speakerMessages = [];
    (notesData?.speakers || []).forEach((speaker) => {
      (speaker.contributions || []).forEach((contrib) => {
        const text = String(contrib || '').trim();
        if (!text) return;
        const match = text.match(/^\[(\d{1,2}):(\d{2})(?::(\d{2}))?\]\s*(.*)/);
        if (match) {
          let hours;
          let minutes;
          let seconds;
          let body;
          if (match[3]) {
            hours = parseInt(match[1], 10);
            minutes = parseInt(match[2], 10);
            seconds = parseInt(match[3], 10);
            body = match[4] || '';
          } else {
            hours = 0;
            minutes = parseInt(match[1], 10);
            seconds = parseInt(match[2], 10);
            body = match[3] || match[4] || '';
          }
          speakerMessages.push({
            timestamp: hours * 3600 + minutes * 60 + seconds,
            speaker: speaker.name,
            role: speaker.role,
            text: body || text,
            timeStr: match[1] + ':' + match[2] + (match[3] ? ':' + match[3] : ''),
          });
        } else {
          speakerMessages.push({
            timestamp: 999999,
            speaker: speaker.name,
            role: speaker.role,
            text,
            timeStr: '',
          });
        }
      });
    });

    if (speakerMessages.length > 0) {
      return speakerMessages.sort((a, b) => a.timestamp - b.timestamp);
    }

    const transcriptLines = normalizeTranscript(notesData?.transcript)
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    return transcriptLines.map((line, index) => {
      const bracketMatch = line.match(/^\[(\d{1,2}:\d{2}(?::\d{2})?)\]\s*([^:]+):\s*(.*)$/);
      if (bracketMatch) {
        return {
          timestamp: index,
          speaker: bracketMatch[2].trim(),
          role: '',
          text: bracketMatch[3].trim(),
          timeStr: bracketMatch[1],
        };
      }

      const plainMatch = line.match(/^([^:]+):\s*(.*)$/);
      if (plainMatch) {
        return {
          timestamp: index,
          speaker: plainMatch[1].trim(),
          role: '',
          text: plainMatch[2].trim(),
          timeStr: '',
        };
      }

      return {
        timestamp: index,
        speaker: 'Transcript',
        role: '',
        text: line,
        timeStr: '',
      };
    });
  }, [normalizeTranscript]);

  const extractAIInterviewTranscriptEntries = useCallback((result) => {
    const formatTranscriptTime = (value) => {
      const raw = String(value || '').trim();
      if (!raw) return '';
      const parsedDate = new Date(raw);
      if (!Number.isNaN(parsedDate.getTime())) {
        const options = { hour: '2-digit', minute: '2-digit' };
        if (ianaTimezone) options.timeZone = ianaTimezone;
        return parsedDate.toLocaleTimeString([], options);
      }
      const timeMatch = raw.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
      if (!timeMatch) return '';
      return `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`;
    };
    const normalizeSpeaker = (value) => {
      const raw = String(value || '').trim();
      const lower = raw.toLowerCase();
      if (lower.includes('candidate') || lower.includes('applicant') || lower === 'user') return 'Candidate';
      if (lower.includes('interviewer') || lower.includes('assistant') || lower.includes('wiza') || lower.includes('ai')) return 'Wiza';
      return raw || 'Wiza';
    };
    const isMetadataLine = (line) => {
      const trimmed = String(line || '').trim();
      if (!trimmed) return true;
      if (/^#{1,6}\s*(interview transcript|interview_id|session_id|candidate_id|candidate_name|candidate|role|started_at|ended_at|duration|metadata)\b/i.test(trimmed)) return true;
      if (/^(interview_id|session_id|candidate_id|candidate_name|role|started_at|ended_at|duration)\s*[:=]/i.test(trimmed)) return true;
      if (/^(intv_|session_)/i.test(trimmed)) return true;
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(trimmed) && !/(candidate|interviewer|assistant|wiza|ai)\s*:/i.test(trimmed)) return true;
      return false;
    };
    const parseTranscriptLine = (line, index) => {
      const rawLine = String(line || '').trim();
      if (isMetadataLine(rawLine)) return null;
      const stampedMatch = rawLine.match(/^\[?([^\]]*?\d{1,2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:[+-]\d{2}:?\d{2}|Z)?)\]?\s*(Candidate|Interviewer|Assistant|Wiza|AI)\s*:\s*(.*)$/i);
      if (stampedMatch) {
        const speaker = normalizeSpeaker(stampedMatch[2]);
        return {
          timestamp: index,
          speaker,
          role: speaker.toLowerCase(),
          text: stampedMatch[3].trim(),
          timeStr: formatTranscriptTime(stampedMatch[1]),
        };
      }
      const bracketMatch = rawLine.match(/^\[(\d{1,2}:\d{2}(?::\d{2})?)\]\s*(Candidate|Interviewer|Assistant|Wiza|AI)\s*:\s*(.*)$/i);
      if (bracketMatch) {
        const speaker = normalizeSpeaker(bracketMatch[2]);
        return {
          timestamp: index,
          speaker,
          role: speaker.toLowerCase(),
          text: bracketMatch[3].trim(),
          timeStr: formatTranscriptTime(bracketMatch[1]),
        };
      }
      const plainMatch = rawLine.match(/^(Candidate|Interviewer|Assistant|Wiza|AI)\s*:\s*(.*)$/i);
      if (plainMatch) {
        const speaker = normalizeSpeaker(plainMatch[1]);
        return {
          timestamp: index,
          speaker,
          role: speaker.toLowerCase(),
          text: plainMatch[2].trim(),
          timeStr: '',
        };
      }
      return null;
    };

    const rawTurns = result?.turns || result?.messages || result?.transcript || result?.conversation || null;
    const parsedTurns = parseJsonValue(rawTurns, rawTurns);
    if (Array.isArray(parsedTurns)) {
      return parsedTurns
        .map((turn, index) => {
          if (typeof turn === 'string') {
            return parseTranscriptLine(turn, index);
          }
          if (!turn || typeof turn !== 'object') return null;
          const rawRole = String(turn.role || turn.speaker || turn.author || '').trim();
          const text = String(turn.text || turn.content || turn.message || turn.transcript || '').trim();
          if (!text) return null;
          const speaker = normalizeSpeaker(rawRole || (String(turn.type || '').toLowerCase().includes('candidate') ? 'Candidate' : 'Wiza'));
          return {
            timestamp: Number.isFinite(Number(turn.timestamp)) ? Number(turn.timestamp) : index,
            speaker,
            role: speaker.toLowerCase(),
            text,
            timeStr: formatTranscriptTime(turn.time || turn.timeStr || turn.created_at || turn.timestamp_iso || ''),
          };
        })
        .filter(Boolean);
    }

    const transcriptLines = normalizeTranscript(result?.transcript_text || result?.transcript || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    return transcriptLines
      .map((line, index) => parseTranscriptLine(line, index))
      .filter(Boolean);
  }, [ianaTimezone, normalizeTranscript, parseJsonValue]);

  const isCandidateTranscriptEntry = useCallback((entry) => {
    const role = String(entry?.role || entry?.speaker || '').toLowerCase();
    return role.includes('candidate') || role.includes('applicant') || role === 'user';
  }, []);

  const isInterviewNoteCompleted = useCallback((note) => {
    if (!note) return false;

    // Backend completion markers are authoritative even if status string is stale.
    if (note.meeting_completed || note.notes_cached_at) {
      return true;
    }

    const rawStatus = String(note.ai_note_taker_status || '').trim().toLowerCase();
    const activeStatuses = ['recording', 'active', 'joined', 'joining', 'in_progress', 'bot_requested', 'awaiting_admission', 'pending'];
    if (activeStatuses.includes(rawStatus)) return false;

    if (['completed', 'recording_completed', 'ended', 'left', 'done', 'finished', 'disconnected', 'closed'].includes(rawStatus)) {
      return true;
    }

    return false;
  }, []);

  const canOpenInterviewNotes = useCallback((interview) => {
    if (!interview) return false;

    const interviewType = String(interview.interview_type || '').trim().toLowerCase();
    const status = String(interview.status || '').trim().toLowerCase();
    if (interviewType === 'ai') {
      return Boolean(interview.id && (interview.meeting_completed || status === 'completed'));
    }

    const noteTakerStatus = String(interview.ai_note_taker_status || '').trim().toLowerCase();
    if (['completed', 'recording_completed', 'ended', 'left', 'done', 'finished', 'disconnected', 'closed'].includes(noteTakerStatus)) {
      return Boolean(interview.ai_note_taker_meeting_id || interview.notes_cached_at || interview.meeting_completed);
    }

    if (interview.meeting_completed && (interview.notes_cached_at || interview.ai_note_taker_meeting_id)) {
      return true;
    }

    if (status === 'completed' && (interview.notes_cached_at || interview.ai_note_taker_meeting_id)) {
      return true;
    }

    return false;
  }, []);

  const getInterviewNoteStatusMeta = useCallback((note) => {
    const rawStatus = String(note?.ai_note_taker_status || '').trim().toLowerCase();

    if (isInterviewNoteCompleted(note)) {
      return {
        label: note?.notes?.transcript || note?.notes?.executive_summary ? 'Completed' : 'Ended',
        className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      };
    }

    if (['recording', 'active', 'joined', 'joining', 'in_progress'].includes(rawStatus)) {
      return {
        label: 'In Progress',
        className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      };
    }

    if (rawStatus === 'awaiting_admission') {
      return {
        label: 'Awaiting Admit',
        className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
      };
    }

    if (rawStatus === 'bot_requested') {
      return {
        label: 'Bot Requested',
        className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      };
    }

    if (['error', 'failed'].includes(rawStatus)) {
      return {
        label: 'Failed',
        className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      };
    }

    return {
      label: 'Scheduled',
      className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    };
  }, [isInterviewNoteCompleted]);

  // Until the candidate's booked-interview state is resolved, the action row can't know whether to
  // show "Book Interview" vs "Interview Report" — showing it early makes the button visibly swap once
  // the fetch lands. Gate the action row on this so it renders a seamless loader instead of flashing.
  const [bookedInterviewLoading, setBookedInterviewLoading] = useState(true);

  // Whether this candidate already has a booked interview (hide Schedule button)
  const [hasBookedInterview, setHasBookedInterview] = useState(false);
  const [bookedInterviewData, setBookedInterviewData] = useState(null);
  const [missedInterviewData, setMissedInterviewData] = useState(null);
  const [canJoinBookedInterview, setCanJoinBookedInterview] = useState(false);
  const [aiInterviewResult, setAiInterviewResult] = useState(null);
  const [aiInterviewResultLoading, setAiInterviewResultLoading] = useState(false);
  const [aiInterviewRecalculating, setAiInterviewRecalculating] = useState(false);
  const [aiInterviewResultError, setAiInterviewResultError] = useState('');
  const [aiTranscriptExpanded, setAiTranscriptExpanded] = useState(true);

  // Direct Book Interview modal state
  const [directBookOpen, setDirectBookOpen] = useState(false);
  const [directBookForm, setDirectBookForm] = useState({
    date: '', startTime: '10:00', duration: 30, interviewerId: '', interviewType: 'manual',
  });
  useEffect(() => {
    setDirectBookForm((prev) => {
      if (prev.interviewType === 'ai' && !canUseAiInterview && canUseManualInterview) {
        return { ...prev, interviewType: 'manual' };
      }
      if (prev.interviewType === 'manual' && !canUseManualInterview && canUseAiInterview) {
        return { ...prev, interviewType: 'ai' };
      }
      return prev;
    });
  }, [canUseAiInterview, canUseManualInterview]);
  const [directBookInterviewers, setDirectBookInterviewers] = useState([]);
  const [directBookLoading, setDirectBookLoading] = useState(false);
  // The selected saved AI interview blueprint (if any) — AI direct-book uses this setup and its duration.
  const [directBookAiSetup, setDirectBookAiSetup] = useState(null);
  const [directBookAiSetups, setDirectBookAiSetups] = useState([]);
  const [directBookAiSetupsLoading, setDirectBookAiSetupsLoading] = useState(false);
  const directBookAiSetupDuration = Number(
    directBookAiSetup?.configuration?.duration_minutes
    || directBookAiSetup?.duration_minutes
    || 0
  );
  // Inline "quick setup" used ONLY when the job has no saved AI setup and the recruiter books AI directly.
  const [directBookAiConfig, setDirectBookAiConfig] = useState({ name: '', total_questions: 8, description: '' });

  const getAiSetupName = useCallback((setup) => (
    setup?.display_name || setup?.setup_name || setup?.name || 'AI interview setup'
  ), []);

  const selectDirectBookAiSetup = useCallback((setup) => {
    setDirectBookAiSetup(setup || null);
    const setupDuration = Number(setup?.configuration?.duration_minutes || setup?.duration_minutes || 0);
    if (setupDuration) {
      setDirectBookForm(prev => ({
        ...prev,
        duration: prev.interviewType === 'ai' ? setupDuration : prev.duration,
      }));
    }
  }, []);

  // Reschedule Request state — pending reschedule requests for this candidate
  const [rescheduleRequest, setRescheduleRequest] = useState(null); // latest pending/needs_manual_action request
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [rescheduleActionLoading, setRescheduleActionLoading] = useState(false);

  const handleJoinMeet = useCallback(async () => {
    const interviewId = bookedInterviewData?.id;
    const meetLink = bookedInterviewData?.google_meet_link;
    if (!interviewId || !meetLink) return;

    setJoinMeetLoading(true);
    try {
      const response = await aiAutomationApi.post(`/interviews/${interviewId}/join-meeting`, {});
      if (!response?.success) {
        throw new Error(response?.message || response?.error || 'Failed to trigger AI Note Taker');
      }
      window.open(meetLink, '_blank');
      showToast('AI Note Taker triggered for this meeting.', 'success');
    } catch (err) {
      console.error('[CandidateDetail] Failed to join meeting:', err);
      showToast(err?.message || 'Failed to join meeting', 'error');
    } finally {
      setJoinMeetLoading(false);
    }
  }, [bookedInterviewData?.id, bookedInterviewData?.google_meet_link, showToast]);

  // Preload cached analysis from DB, then only call OpenAI for meetings without cached analysis.
  useEffect(() => {
    if (aiNotes.length > 0) {
      const cachedAnalysis = {};
      aiNotes.forEach((note) => {
        if (note.interview_id && note.meeting_analysis_json?.success) {
          cachedAnalysis[note.interview_id] = note.meeting_analysis_json;
        }
      });
      if (Object.keys(cachedAnalysis).length > 0) {
        setAiAnalysisMap(prev => ({ ...cachedAnalysis, ...prev }));
      }
    }

    if (meetingAnalysisModalOpen && aiNotes.length > 0) {
      aiNotes.forEach(note => {
        if (note.interview_id && !aiAnalysisMap[note.interview_id] && !analysisLoadingMap[note.interview_id]) {
          handleAnalyzeWithAI(note.interview_id);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingAnalysisModalOpen, aiNotes.length]);

  const fetchMeetingAssets = useCallback(async (interviewId, { isRetry = false } = {}) => {
    if (!interviewId) return;
    if (meetingAssetsLoadingMap[interviewId]) return;

    setMeetingAssetsLoadingMap((prev) => ({ ...prev, [interviewId]: true }));
    try {
      const isLocalClient = ['localhost', '127.0.0.1'].includes(window.location.hostname);
      const endpoint = isLocalClient
        ? `/interviews/${interviewId}/meeting-assets?prefer_local=1`
        : `/interviews/${interviewId}/meeting-assets`;
      const data = await aiAutomationApi.get(endpoint);
                                                                                                                                                                                                      const normalizedHighlights = normalizeMeetingHighlights(data?.highlights);
      // Build absolute video URL from the relative path returned by the API
      const aiBaseUrl = (process.env.REACT_APP_AI_AUTOMATION_URL || 'http://localhost:8100/api/v1').replace(/\/+$/, '');
      const rawVideoUrl = data?.video_url || null;
      const absoluteVideoUrl = rawVideoUrl && rawVideoUrl.startsWith('/') ? `${aiBaseUrl}${rawVideoUrl}` : rawVideoUrl;
      setMeetingAssetsMap((prev) => ({                                                                                                                                                                                                                                            
        ...prev,                                
        [interviewId]: {
          videoUrl: absoluteVideoUrl,
          highlights: normalizedHighlights,
        },
      }));

      // If highlights came back empty and we haven't retried yet, schedule a
      // one-time re-fetch in 30 s — generation was just triggered in background.
      if (!isRetry && normalizedHighlights.length === 0 && data?.video_url) {
        setTimeout(() => {                                                                                                                                                                
          setMeetingAssetsMap((prev) => {
            // Only retry if still empty after the delay
            const current = prev[interviewId];
            if (!current || current.highlights.length === 0) {
              // Clear the cached entry so the guard in the effect allows a fresh load
              const next = { ...prev };
              delete next[interviewId];
              return next;
            }
            return prev;
          });
        }, 30000);
      }
    } catch (err) {
      console.error('[CandidateDetail] Failed to fetch meeting assets:', err);
      setMeetingAssetsMap((prev) => ({
        ...prev,
        [interviewId]: {
          videoUrl: null,
          highlights: [],
        },
      }));
    } finally {
      setMeetingAssetsLoadingMap((prev) => ({ ...prev, [interviewId]: false }));
    }
  }, [meetingAssetsLoadingMap, normalizeMeetingHighlights]);

  useEffect(() => {
    if (!meetingAnalysisModalOpen || aiNotes.length === 0) return;
    aiNotes.forEach((note) => {
      if (note?.interview_id && !meetingAssetsMap[note.interview_id] && !meetingAssetsLoadingMap[note.interview_id]) {
        fetchMeetingAssets(note.interview_id);
      }
    });
  }, [aiNotes, fetchMeetingAssets, meetingAnalysisModalOpen, meetingAssetsLoadingMap, meetingAssetsMap]);

  useEffect(() => {
    if (!meetingAnalysisModalOpen || aiNotes.length === 0) return;
    setShowConversationMap((prev) => {
      const next = { ...prev };
      aiNotes.forEach((note) => {
        if (note?.interview_id) {
          next[note.interview_id] = true;
        }
      });
      return next;
    });
  }, [meetingAnalysisModalOpen, aiNotes]);

  // Initial Outreach Modal state
  const [initialOutreachModal, setInitialOutreachModal] = useState({
    isOpen: false,
    draftSubject: '',
    draftBody: '',
    loading: false,
    sending: false,
  });

  // Resume View Modal state
  const [resumeViewModal, setResumeViewModal] = useState({
    isOpen: false,
    loading: false,
    data: null, // { resume, basic_detail, skills, work_experience, match_score }
    error: null,
    attachmentUrl: null, // URL to the physical PDF file
  });

  // AI Interview Questions state
  const { generating: generatingInterview, phase: interviewGenPhase, startGenerating: startInterviewGeneration, stopGenerating: stopInterviewGeneration } = useInterviewGeneration();
  const [interviewSessions, setInterviewSessions] = useState([]); // all sessions
  const [activeSession, setActiveSession] = useState(null);        // currently viewed session
  const [interviewSourceTab, setInterviewSourceTab] = useState('all'); // 'all' | 'jd_based' | 'candidate_based'
  const [interviewSectionFilter, setInterviewSectionFilter] = useState('all'); // 'all' | section name
  const [showAllQuestions, setShowAllQuestions] = useState(false);
  const [loadingInterview, setLoadingInterview] = useState(false);


  // Sync state — uses global SyncContext so state persists across route changes
  const { getSyncState, startSync, updateSyncState, setCurrentSearchId } = useSyncContext();
  const autoSyncTimerRef = useRef(null);

  // Sync priority state when candidate prop changes (navigation / page reload)
  useEffect(() => {
    setIsPriority(rawCandidate?.priority || false);
  }, [rawCandidate?.email, rawCandidate?.priority]);

  // Only use bookedInterviewData.id (UUID from aiautomation_interviews) —
  // activeSession.interview_id is a numeric SERIAL from the core API interview
  // question table and must NOT be sent to the aiAutomation scheduling API.
  const scopedInterviewId = bookedInterviewData?.id || null;
  const scopedMeetingId = bookedInterviewData?.ai_note_taker_meeting_id || null;
  const isFullyAIInterview = String(bookedInterviewData?.interview_type || '').toLowerCase() === 'ai'
    || Boolean(bookedInterviewData?.ai_launch_token || bookedInterviewData?.ai_interview_id);
  const hasScopedInterviewContext = interviewSessions.length > 0 || Boolean(bookedInterviewData?.id) || aiNotes.length > 0;

  const aiResultContextRef = useRef('');
  aiResultContextRef.current = `${candidate?.id || email}:${bookedInterviewData?.id || ''}`;
  const loadAIInterviewResult = useCallback(async ({ refresh = false, silent = false, recalculationBasis = 'saved_interview' } = {}) => {
    const contextKey = aiResultContextRef.current;
    if (!isFullyAIInterview || !bookedInterviewData?.id) {
      setAiInterviewResult(null);
      setAiInterviewResultLoading(false);
      setAiInterviewResultError('');
      return null;
    }
    if (refresh) setAiInterviewRecalculating(true);
    else if (!silent) setAiInterviewResultLoading(true);
    setAiInterviewResultError('');
    try {
      const query = refresh ? `?refresh=true&recalculation_basis=${encodeURIComponent(recalculationBasis)}` : '';
      const data = await aiAutomationApi.get(`/interviews/${encodeURIComponent(String(bookedInterviewData.id))}/ai-result${query}`);
      if (aiResultContextRef.current !== contextKey) return null;
      setAiInterviewResult(data);
      if (data?.interview_completed) {
        setBookedInterviewData((previous) => previous ? { ...previous, meeting_completed: true, status: 'completed' } : previous);
        syncCandidateStageLocally('Interviewed');
      }
      return data;
    } catch (error) {
      if (aiResultContextRef.current !== contextKey) return null;
      setAiInterviewResult(null);
      // Keep the modal clean — surface a friendly message instead of the raw backend detail
      // (e.g. "AI interview source identifiers are unavailable.").
      setAiInterviewResultError("The AI interview result isn't available for this candidate yet.");
      return null;
    } finally {
      if (aiResultContextRef.current === contextKey) {
        if (refresh) setAiInterviewRecalculating(false);
        else if (!silent) setAiInterviewResultLoading(false);
      }
    }
  }, [bookedInterviewData?.id, isFullyAIInterview, syncCandidateStageLocally]);

  useEffect(() => {
    let cancelled = false;
    if (!isFullyAIInterview || !bookedInterviewData?.id) {
      setAiInterviewResult(null);
      setAiInterviewResultLoading(false);
      setAiInterviewResultError('');
      return undefined;
    }
    (async () => {
      const data = await loadAIInterviewResult();
      if (cancelled || data) return;
    })();
    return () => { cancelled = true; };
  }, [bookedInterviewData?.id, isFullyAIInterview, loadAIInterviewResult]);

  const handleRecalculateAIInterviewResult = useCallback(async (recalculationBasis = 'saved_interview') => {
    const data = await loadAIInterviewResult({ refresh: true, silent: true, recalculationBasis });
    if (data?.evaluation?.refresh_failed) {
      showToast(data.evaluation.refresh_error || 'Recalculation failed. Showing the last available result.', 'warning');
      return;
    }
    if (data) showToast('AI interview score recalculated.', 'success');
  }, [loadAIInterviewResult, showToast]);

  // Resolve searchId — prop > candidate._searchId > fetch from session
  const [resolvedSearchId, setResolvedSearchId] = useState(searchId || rawCandidate?._searchId || rawCandidate?.search_id || rawCandidate?.searchId || '');

  const openResumeRequestModal = useCallback(async () => {
    const fallbackDraft = buildResumeRequestDraft();
    setResumeRequestModal({
      isOpen: true,
      draftSubject: fallbackDraft.subject,
      draftBody: fallbackDraft.body,
      loading: true,
    });
    try {
      const rendered = await aiAutomationApi.post('/mail-templates/render', {
        template_key: 'resume_request',
        session_id: sessionId,
        search_id: resolvedSearchId,
        cid: getCid() ? String(getCid()) : undefined,
        candidate_name: name || 'Candidate',
      });
      setResumeRequestModal({
        isOpen: true,
        draftSubject: rendered?.subject || fallbackDraft.subject,
        draftBody: rendered?.body || fallbackDraft.body,
        loading: false,
      });
    } catch (err) {
      console.warn('[CandidateDetail] Could not render resume request template:', err?.message || err);
      setResumeRequestModal((prev) => ({ ...prev, loading: false }));
    }
  }, [buildResumeRequestDraft, name, resolvedSearchId, sessionId]);

  // Access sync state AFTER resolvedSearchId is declared
  const syncState = getSyncState(resolvedSearchId);
  const syncing = syncState.syncing;
  const syncMessage = syncState.syncMessage;
  // A completed AI interview = one the engine actually finished. The raw aiInterviewResult object is ALSO
  // populated for a scheduled-but-not-held interview (the /ai-result endpoint returns a partial record),
  // so gate on the completion fields — not the object's mere presence — otherwise the report surfaces
  // before the interview happens. (Manual interviews already gate correctly on meeting_completed.)
  const hasCompletedAIResult = Boolean(
    aiInterviewResult?.interview_completed ||
    aiInterviewResult?.metadata?.current_state === 'INTERVIEW_COMPLETED' ||
    aiInterviewResult?.metadata?.ended_at
  );
  const visibleMeetingAnalysisCount = isFullyAIInterview
    ? (bookedInterviewData?.id && (hasCompletedAIResult || bookedInterviewData?.meeting_completed) ? 1 : 0)
    : aiNotes.filter((note) => {
        return Boolean(
          note?.interview_id &&
          (
            isInterviewNoteCompleted(note) ||
            note?.notes?.executive_summary ||
            note?.notes?.transcript ||
            (Array.isArray(note?.notes?.speakers) && note.notes.speakers.length > 0)
          )
        );
      }).length;

  useEffect(() => {
    setAiNotes([]);
    setBookedInterviewData(null);
    setHasBookedInterview(false);
  }, [candidate?.id, email, resolvedSearchId, sessionId]);

  useEffect(() => {
    if (searchId) {
      setResolvedSearchId(searchId);
      return;
    }
    if (candidate?._searchId) {
      setResolvedSearchId(candidate._searchId);
      return;
    }
    // Fallback: use candidate's search_id directly (works when session_id is string format)
    if (candidate?.search_id) {
      setResolvedSearchId(candidate.search_id);
      return;
    }
    if (candidate?.searchId) {
      setResolvedSearchId(candidate.searchId);
      return;
    }
    // Fallback: fetch from session data
    if (!sessionId) return;
    let cancelled = false;
    const fetchSearchId = async () => {
      try {
        const data = await aiAutomationApi.get(`/sessions/${sessionId}`);
        if (!cancelled && data?.searches?.length > 0) {
          setResolvedSearchId(data.searches[0].search_id || '');
        }
      } catch {
        // ignore
      }
    };
    fetchSearchId();
    return () => { cancelled = true; };
  }, [searchId, sessionId, candidate?._searchId, candidate?.search_id, candidate?.searchId]);

  // Rejected candidates: fetch the recruiter's rejection reason directly so it shows regardless of
  // which list/source loaded this candidate (the sourcing/session objects don't carry it).
  useEffect(() => {
    const stage = String(candidate?.funnel_stage || candidate?._funnel_stage || candidate?.status || '').toLowerCase();
    const alreadyHave = candidate?.rejection_reason || candidate?._rejection_reason;
    if (stage !== 'rejected' || alreadyHave || !email || !resolvedSearchId) {
      setFetchedRejectionReason('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const q = sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : '';
        const data = await aiAutomationApi.get(
          `/candidates/rejection/${encodeURIComponent(email)}/${encodeURIComponent(resolvedSearchId)}${q}`
        );
        if (!cancelled && data?.rejection_reason) setFetchedRejectionReason(data.rejection_reason);
      } catch { /* ignore — banner falls back to "No reason recorded." */ }
    })();
    return () => { cancelled = true; };
  }, [candidate?.funnel_stage, candidate?.status, candidate?.rejection_reason, email, resolvedSearchId, sessionId]);

  // Load manual followup cooldown from mail settings
  useEffect(() => {
    const cid = getCid();
    if (!cid) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await aiAutomationApi.get(`/email/settings/${encodeURIComponent(cid)}`);
        if (!cancelled && data?.settings?.manual_followup_cooldown_seconds) {
          setFollowupCooldownHours(data.settings.manual_followup_cooldown_seconds / 3600);
        }
      } catch { /* use default */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Load conversation history (sent + received emails merged) ──
  // silent=true skips the loading indicator (used for background refreshes)
  // Passes search_id to filter conversations per job, preventing cross-job history leakage
  const loadConversation = useCallback(async (silent = false) => {
    if (!email) return;
    if (!silent) setLoadingReplies(true);
    try {
      // Use the candidate-specific endpoint which properly merges
      // sent emails (with is_sent_email/is_nudge flags) and received replies
      // Pass search_id to scope conversation to the current job only
      const params = new URLSearchParams();
      if (resolvedSearchId) params.set('search_id', resolvedSearchId);
      if (candidateRecordId) params.set('candidate_id', candidateRecordId);
      const queryString = params.toString();
      const data = await aiAutomationApi.get(
        `/email/replies/candidate/${encodeURIComponent(email)}${queryString ? `?${queryString}` : ''}`
      );
      if (data?.replies) {
        setReplies(data.replies);
        // Check if the LAST message in the thread is from the candidate (not sent by us).
        // Draft reply should only be enabled when the candidate's message is the latest one.
        // If the last message was sent by us, there is no need for a draft reply.
        const sorted = [...data.replies].sort((a, b) => {
          const dateA = new Date(a.received_at || a.sent_at || a.created_at || 0);
          const dateB = new Date(b.received_at || b.sent_at || b.created_at || 0);
          return dateB - dateA;
        });
        const latestMsg = sorted[0];
        // Only flag as "candidate question" if the latest message is from the candidate
        // AND it's actually a question/inquiry (not a simple reply like "I'm interested")
        const lastIsCandidateQuestion = latestMsg && !latestMsg.is_sent_email &&
          (latestMsg.classification === 'Question' || latestMsg.context_type === 'question');
        setHasCandidateQuestion(lastIsCandidateQuestion);
        // If last message is from us, clear any stale draft state
        if (!lastIsCandidateQuestion) {
          setHasQuestionDraft(false);
        }
      }
    } catch (err) {
      console.error('Error loading conversation:', err);
    } finally {
      if (!silent) setLoadingReplies(false);
    }
  }, [candidateRecordId, email, resolvedSearchId]);

  const refreshRescheduleRequest = useCallback(async () => {
    if (!email || !resolvedSearchId) {
      setRescheduleRequest(null);
      return;
    }
    const data = await aiAutomationApi.get(
      `/interviews/reschedule-requests?search_id=${encodeURIComponent(resolvedSearchId)}&status=pending&status=needs_manual_action`
    );
    const requests = Array.isArray(data?.requests) ? data.requests : (Array.isArray(data) ? data : []);
    const emailLower = email.toLowerCase();
    const match = requests.find((r) => {
      const requestEmail = String(r?.candidate_email || '').toLowerCase();
      const requestCandidateId = String(r?.candidate_id || r?.candidateId || '');
      return requestEmail === emailLower || (candidateRecordId && requestCandidateId === String(candidateRecordId));
    });
    setRescheduleRequest(match || null);
  }, [candidateRecordId, email, resolvedSearchId]);

  const refreshBookedInterview = useCallback(async () => {
    if (!email) {
      setBookedInterviewData(null);
      setMissedInterviewData(null);
      return;
    }
    const data = await aiAutomationApi.get('/interviews/calendar/all');
    const interviews = Array.isArray(data?.interviews) ? data.interviews : [];
    const sortByScheduledTimeDesc = (a, b) => {
      const aStamp = new Date(`${a?.scheduled_date || ''}T${a?.scheduled_time || '00:00:00'}`).getTime() || 0;
      const bStamp = new Date(`${b?.scheduled_date || ''}T${b?.scheduled_time || '00:00:00'}`).getTime() || 0;
      return bStamp - aStamp;
    };
    const byJob = interviews
      .filter((iv) => iv.candidate_email?.toLowerCase() === email.toLowerCase())
      .filter((iv) => !resolvedSearchId || String(iv.search_id || '') === String(resolvedSearchId))
      .sort(sortByScheduledTimeDesc);
    const bySession = byJob.filter((iv) => !sessionId
      || String(iv.source_session_id || '') === String(sessionId)
      || String(iv.session_id || '') === String(sessionId));
    // Prefer the session-scoped interview, but fall back to the most recent interview for this
    // candidate on the SAME job. An AI interview runs on the sourced candidate's own session, so its
    // session id differs from the sourcing session — the strict session filter would drop it, leaving
    // bookedInterviewData null so isFullyAIInterview reads false and the Meeting Analysis modal wrongly
    // showed the manual view instead of the AI interview result.
    const isNoShow = (iv) => String(iv?.interview_status || '').toLowerCase() === 'no_show'
      || String(iv?.status || '').toLowerCase() === 'no_show'
      || Boolean(iv?.missed_interview);
    const activeBySession = bySession.filter((iv) => !isNoShow(iv));
    const activeByJob = byJob.filter((iv) => !isNoShow(iv));
    const missedBySession = bySession.filter(isNoShow);
    const missedByJob = byJob.filter(isNoShow);
    const match = activeBySession[0] || activeByJob[0] || null;
    const missedMatch = missedBySession[0] || missedByJob[0] || null;
    setBookedInterviewData(match);
    setMissedInterviewData(missedMatch);
  }, [email, resolvedSearchId, sessionId]);

  useEffect(() => {
    loadConversation();
  }, [loadConversation]);

  // Refresh conversation when a reply is sent via the lifecycle agent or another component
  useEffect(() => {
    const handler = () => {
      loadConversation(true);
    };
    window.addEventListener('conversationChanged', handler);
    return () => window.removeEventListener('conversationChanged', handler);
  }, [loadConversation]);

  // Refresh candidate data and conversation when sync completes
  useEffect(() => {
    const handler = () => {
      loadConversation(true);
      refreshBookedInterview().catch(() => {});
      // Re-fetch candidate profile data from API so synced fields (funnel_stage, resume, etc.) update
      if (candidate?.id && resolvedSearchId) {
        (async () => {
          try {
            const freshData = await aiAutomationApi.get(`/candidates/${encodeURIComponent(candidate.id)}?search_id=${encodeURIComponent(resolvedSearchId)}`);
            if (freshData && onCandidateUpdate) {
              onCandidateUpdate(email, freshData);
            }
          } catch { /* ignore - candidate detail will refresh on next navigation */ }
        })();
      }
    };
    window.addEventListener('syncCompleted', handler);
    window.addEventListener('candidateDetailRefresh', handler);
    return () => {
      window.removeEventListener('syncCompleted', handler);
      window.removeEventListener('candidateDetailRefresh', handler);
    };
  }, [loadConversation, refreshBookedInterview, candidate?.id, resolvedSearchId, email, onCandidateUpdate]);

  // Pull the freshest funnel_stage (and synced fields) when the detail view opens. The candidate object
  // handed in from the list can be stale — e.g. an imported candidate whose interview was booked after the
  // list loaded still carries funnel_stage='Sourced' locally while the DB already says 'Shortlisted', so
  // the "Current Funnel Stage" strip renders the wrong step until a sync event happens to fire. Runs once
  // per (candidate, job); the ref guard prevents a refetch loop when onCandidateUpdate replaces the object.
  const funnelRefreshedRef = useRef('');
  useEffect(() => {
    if (!candidate?.id || !resolvedSearchId) return;
    const key = `${candidate.id}:${resolvedSearchId}`;
    if (funnelRefreshedRef.current === key) return;
    funnelRefreshedRef.current = key;
    (async () => {
      try {
        const freshData = await aiAutomationApi.get(`/candidates/${encodeURIComponent(candidate.id)}?search_id=${encodeURIComponent(resolvedSearchId)}`);
        if (freshData && onCandidateUpdate) onCandidateUpdate(email, freshData);
      } catch { /* ignore — will refresh on next sync/navigation */ }
    })();
  }, [candidate?.id, resolvedSearchId, email, onCandidateUpdate]);

  // Fetch pending reschedule request for this candidate
  useEffect(() => {
    if (!email || !resolvedSearchId) return;
    let cancelled = false;
    (async () => {
      try {
        await refreshRescheduleRequest();
      } catch {
        if (!cancelled) setRescheduleRequest(null);
      }
    })();
    return () => { cancelled = true; };
  }, [email, refreshRescheduleRequest, resolvedSearchId]);

  // Check if this candidate already has a booked interview
  useEffect(() => {
    if (!email || !resolvedSearchId) return;
    let cancelled = false;
    const checkBookedInterview = async () => {
      try {
        const data = await aiAutomationApi.get(
          `/interviews/search/${encodeURIComponent(resolvedSearchId)}/candidates-status`
        );
        if (!cancelled && data?.already_scheduled_candidates) {
          const emailLower = email.toLowerCase();
          setHasBookedInterview(!!data.already_scheduled_candidates[emailLower]);
        }
      } catch {
        // ignore
      }
    };
    checkBookedInterview();
    return () => { cancelled = true; };
  }, [email, resolvedSearchId]);

  // Fetch booked interview data (with meet link) for this candidate
  useEffect(() => {
    if (!email) {
      setBookedInterviewData(null);
      setBookedInterviewLoading(false);
      return;
    }
    let cancelled = false;
    setBookedInterviewLoading(true);
    (async () => {
      try {
        await refreshBookedInterview();
      } catch {
        if (!cancelled) setBookedInterviewData(null);
      } finally {
        if (!cancelled) setBookedInterviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [candidate?.id, email, refreshBookedInterview]);

  // Hide only the Join action after the meeting ends. Keep the booking record: its
  // interview_type and source IDs drive the completed AI-interview overview.
  useEffect(() => {
    if (!bookedInterviewData?.scheduled_date || !bookedInterviewData?.scheduled_time) {
      setCanJoinBookedInterview(false);
      return;
    }
    const duration = bookedInterviewData.duration || 60;
    const [h, m] = bookedInterviewData.scheduled_time.split(':').map(Number);
    const meetEnd = new Date(bookedInterviewData.scheduled_date);
    meetEnd.setHours(h, m + duration, 0, 0);
    const msUntilEnd = meetEnd.getTime() - Date.now();
    if (msUntilEnd <= 0) {
      setCanJoinBookedInterview(false);
      return;
    }
    setCanJoinBookedInterview(true);
    const timer = setTimeout(() => setCanJoinBookedInterview(false), msUntilEnd);
    return () => clearTimeout(timer);
  }, [bookedInterviewData]);

  // Emails whose draft the recruiter JUST sent, mapped to the send time. The real send + the draft's
  // flip to status='sent' happen in a fire-and-forget backend task, so the /email/drafts poll keeps
  // returning the row as 'draft' for a few seconds — which used to resurrect the "Reply to Question"
  // button right after it was clicked. We suppress the button for a bounded window; if the backend still
  // shows it 'draft' AFTER the window (i.e. the send silently failed — e.g. Gmail not connected), the
  // button correctly reappears so the recruiter knows it didn't go out.
  const sentDraftAtRef = useRef(new Map());
  const SENT_SUPPRESS_MS = 30000;

  // Check if this candidate has a question draft (Scene 2)
  // Only show draft when the last message is from the candidate
  useEffect(() => {
    if (!email || !resolvedSearchId) return;
    // If the last message isn't from the candidate, no need for a draft
    if (!hasCandidateQuestion) {
      setHasQuestionDraft(false);
      return;
    }
    let cancelled = false;
    const checkDrafts = async () => {
      try {
        const data = await aiAutomationApi.get(`/email/drafts/${resolvedSearchId}`);
        if (!cancelled && data?.drafts) {
          const key = email.toLowerCase();
          const match = data.drafts.find(
            (d) => d.candidate_email?.toLowerCase() === key && d.status === 'draft'
          );
          if (!match) {
            // Backend no longer has a pending draft for this candidate → the send went through.
            sentDraftAtRef.current.delete(key);
            setHasQuestionDraft(false);
            return;
          }
          const sentAt = sentDraftAtRef.current.get(key);
          const recentlySent = sentAt && (Date.now() - sentAt < SENT_SUPPRESS_MS);
          if (sentAt && !recentlySent) sentDraftAtRef.current.delete(key); // window elapsed, still draft
          setHasQuestionDraft(!recentlySent); // hide while the just-sent draft is still being processed
        }
      } catch {
        // Ignore – just means no drafts
      }
    };
    checkDrafts();
    return () => { cancelled = true; };
  }, [email, resolvedSearchId, hasCandidateQuestion]);

  // ── Manual override: "Not a question — move to Interested" (from the draft modal) ──
  // Reclassifies the candidate's latest reply so a misread courtesy line (e.g. "…answer any
  // questions you may have") no longer parks them in "Question pending". No email is sent.
  const handleMoveToInterested = async () => {
    if (!email || !resolvedSearchId) {
      showToast('Missing candidate email or search context.', 'error');
      return;
    }
    await aiAutomationApi.post('/email/reply/mark-interested', {
      candidate_email: email,
      search_id: resolvedSearchId,
      cid: getCid() ? String(getCid()) : undefined,
    });
    // Clear question state, reflect the stage locally, and refresh the thread.
    setHasCandidateQuestion(false);
    setHasQuestionDraft(false);
    syncCandidateStageLocally('Interested');
    await loadConversation(true);
    showToast('Moved to Interested — ready to schedule.', 'success');
  };

  // ── Open draft modal (Scene 2: reply to question) ──
  const handleOpenReplyDraft = async () => {
    // Guard: only generate reply draft if the last message is truly from the candidate
    if (!hasCandidateQuestion) {
      return;
    }

    if (!email || !resolvedSearchId) {
      setDraftModal({
        isOpen: true, title: 'Draft Reply', subject: '', draftId: null, scene: 'reply', candidateQuestion: '',
        body: !email
          ? 'This candidate does not have an email address on record. You can add one or reach out via LinkedIn.'
          : 'Search context is not available. Please go back and try again.',
      });
      return;
    }

    setDraftLoading(true);

    try {
      const data = await aiAutomationApi.post('/email/create-template', {
        scene: 'reply',
        candidate_email: email,
        search_id: resolvedSearchId,
        cid: getCid() ? String(getCid()) : undefined,
      });
      if (data?.status === 'success') {
        // Only open modal once we have actual content
        setDraftModal({
          isOpen: true,
          title: 'Draft Reply',
          subject: data.subject || '',
          body: data.template || '',
          candidateQuestion: data.candidate_message || '',
          scene: 'reply',
          draftId: data.draft_id || null,
        });
      } else if (data?.status === 'already_replied') {
        // Already replied to candidate's question — switch to follow-up
        setHasQuestionDraft(false);
        setHasCandidateQuestion(false);
        // Auto-open follow-up modal instead (skip cooldown warning since we're redirecting)
        handleOpenFollowupDraft(true);
        return;
      } else {
        setDraftModal({
          isOpen: true, title: 'Draft Reply', subject: '', draftId: null, scene: 'reply', candidateQuestion: '',
          body: 'Could not generate reply draft. Please try again.',
        });
      }
    } catch (err) {
      console.error('Error loading reply draft:', err);
      const errMsg = err?.message || String(err);
      let displayMsg = 'Failed to generate reply. The AI service may be unavailable. You can type your message manually.';
      if (errMsg.includes('quota') || errMsg.includes('429') || errMsg.includes('rate limit')) {
        displayMsg = 'OpenAI API quota exceeded. Please check your billing at platform.openai.com or wait and try again.';
      } else if (errMsg.includes('invalid') && errMsg.includes('key') || errMsg.includes('401') || errMsg.includes('auth')) {
        displayMsg = 'OpenAI API key is invalid or expired. Please update it in Mail Settings.';
      }
      setDraftModal({
        isOpen: true, title: 'Draft Reply', subject: '', draftId: null, scene: 'reply', candidateQuestion: '',
        body: displayMsg,
      });
    } finally {
      setDraftLoading(false);
    }
  };

  // ── Open draft modal (Scene 3: followup) ──
  // Checks if followup is within configured cooldown of last sent email and shows warning
  const handleOpenFollowupDraft = async (skipWarning = false) => {
    // Check if last sent email was within the configured cooldown — show warning if so
    if (!skipWarning && replies.length > 0) {
      const sentMessages = replies.filter((r) => r.is_sent_email);
      if (sentMessages.length > 0) {
        const latestSent = sentMessages.reduce((latest, r) => {
          const d = new Date(r.sent_at || r.received_at || r.created_at || 0);
          return d > latest ? d : latest;
        }, new Date(0));
        const hoursSinceLastSent = (Date.now() - latestSent.getTime()) / (1000 * 60 * 60);
        if (hoursSinceLastSent < followupCooldownHours) {
          setFollowupWarningModal({ isOpen: true, lastSentAt: latestSent });
          return;
        }
      }
    }

    // Open modal immediately with loading state (even if email/searchId might be missing)
    setDraftLoading(true);
    setDraftModal({ isOpen: true, title: 'Follow-up Message', subject: '', body: '', candidateQuestion: '', scene: 'followup', draftId: null });

    if (!email || !resolvedSearchId) {
      setDraftLoading(false);
      setDraftModal((prev) => ({
        ...prev,
        body: !email
          ? 'This candidate does not have an email address on record. You can add one or reach out via LinkedIn.'
          : 'Search context is not available. Please go back and try again.',
      }));
      return;
    }

    try {
      const data = await aiAutomationApi.post('/email/create-template', {
        scene: 'followup',
        candidate_email: email,
        candidate_name: name,
        search_id: resolvedSearchId,
        cid: getCid() ? String(getCid()) : undefined,
      });
      if (data?.status === 'success') {
        setDraftModal((prev) => ({
          ...prev,
          subject: data.subject || '',
          body: data.template || '',
        }));
      } else {
        setDraftModal((prev) => ({
          ...prev,
          body: 'Could not generate follow-up template. Please try again.',
        }));
      }
    } catch (err) {
      console.error('Error loading followup draft:', err);
      const errMsg = err?.message || String(err);
      let displayMsg = 'Failed to generate follow-up. The AI service may be unavailable. You can type your message manually.';
      if (errMsg.includes('quota') || errMsg.includes('429') || errMsg.includes('rate limit')) {
        displayMsg = 'OpenAI API quota exceeded. Please check your billing at platform.openai.com or wait and try again.';
      } else if (errMsg.includes('invalid') && errMsg.includes('key') || errMsg.includes('401') || errMsg.includes('auth')) {
        displayMsg = 'OpenAI API key is invalid or expired. Please update it in Mail Settings.';
      }
      setDraftModal((prev) => ({
        ...prev,
        body: displayMsg,
      }));
    } finally {
      setDraftLoading(false);
    }
  };

  // ── Send from draft modal ──
  const handleDraftSend = async (editedSubject, editedBody) => {
    if (!email || !resolvedSearchId) return;
    setDraftSending(true);
    try {
      const metadata = getMetadata();
      const userId = metadata?.uid || metadata?.userId || metadata?.id;

      if (draftModal.scene === 'reply' && draftModal.draftId) {
        // Update draft content first, then send
        await aiAutomationApi.put(`/email/drafts/${draftModal.draftId}`, {
          draft_content: editedBody,
        });
        await aiAutomationApi.post('/email/drafts/send', {
          draft_ids: [draftModal.draftId],
        });
      } else {
        // For followup or reply without existing draft — use send-followup
        await aiAutomationApi.post('/email/send-followup', {
          candidate_email: email,
          search_id: resolvedSearchId,
          user_id: String(userId),
          subject: editedSubject,
          body: editedBody,
          cid: getCid() ? String(getCid()) : undefined,
        });
      }

      // Clear draft/question state immediately (don't call loadConversation() here
      // because the background send may not have completed yet — the auto-sync at
      // 5 seconds will refresh the timeline after Gmail processing finishes)
      sentDraftAtRef.current.set(email.toLowerCase(), Date.now()); // suppress re-derivation while sending
      setHasQuestionDraft(false); // clear the badge
      setHasCandidateQuestion(false); // we just sent a reply, so no pending question

      // Notify AppHeader to refresh notifications (reply sent = question addressed)
      window.dispatchEvent(new CustomEvent('notificationsChanged'));

      // Auto-sync after 5 seconds to pick up Gmail processing and disable stale reply button
      // Show progress bar immediately, but delay the actual API call by 5 seconds
      if (resolvedSearchId) {
        if (autoSyncTimerRef.current) clearTimeout(autoSyncTimerRef.current);
        setCurrentSearchId(resolvedSearchId);
        // Show syncing indicator immediately
        updateSyncState(resolvedSearchId, { syncing: true, syncMessage: 'Waiting for mail provider...' });
        autoSyncTimerRef.current = setTimeout(() => {
          const metadata = getMetadata();
          startSync(resolvedSearchId, {
            sessionId,
            cid: metadata?.cid ? String(metadata.cid) : undefined,
            onComplete: () => {
              loadConversation(true);
              // Notify AppHeader again after sync completes
              window.dispatchEvent(new CustomEvent('notificationsChanged'));
            },
            onGmailNotConnected: async (authError) => {
              // The send could not go out — stop suppressing so the "Reply to Question" button
              // reappears and the recruiter knows the reply is still pending.
              sentDraftAtRef.current.delete(email.toLowerCase());
              setHasQuestionDraft(true);
              await handleMailProviderReconnect({
                cid: metadata?.cid ? String(metadata.cid) : undefined,
                error: authError,
                onConnecting: (providerLabel) => showToast(`Connecting ${providerLabel}...`, 'info'),
                onConnected: (connectedEmail, providerLabel) => showToast(`${providerLabel} connected${connectedEmail ? ` (${connectedEmail})` : ''}! Please sync replies again.`, 'success'),
                onError: (msg) => showToast(`Mail connection failed: ${msg}`, 'error'),
              });
            },
          });
        }, 5000);
      }
    } catch (err) {
      console.error('Error sending draft:', err);
      throw err; // let modal handle error state
    } finally {
      setDraftSending(false);
    }
  };

  // ── Regenerate draft (inside modal) ──
  const handleRegenerate = async () => {
    if (!email || !resolvedSearchId) return;
    setDraftLoading(true);
    try {
      const data = await aiAutomationApi.post('/email/create-template', {
        scene: draftModal.scene,
        candidate_email: email,
        search_id: resolvedSearchId,
        cid: getCid() ? String(getCid()) : undefined,
      });
      if (data?.status === 'success') {
        setDraftModal((prev) => ({
          ...prev,
          subject: data.subject || '',
          body: data.template || '',
          candidateQuestion: data.candidate_message || prev.candidateQuestion,
          draftId: data.draft_id || null,
        }));
      }
    } catch (err) {
      console.error('Error regenerating draft:', err);
    } finally {
      setDraftLoading(false);
    }
  };

  const fetchCandidateAiNotes = useCallback(async () => {
    if (!email) return [];
    if (isFullyAIInterview) return [];

    const requestNotes = async (params) => {
      const query = params.toString();
      const data = await aiAutomationApi.get(`/interviews/candidate/${encodeURIComponent(email)}/notes${query ? `?${query}` : ''}`);
      if (data?.success && Array.isArray(data?.interviews)) {
        return data.interviews.filter((interview) => {
          if (resolvedSearchId && String(interview?.search_id || '') !== String(resolvedSearchId)) {
            return false;
          }
          return true;
        });
      }
      return [];
    };

    const exactParams = new URLSearchParams();
    if (resolvedSearchId) exactParams.set('search_id', resolvedSearchId);
    if (sessionId) exactParams.set('session_id', sessionId);
    if (scopedInterviewId) exactParams.set('interview_id', String(scopedInterviewId));
    if (scopedMeetingId) exactParams.set('meeting_id', String(scopedMeetingId));

    const exactMatches = await requestNotes(exactParams);
    if (exactMatches.length > 0) {
      return exactMatches;
    }

    if (scopedInterviewId) {
      try {
        const data = await aiAutomationApi.get(`/interviews/${encodeURIComponent(String(scopedInterviewId))}/notes`);
        if (data?.interview_id) {
          return [{
            interview_id: data.interview_id,
            interview_title: data.interview_title,
            scheduled_date: data.scheduled_date || bookedInterviewData?.scheduled_date || null,
            scheduled_time: data.scheduled_time || bookedInterviewData?.scheduled_time || null,
            google_meet_link: data.google_meet_link || bookedInterviewData?.google_meet_link || null,
            ai_note_taker_status: data.notes?.status || data.ai_note_taker_status || 'completed',
            meeting_completed: true,
            ai_note_taker_used_url: data.ai_note_taker_used_url || null,
            notes: data.notes || {},
            recording_url: data.recording_url || data.notes?.audio_url || null,
            recording_s3_bucket: data.recording_s3_bucket || data.notes?.recording_s3_bucket || null,
            recording_s3_key: data.recording_s3_key || data.notes?.recording_s3_key || null,
            meeting_analysis_json: data.meeting_analysis_json || null,
            meeting_analysis_cached_at: data.meeting_analysis_cached_at || null,
            meeting_analysis_model: data.meeting_analysis_model || null,
          }];
        }
      } catch (err) {
        console.error('[CandidateDetail] Failed scoped interview notes lookup:', err);
      }
    }

    // Some candidate detail entries carry stale candidate/session identifiers even
    // though the interview row exists for the same email and job. Fall back to the
    // job-scoped query before giving up so Meeting Analysis can still appear.
    if (!scopedInterviewId && !scopedMeetingId && resolvedSearchId && (sessionId || candidate?.id)) {
      const relaxedParams = new URLSearchParams();
      relaxedParams.set('search_id', resolvedSearchId);
      const relaxedMatches = await requestNotes(relaxedParams);
      if (relaxedMatches.length > 0) {
        return relaxedMatches;
      }
    }

    if (!scopedInterviewId && !scopedMeetingId && sessionId && !resolvedSearchId) {
      const sessionParams = new URLSearchParams();
      sessionParams.set('session_id', sessionId);
      const sessionMatches = await requestNotes(sessionParams);
      if (sessionMatches.length > 0) {
        return sessionMatches;
      }
    }

    return [];
  }, [
    bookedInterviewData?.google_meet_link,
    bookedInterviewData?.scheduled_date,
    bookedInterviewData?.scheduled_time,
    candidate?.id,
    email,
    isFullyAIInterview,
    resolvedSearchId,
    scopedInterviewId,
    scopedMeetingId,
    sessionId,
  ]);

  const fetchCandidateCalendarInterviews = useCallback(async () => {
    if (!email) return [];

    const metadata = getMetadata();
    const query = new URLSearchParams();
    const companyId = getCid();
    const currentUserId = metadata?.uid || metadata?.userId || metadata?.id;

    if (companyId) query.set('cid', String(companyId));
    if (currentUserId) query.set('user_id', String(currentUserId));
    if (resolvedSearchId) query.set('search_id', String(resolvedSearchId));
    query.set('candidate_email', String(email));

    const data = await aiAutomationApi.get(`/interviews/calendar/all${query.toString() ? `?${query.toString()}` : ''}`);
    return Array.isArray(data?.interviews) ? data.interviews : [];
  }, [email, resolvedSearchId]);

  useEffect(() => {
    if (!candidate?.id || !resolvedSearchId) return;
    let cancelled = false;
    const loadNotes = async () => {
      try {
        const params = new URLSearchParams();
        if (sessionId) params.set('session_id', sessionId);
        const data = await aiAutomationApi.get(
          `/candidates/notes/${encodeURIComponent(candidate.id)}/${encodeURIComponent(resolvedSearchId)}${params.toString() ? `?${params.toString()}` : ''}`
        );
        if (!cancelled && data?.notes) {
          setNotes(data.notes);
        }
      } catch {
        // Fallback to localStorage
        try {
          const stored = localStorage.getItem(`candidate_notes_${candidate.id}_${resolvedSearchId}`);
          if (!cancelled && stored) setNotes(JSON.parse(stored));
        } catch { /* ignore */ }
      }
    };
    loadNotes();
    return () => { cancelled = true; };
  }, [candidate?.id, resolvedSearchId, sessionId]);

  // Fetch AI Notes for candidate
  useEffect(() => {
    if (!email) return;
    if (isFullyAIInterview) {
      setAiNotes([]);
      setLoadingAiNotes(false);
      return;
    }
    let cancelled = false;
    const fetchAiNotes = async () => {
      setLoadingAiNotes(true);
      try {
        const interviews = await fetchCandidateAiNotes();
        if (!cancelled) setAiNotes(interviews);
      } catch (err) {
        console.error('[CandidateDetail] Failed to fetch AI notes:', err);
        if (!cancelled) setAiNotes([]);
      } finally {
        if (!cancelled) setLoadingAiNotes(false);
      }
    };
    fetchAiNotes();
    return () => { cancelled = true; };
  }, [email, fetchCandidateAiNotes, isFullyAIInterview]);

  const refreshAiNotes = async () => {
    if (!email) return;
    if (isFullyAIInterview) {
      setAiNotes([]);
      return;
    }
    try {
      setAiNotes(await fetchCandidateAiNotes());
    } catch (err) {
      console.error('[CandidateDetail] Failed to refresh AI notes:', err);
    }
  };

  useEffect(() => {
    if (!meetingAnalysisModalOpen) return;
    if (isFullyAIInterview) return;
    if (aiNotes.length > 0) return;

    let cancelled = false;

    const loadFallbackInterviewNotes = async () => {
      setLoadingAiNotes(true);
      try {
        if (scopedInterviewId) {
          const data = await aiAutomationApi.get(`/interviews/${encodeURIComponent(String(scopedInterviewId))}/notes`);
          if (cancelled || !data?.interview_id) return;

          setAiNotes([{
            interview_id: data.interview_id,
            interview_title: data.interview_title,
            scheduled_date: data.scheduled_date || bookedInterviewData?.scheduled_date || null,
            scheduled_time: data.scheduled_time || bookedInterviewData?.scheduled_time || null,
            google_meet_link: data.google_meet_link || bookedInterviewData?.google_meet_link || null,
            ai_note_taker_status: data.notes?.status || data.ai_note_taker_status || 'completed',
            meeting_completed: true,
            ai_note_taker_used_url: data.ai_note_taker_used_url || null,
            notes: data.notes || {},
            recording_url: data.recording_url || data.notes?.audio_url || null,
            recording_s3_bucket: data.recording_s3_bucket || data.notes?.recording_s3_bucket || null,
            recording_s3_key: data.recording_s3_key || data.notes?.recording_s3_key || null,
            meeting_analysis_json: data.meeting_analysis_json || null,
            meeting_analysis_cached_at: data.meeting_analysis_cached_at || null,
            meeting_analysis_model: data.meeting_analysis_model || null,
          }]);
          return;
        }

        let fallbackInterview = bookedInterviewData || null;

        if (!fallbackInterview?.id) {
          const calendarInterviews = await fetchCandidateCalendarInterviews();
          if (cancelled) return;

          const candidateRows = calendarInterviews
            .filter((interview) => interview?.candidate_email?.toLowerCase() === email.toLowerCase())
            .filter((interview) => {
              if (canOpenInterviewNotes(interview)) return true;

              // Some rows miss completion flags even though interview notes are queryable.
              // Keep interviewed/completed rows eligible for fallback loading.
              const interviewStatus = String(interview?.status || '').trim().toLowerCase();
              const noteTakerStatus = String(interview?.ai_note_taker_status || '').trim().toLowerCase();
              const hasInterviewContext = Boolean(interview?.id || interview?.ai_note_taker_meeting_id || interview?.google_meet_link);

              return (
                hasInterviewContext &&
                (
                  interviewStatus === 'completed' ||
                  interviewStatus === 'interviewed' ||
                  ['completed', 'recording_completed', 'ended', 'left', 'done', 'finished', 'disconnected', 'closed'].includes(noteTakerStatus)
                )
              );
            })
            .sort((a, b) => {
              const aStamp = new Date(`${a?.scheduled_date || ''}T${a?.scheduled_time || '00:00:00'}`).getTime() || 0;
              const bStamp = new Date(`${b?.scheduled_date || ''}T${b?.scheduled_time || '00:00:00'}`).getTime() || 0;
              return bStamp - aStamp;
            });

          fallbackInterview = candidateRows[0] || null;
        }

        if (!fallbackInterview?.id) return;

        const data = await aiAutomationApi.get(`/interviews/${fallbackInterview.id}/notes`);
        if (cancelled || !data?.interview_id) return;

        setAiNotes([{
          interview_id: data.interview_id,
          interview_title: data.interview_title,
          scheduled_date: fallbackInterview.scheduled_date || null,
          scheduled_time: fallbackInterview.scheduled_time || null,
          google_meet_link: fallbackInterview.google_meet_link || null,
          ai_note_taker_status: data.notes?.status || data.ai_note_taker_status || 'completed',
          meeting_completed: true,
          ai_note_taker_used_url: data.ai_note_taker_used_url || null,
          notes: data.notes || {},
          recording_url: data.recording_url || data.notes?.audio_url || null,
          recording_s3_bucket: data.recording_s3_bucket || data.notes?.recording_s3_bucket || null,
          recording_s3_key: data.recording_s3_key || data.notes?.recording_s3_key || null,
          meeting_analysis_json: data.meeting_analysis_json || null,
          meeting_analysis_cached_at: data.meeting_analysis_cached_at || null,
          meeting_analysis_model: data.meeting_analysis_model || null,
        }]);
      } catch (err) {
        console.error('[CandidateDetail] Failed to load fallback meeting analysis notes:', err);
      } finally {
        if (!cancelled) setLoadingAiNotes(false);
      }
    };

    loadFallbackInterviewNotes();
    return () => { cancelled = true; };
  }, [aiNotes.length, bookedInterviewData, canOpenInterviewNotes, email, fetchCandidateCalendarInterviews, isFullyAIInterview, meetingAnalysisModalOpen, scopedInterviewId]);

  const handleDownloadRecording = async (interviewId, videoUrl, audioUrl) => {
    const directMediaUrl = videoUrl || audioUrl || null;
    const downloadUrl = directMediaUrl
      ? (directMediaUrl.includes('/api/interviews/meetings/') && directMediaUrl.includes('/video')
          ? `${directMediaUrl}${directMediaUrl.includes('?') ? '&' : '?'}download=1`
          : directMediaUrl)
      : null;

    if (downloadUrl) {
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = '';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    setDownloadingRecordingForInterviewId(interviewId);
    try {
      const result = await aiAutomationApi.post(`/interviews/${interviewId}/download-recording`);
      if (result?.status === 'success' || result?.success) {
        showToast('Recording sync started. Trying download again after refresh.', 'success');
        await refreshAiNotes();
      } else {
        showToast(result?.message || 'Failed to download recording', 'error');
      }
    } catch (err) {
      console.error('[CandidateDetail] Failed to download recording:', err);
      showToast('Failed to download recording', 'error');
    } finally {
      setDownloadingRecordingForInterviewId(null);
    }
  };

  const handleFetchRecordingUrl = async (interviewId) => {
    setFetchingRecordingUrlForInterviewId(interviewId);
    try {
      const result = await aiAutomationApi.post(`/interviews/${interviewId}/fetch-recording-url`);
      if (result?.status === 'success' || result?.success) {
        showToast('Recording URL fetched successfully', 'success');
        // Refresh notes to get updated recording URL
        await refreshAiNotes();
      } else {
        showToast(result?.message || 'Failed to fetch recording URL', 'error');
      }
    } catch (err) {
      console.error('[CandidateDetail] Failed to fetch recording URL:', err);
      showToast('Failed to fetch recording URL', 'error');
    } finally {
      setFetchingRecordingUrlForInterviewId(null);
    }
  };

  const handleAnalyzeWithAI = async (interviewId) => {
    if (!interviewId) return;
    setAnalysisLoadingMap(prev => ({ ...prev, [interviewId]: true }));
    try {
      console.log('[CandidateDetail] Analyzing interview with AI:', interviewId);
      const data = await aiAutomationApi.post(`/interviews/${interviewId}/notes/analyze`);
      console.log('[CandidateDetail] AI Analysis response:', data);
      setAiAnalysisMap(prev => ({ ...prev, [interviewId]: data }));
    } catch (err) {
      console.error('[CandidateDetail] Failed to analyze interview:', err);
      setAiAnalysisMap(prev => ({ ...prev, [interviewId]: { success: false, error: err.message || 'Failed to generate AI analysis' } }));
    } finally {
      setAnalysisLoadingMap(prev => ({ ...prev, [interviewId]: false }));
    }
  };

  const buildClipVideoUrl = useCallback((videoUrl, startTime, endTime) => {
    if (!videoUrl) return '';
    const safeStartTime = Math.max(0, Number(startTime) || 0);
    const safeEndTime = Math.max(safeStartTime + 1, Number(endTime) || safeStartTime + 20);
    const baseUrl = String(videoUrl).split('#')[0];

    if (baseUrl.includes('/api/interviews/meetings/') && baseUrl.includes('/video')) {
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}start=${safeStartTime}&end=${safeEndTime}`;
    }

    return `${baseUrl}#t=${safeStartTime},${safeEndTime}`;
  }, []);

  const buildClipHlsUrl = useCallback((videoUrl, interviewId, startTime, endTime) => {
    if (!videoUrl || !interviewId) return '';
    const safeStartTime = Math.max(0, Math.floor(Number(startTime) || 0));
    const safeEndTime = Math.max(safeStartTime + 1, Math.ceil(Number(endTime) || safeStartTime + 20));
    const baseUrl = String(videoUrl).split('#')[0].split('?')[0];

    if (baseUrl.includes('/api/interviews/meetings/') && baseUrl.includes('/video')) {
      return baseUrl.replace(
        /\/api\/interviews\/meetings\/[^/]+\/video$/,
        `/api/interviews/meetings/${encodeURIComponent(interviewId)}/hls/${safeStartTime}-${safeEndTime}/index.m3u8`
      );
    }

    return buildClipVideoUrl(videoUrl, safeStartTime, safeEndTime);
  }, [buildClipVideoUrl]);

  const handleOpenHighlightModal = useCallback((interviewId, highlight, videoUrl) => {
    if (!interviewId || !highlight || !videoUrl) return;

    const clipStart = Number(highlight.clipStart);
    const clipEnd = Number(highlight.clipEnd);
    const startTime = Number.isFinite(clipStart) ? Math.max(0, clipStart) : Math.max(0, Number(highlight.timestamp) || 0);
    const endTime = Number.isFinite(clipEnd) ? Math.max(startTime + 1, clipEnd) : Math.max(startTime + 1, (Number(highlight.timestamp) || 0) + 20);

    setActiveHighlightModal({
      interviewId,
      title: highlight.title || 'Interview Highlight',
      videoUrl,
      clipVideoUrl: buildClipHlsUrl(videoUrl, interviewId, startTime, endTime),
      fallbackClipVideoUrl: buildClipVideoUrl(videoUrl, startTime, endTime),
      startTime,
      endTime,
      clipDuration: Math.max(1, endTime - startTime),
      timestampLabel: highlight.timestampLabel || formatSecondsAsTimestamp(startTime),
    });
  }, [buildClipHlsUrl, buildClipVideoUrl, formatSecondsAsTimestamp]);

  useEffect(() => {
    if (!activeHighlightModal?.clipVideoUrl) return;

    const videoElement = highlightModalVideoRef.current;
    if (!videoElement) return;

    let cancelled = false;
    const clipVideoUrl = activeHighlightModal.clipVideoUrl;
    const fallbackClipVideoUrl = activeHighlightModal.fallbackClipVideoUrl || clipVideoUrl;
    const isHlsClip = clipVideoUrl.split('?')[0].endsWith('.m3u8');

    const syncPlayback = async () => {
      const startTime = Math.max(0, Number(activeHighlightModal.startTime) || 0);
      const endTime = Math.max(startTime + 1, Number(activeHighlightModal.endTime) || startTime + 20);
      const clipDuration = Math.max(1, Number(activeHighlightModal.clipDuration) || (endTime - startTime));
      let fallbackTriggered = false;

      const attachDirectClipFallback = async () => {
        if (cancelled || fallbackTriggered) return;
        fallbackTriggered = true;

        if (highlightModalHlsRef.current) {
          highlightModalHlsRef.current.destroy();
          highlightModalHlsRef.current = null;
        }

        videoElement.pause();
        videoElement.src = fallbackClipVideoUrl;
        videoElement.load();

        try {
          await new Promise((resolve) => {
            const onLoadedMetadata = () => {
              videoElement.removeEventListener('loadedmetadata', onLoadedMetadata);
              resolve();
            };
            videoElement.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
          });
        } catch (_error) {
          // Ignore and let autoplay attempt fail naturally below.
        }

        try {
          await videoElement.play();
        } catch (error) {
          console.error('[CandidateDetail] Failed to autoplay fallback highlight video', error);
        }
      };

      const stopAtClipEnd = () => {
        const browserIgnoredFragment = videoElement.duration > (clipDuration + 2);
        const effectiveEndTime = browserIgnoredFragment ? endTime : clipDuration;

        if (videoElement.currentTime >= effectiveEndTime) {
          videoElement.pause();
          videoElement.currentTime = effectiveEndTime;
        }
      };

      videoElement.pause();

      if (highlightModalHlsRef.current) {
        highlightModalHlsRef.current.destroy();
        highlightModalHlsRef.current = null;
      }

      if (isHlsClip) {
        videoElement.removeAttribute('src');

        if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
          videoElement.src = clipVideoUrl;
          videoElement.load();
          try {
            await videoElement.play();
          } catch (error) {
            console.error('[CandidateDetail] Failed to autoplay native HLS highlight video', error);
            await attachDirectClipFallback();
          }
          return;
        }

        if (Hls.isSupported()) {
          const hls = new Hls();
          highlightModalHlsRef.current = hls;
          hls.loadSource(clipVideoUrl);
          hls.attachMedia(videoElement);
          hls.on(Hls.Events.ERROR, async (_event, data) => {
            console.error('[CandidateDetail] HLS highlight playback error', data);
            await attachDirectClipFallback();
          });
          hls.on(Hls.Events.MANIFEST_PARSED, async () => {
            if (cancelled) return;
            try {
              await videoElement.play();
            } catch (error) {
              console.error('[CandidateDetail] Failed to autoplay HLS highlight video', error);
              await attachDirectClipFallback();
            }
          });
          return;
        }
      }

      videoElement.src = clipVideoUrl;
      videoElement.load();

      const ready = async () => {
        if (videoElement.readyState < 1) {
          await new Promise((resolve) => {
            const onLoadedMetadata = () => {
              videoElement.removeEventListener('loadedmetadata', onLoadedMetadata);
              resolve();
            };
            videoElement.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
          });
        }

        if (cancelled) return;

        try {
          const browserIgnoredFragment = videoElement.duration > (clipDuration + 2);
          const targetStartTime = browserIgnoredFragment ? startTime : 0;

          if (videoElement.duration && targetStartTime >= videoElement.duration) {
            videoElement.currentTime = Math.max(0, videoElement.duration - 0.1);
          } else {
            videoElement.currentTime = targetStartTime;
          }
        } catch (error) {
          console.error('[CandidateDetail] Failed to seek highlight modal video', error);
        }

        videoElement.removeEventListener('timeupdate', stopAtClipEnd);
        videoElement.addEventListener('timeupdate', stopAtClipEnd);

        try {
          await videoElement.play();
        } catch (error) {
          console.error('[CandidateDetail] Failed to autoplay highlight modal video', error);
        }
      };

      await ready();
    };

    syncPlayback();

    return () => {
      cancelled = true;
      if (highlightModalHlsRef.current) {
        highlightModalHlsRef.current.destroy();
        highlightModalHlsRef.current = null;
      }
      videoElement.pause();
      videoElement.removeAttribute('src');
      videoElement.load();
    };
  }, [activeHighlightModal]);

  const formatHighlightCategoryLabel = useCallback((category) => {
    const normalized = String(category || 'general').trim().toLowerCase();
    if (!normalized) return 'General';
    return normalized
      .split(/[-_\s]+/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }, []);

  const getHighlightRating = useCallback((highlight, aiAnalysis) => {
    if (!highlight) return null;

    const directRating = Number(highlight.rating);
    if (Number.isFinite(directRating)) {
      return Math.max(0, Math.min(10, directRating));
    }

    const skillRatings = Array.isArray(aiAnalysis?.skill_ratings) ? aiAnalysis.skill_ratings : [];
    if (skillRatings.length === 0) return null;

    const highlightText = [
      highlight.category,
      highlight.title,
      highlight.description,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (!highlightText) return null;

    const normalizedHighlightText = highlightText.replace(/[^a-z0-9]+/g, ' ');

    const bestMatch = skillRatings.find((item) => {
      if (!item || typeof item !== 'object') return false;
      const skillName = String(item.skill || '').trim().toLowerCase();
      if (!skillName) return false;

      const normalizedSkillName = skillName.replace(/[^a-z0-9]+/g, ' ');
      return normalizedHighlightText.includes(normalizedSkillName);
    });

    if (bestMatch) {
      const score = Number(bestMatch.score);
      return Number.isFinite(score) ? Math.max(0, Math.min(10, score)) : null;
    }

    const categoryAliases = {
      technical: ['node', 'javascript', 'mongodb', 'express', 'rest api', 'technical'],
      communication: ['communication', 'clarity', 'explanation', 'speaking'],
      'problem solving': ['problem solving', 'structured approach', 'debugging', 'logic'],
      leadership: ['leadership', 'ownership', 'initiative'],
      culture: ['culture', 'values', 'teamwork'],
    };

    const categoryTokens = categoryAliases[String(highlight.category || '').trim().toLowerCase()] || [];
    const aliasMatch = skillRatings.find((item) => {
      if (!item || typeof item !== 'object') return false;
      const skillName = String(item.skill || '').trim().toLowerCase();
      if (!skillName) return false;
      return categoryTokens.some((token) => skillName.includes(token) || normalizedHighlightText.includes(token));
    });

    if (!aliasMatch) return null;
    const aliasScore = Number(aliasMatch.score);
    return Number.isFinite(aliasScore) ? Math.max(0, Math.min(10, aliasScore)) : null;
  }, []);

  // Notes modal state
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [noteInput, setNoteInput] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);

  const handleAddNote = async () => {
    if (!noteInput.trim() || !candidate?.id || !resolvedSearchId) return;
    setNoteSaving(true);
    try {
      await aiAutomationApi.post('/candidates/notes', {
        candidate_id: candidate.id,
        search_id: resolvedSearchId,
        session_id: sessionId || '',
        text: noteInput.trim(),
      });
      setNotes(prev => [...prev, { text: noteInput.trim(), created_at: new Date().toISOString() }]);
      setNoteInput('');
      setNotesModalOpen(false);
      showToast('Note added successfully', 'success');
    } catch (err) {
      // Fallback to localStorage
      const newNotes = [...notes, { text: noteInput.trim(), created_at: new Date().toISOString() }];
      setNotes(newNotes);
      localStorage.setItem(`candidate_notes_${candidate.id}_${resolvedSearchId}`, JSON.stringify(newNotes));
      setNoteInput('');
      setNotesModalOpen(false);
    } finally {
      setNoteSaving(false);
    }
  };

  // ── Open Resume View Modal — fetch resume data + match score ──
  const handleViewResume = useCallback(async (docId, attachmentLocation) => {
    if (!docId) return;
    // Use Core's file endpoint (permanent storage) instead of AI attachment path
    // Core serves: GET /resume-parsing/file/{doc_id}?cid={cid}
    const metadata = getMetadata?.() || {};
    const cid = metadata.cid;
    const authToken = getAuthToken();
    const CORE_URL = process.env.REACT_APP_CORE_URL || '';
    const pdfUrl = cid
      ? `${CORE_URL}/api/resume-parsing/file/${docId}?cid=${cid}${authToken ? `&token=${encodeURIComponent(authToken)}` : ''}`
      : null;
    setResumeViewModal({ isOpen: true, loading: true, data: null, error: null, attachmentUrl: pdfUrl });
    try {
      const searchParam = resolvedSearchId ? `?search_id=${resolvedSearchId}` : '';
      const data = await aiAutomationApi.get(`/resume/view/${docId}${searchParam}`);
      if (data?.success) {
        setResumeViewModal({ isOpen: true, loading: false, data, error: null, attachmentUrl: pdfUrl });
      } else {
        setResumeViewModal({ isOpen: true, loading: false, data: null, error: 'Resume data not found.', attachmentUrl: pdfUrl });
      }
    } catch (err) {
      setResumeViewModal({ isOpen: true, loading: false, data: null, error: err?.message || 'Failed to load resume data.', attachmentUrl: pdfUrl });
    }
  }, [resolvedSearchId]);

  // ── Sync emails and refresh conversation data (uses global SyncContext) ──
  const handleSyncEmails = useCallback(async () => {
    if (syncing) return;
    if (!resolvedSearchId) {
      showToast('No search context available for sync', 'error');
      return;
    }
    setCurrentSearchId(resolvedSearchId);
    const metadata = getMetadata();
    startSync(resolvedSearchId, {
      sessionId,
      cid: metadata?.cid ? String(metadata.cid) : undefined,
      onComplete: () => {
        loadConversation(true);
        // Notify AppHeader to refresh notifications after sync completes
        window.dispatchEvent(new CustomEvent('notificationsChanged'));
      },
      onGmailNotConnected: async (authError) => {
        // Don't show error text — directly trigger the active mail provider OAuth popup.
        console.log('[CandidateDetail] Mail provider not connected — triggering OAuth popup');
        try {
          await handleMailProviderReconnect({
            cid: metadata?.cid ? String(metadata.cid) : undefined,
            error: authError,
            onConnecting: (providerLabel) => showToast(`Connecting ${providerLabel}... Please complete authorization in the popup window.`, 'info'),
            onConnected: (email, providerLabel) => showToast(`${providerLabel} connected${email ? ` (${email})` : ''}! Try syncing again.`, 'success'),
            onError: (msg) => showToast(`Mail connection failed: ${msg}. Please go to Account → Credentials to connect your mail provider.`, 'error'),
          });
        } catch (err) {
          showToast('Please connect your mail account in Account → Credentials to enable sync.', 'error');
        }
      },
    });
  }, [syncing, resolvedSearchId, loadConversation, setCurrentSearchId, showToast, startSync]);

  // ── Load existing interview sessions on page open ──
  const loadInterviewHistory = useCallback(async () => {
    if (!candidate?.id || !sessionId || !resolvedSearchId) return;
    setLoadingInterview(true);
    try {
      const response = await coreApi.get(
        `/interview/candidate/${candidate.id}?session_id=${encodeURIComponent(sessionId)}&search_id=${encodeURIComponent(resolvedSearchId)}`
      );
      const result = response.data || response;
      if (result?.success && result?.interviews?.length > 0) {
        setInterviewSessions(result.interviews);
        const latest = result.interviews[0];
        // If latest is still generating, resume polling (survives page reload)
        if (latest.status === 'generating') {
          startInterviewGeneration(latest.interview_id, { name: candidate?.name || name, jobTitle: jobTitle });
        } else {
          setActiveSession(latest); // latest ready session
        }
      }
    } catch (err) {
      console.log('[Interview] No existing interviews found:', err.message);
    } finally {
      setLoadingInterview(false);
    }
  }, [candidate?.id, sessionId, resolvedSearchId]);

  useEffect(() => {
    loadInterviewHistory();
  }, [loadInterviewHistory]);

  // ── Generate AI Interview Questions (background, like sync process) ──
  const handleGenerateInterview = useCallback(async (forceNew = false) => {
    try {
      showToast(forceNew ? 'Starting new interview generation...' : 'Starting interview question generation...', 'info');

      const metadata = getMetadata();
      const companyId = getCid();
      if (!companyId) throw new Error('Company ID not found. Please log in again.');
      if (!candidate?.id) throw new Error('Candidate ID not found.');

      const requestData = {
        candidate_id: candidate?.id || null,
        candidate_email: email,
        candidate_name: name,
        candidate_skills: Array.isArray(candidate.skills) ? candidate.skills : (typeof candidate.skills === 'string' && candidate.skills ? [candidate.skills] : []),
        candidate_experience: typeof candidate.experience_years === 'string' ? candidate.experience_years : typeof candidate.experience === 'string' ? candidate.experience : (candidate.experience_years != null ? String(candidate.experience_years) : ''),
        candidate_summary: candidate.summary || '',
        candidate_current_role: candidate.title || '',
        candidate_location: candidate.location || '',
        session_id: sessionId,
        search_id: resolvedSearchId,
        company_id: companyId,
        job_title: jobTitle || candidate.title || 'Position',
        created_by: metadata?.uid || metadata?.userId || null,
        force_new: forceNew
      };

      // Shorter timeout — server returns immediately now (background generation)
      const response = await coreApi.post('/interview/generate', requestData, { timeout: 30000 });
      const result = response.data || response;

      // Background generation started — context polling will handle completion
      if (result.status === 'generating') {
        startInterviewGeneration(result.interview_id, { name: name || candidate?.name, jobTitle: jobTitle });
        // Toast removed — InterviewGenerationBubble handles progress display
        return;
      }

      // Immediate result (from cache) — handle as before
      const questions = result?.questions || result?.all_questions || [];

      if ((result.success || result.interview_id) && questions.length > 0) {
        const newSession = {
          interview_id: result.interview_id,
          status: result.status || 'draft',
          session_number: result.session_number || (interviewSessions.length + 1),
          interview_token: result.interview_token || null,
          question_count: result.question_count || questions.length,
          jd_based_count: result.jd_based_count || 0,
          candidate_based_count: result.candidate_based_count || 0,
          difficulty_distribution: result.difficulty_distribution || {},
          sections: result.sections || [],
          questions: questions,
          jd_based_questions: result.jd_based_questions || questions.filter(q => q.source === 'jd_based'),
          candidate_based_questions: result.candidate_based_questions || questions.filter(q => q.source === 'candidate_based'),
          header: result.header,
          summary: result.summary,
          from_cache: result.from_cache || false,
          created_at: result.created_at || new Date().toISOString()
        };

        if (result.from_cache) {
          setInterviewSessions(prev => {
            const exists = prev.find(s => s.interview_id === newSession.interview_id);
            return exists ? prev : [newSession, ...prev];
          });
        } else {
          setInterviewSessions(prev => [newSession, ...prev]);
        }
        setActiveSession(newSession);
        setInterviewSourceTab('all');
        setInterviewSectionFilter('all');
        setShowAllQuestions(false);

        const msg = result.from_cache
          ? `Loaded existing interview (${questions.length} questions)`
          : `✅ ${questions.length} questions generated! (${newSession.jd_based_count} JD + ${newSession.candidate_based_count} personalized)`;
        showToast(msg, 'success');
      } else {
        throw new Error(response.message || result.message || 'Failed to generate questions');
      }
    } catch (error) {
      console.error('[Generate Interview] Error:', error);
      showToast(error?.response?.data?.message || error?.message || 'Failed to generate questions', 'error');
    }
  }, [candidate, email, name, sessionId, resolvedSearchId, jobTitle, showToast, interviewSessions.length, startInterviewGeneration]);

  // ── Watch for interview generation completion (context handles polling) ──
  // When context phase changes from 'generating' to 'complete' or 'error', reload interview data
  useEffect(() => {
    if (!candidate?.id || !sessionId || !resolvedSearchId) return;

    if (interviewGenPhase === 'complete') {
      // Generation finished — reload interview history to get the new data
      (async () => {
        try {
          const response = await coreApi.get(
            `/interview/candidate/${candidate.id}?session_id=${encodeURIComponent(sessionId)}&search_id=${encodeURIComponent(resolvedSearchId)}`
          );
          const result = response.data || response;
          if (result?.success && result?.interviews?.length > 0) {
            setInterviewSessions(result.interviews);
            setActiveSession(result.interviews[0]);
            setInterviewSourceTab('all');
            setInterviewSectionFilter('all');
            setShowAllQuestions(false);
            showToast(`✅ ${result.interviews[0].question_count || result.interviews[0].questions?.length || 0} questions generated!`, 'success');
          }
        } catch (err) {
          console.error('[Interview] Failed to reload after generation:', err.message);
        }
      })();
    } else if (interviewGenPhase === 'error') {
      showToast('Interview generation failed. Please try again.', 'error');
    }
  }, [interviewGenPhase, candidate?.id, sessionId, resolvedSearchId]);

  // Helper: get filtered questions for the active session
  const getFilteredQuestions = useCallback(() => {
    if (!activeSession?.questions) return [];
    let qs = activeSession.questions;

    // Source filter
    if (interviewSourceTab === 'jd_based') {
      qs = activeSession.jd_based_questions || qs.filter(q => q.source === 'jd_based');
    } else if (interviewSourceTab === 'candidate_based') {
      qs = activeSession.candidate_based_questions || qs.filter(q => q.source === 'candidate_based');
    }

    // Section filter
    if (interviewSectionFilter !== 'all') {
      qs = qs.filter(q => (q.section || q.category || '') === interviewSectionFilter);
    }

    return qs;
  }, [activeSession, interviewSourceTab, interviewSectionFilter]);

  // Cleanup auto-sync timer on unmount
  useEffect(() => {
    return () => {
      if (autoSyncTimerRef.current) clearTimeout(autoSyncTimerRef.current);
    };
  }, []);

  // ── Format date for timeline ──
  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // ── Clean email content ──
  const cleanEmailContent = (content) => {
    if (!content) return '';

    let bodyContent = content;

    // If the content contains our email wrapper HTML, extract just the body
    if (content.includes('<') && content.includes('>')) {
      // Try to extract the body section from our email wrapper template
      // Our wrapper structure: <header> <body div> actual content + reply footer </body div> <footer>
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;

      // Remove our branded wrapper elements before extracting text:
      // 1. Remove header (gradient background div or table with company name)
      // 2. Remove reply footer (Important: Please reply directly...)
      // 3. Remove email footer (company name + copyright)
      // 4. Remove style tags
      // 5. Remove sub-footer text (This email was sent by...)

      // Remove style elements
      tempDiv.querySelectorAll('style').forEach(el => el.remove());

      // Get all text nodes
      let text = tempDiv.textContent || tempDiv.innerText || '';

      // Strip known wrapper text patterns
      // Header: company name at the very beginning (standalone line)
      // Reply footer pattern
      text = text.replace(/[\s\S]*?(?=(?:Dear|Hi|Hello|Hey|Greetings|Good\s+(?:morning|afternoon|evening))\s)/i, (match) => {
        // Only strip if the match looks like wrapper header noise (short, no real content)
        const lines = match.trim().split('\n').filter(l => l.trim());
        if (lines.length <= 2 && lines.every(l => l.trim().length < 60)) return '';
        return match;
      });

      // Strip "Quick Note" / "Important" reply footer
      text = text.replace(/[\u{1F514}]?\s*Quick Note:.*?efficiently\.?/gsu, '');
      text = text.replace(/[\u26A0]?\s*Important:.*?efficiently\.?/gsu, '');
      text = text.replace(/Important:\s*Please reply directly.*?efficiently\.?/gsi, '');

      // Strip copyright footer (© 2025 CompanyName · All rights reserved)
      text = text.replace(/©\s*\d{4}\s+[^\n]*?All rights reserved\.?/gi, '');

      // Strip "This email was sent by..." sub-footer
      text = text.replace(/This email was sent by\s+[^.]+\.\s*If you believe.*?disregard\.?/gsi, '');

      // Strip the standalone company name line that appears in the footer
      // (it's typically the last meaningful line before copyright)

      bodyContent = text;
    } else {
      bodyContent = content;
    }

    // Now apply standard cleaning
    const lines = bodyContent.split('\n');
    const cleanedLines = [];
    let inQuoted = false;
    for (const line of lines) {
      const trimmed = line.trim();
      if (/^On\s+.*?wrote:/i.test(trimmed)) { inQuoted = true; continue; }
      if (inQuoted) { if (trimmed.startsWith('>') || trimmed === '') continue; inQuoted = false; }
      if (trimmed === '>' || (trimmed.startsWith('>') && trimmed.length === 1)) continue;
      if (!inQuoted) cleanedLines.push(trimmed.startsWith('>') ? line.replace(/^>\s*/, '') : line);
    }
    const cleaned = cleanedLines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
    return cleaned;
  };

  // Strip greeting line (Dear/Hi/Hello [Name],) for preview only
  const getPreviewContent = (content) => {
    if (!content) return '';
    const lines = content.split('\n');
    // Skip leading empty lines and greeting
    let startIdx = 0;
    for (let i = 0; i < lines.length; i++) {
      const trimmed = lines[i].trim();
      if (!trimmed) { startIdx = i + 1; continue; }
      if (/^(dear|hi|hello|hey)\s+/i.test(trimmed)) { startIdx = i + 1; continue; }
      break;
    }
    // Skip empty lines after greeting
    while (startIdx < lines.length && !lines[startIdx].trim()) startIdx++;
    const preview = lines.slice(startIdx).join('\n').trim();
    return preview || content; // fallback to original if everything was stripped
  };

  // ── Organize timeline events from replies ──
  const getTimelineEvents = () => {
    if (!replies || replies.length === 0) return [];

    const sorted = [...replies].sort(
      (a, b) => new Date(a.received_at || a.sent_at || a.created_at || 0) - new Date(b.received_at || b.sent_at || b.created_at || 0)
    );

    let isFirstSent = true;

    const events = sorted.map((r) => {
      const date = r.received_at || r.sent_at || r.created_at;
      const isSent = r.is_sent_email === true;
      const isNudge = r.is_nudge === true || r.is_nudge === 'true' || r.is_nudge === 1;
      const subjectLower = (r.subject || '').toLowerCase();
      const contentLower = (r.content || '').toLowerCase();

      let type = 'reply_received';
      let label = 'Reply Received';
      let color = '#8B5CF6'; // purple for replies
      let phase = 'replies'; // phase grouping

      if (isSent) {
        // Classify sent emails by context
        if (subjectLower.includes('resume') || subjectLower.includes('cv') ||
            contentLower.includes('share your updated resume') || contentLower.includes('request for resume')) {
          type = 'resume_request_sent';
          label = 'Resume Request Sent';
          color = '#F59E0B'; // amber
          phase = 'replies';
        } else if (subjectLower.includes('reschedule') || contentLower.includes('your booking has been cancelled') ||
                   contentLower.includes('cancelled your previous interview slot')) {
          // Reschedule invitation sent (when candidate requested reschedule after booking)
          type = 'interview_rescheduled';
          label = 'Reschedule Invitation';
          color = '#F59E0B'; // amber to indicate action needed
          phase = 'interview';
        } else if (subjectLower.includes('confirmed') || contentLower.includes('successfully scheduled') ||
                   contentLower.includes('google meet link') || contentLower.includes('meet.google.com')) {
          // Interview confirmation email (booking confirmed)
          type = 'interview_confirmed';
          label = 'Interview Confirmed';
          color = '#22C55E'; // green
          phase = 'interview';
        } else if ((subjectLower.includes('interview') && subjectLower.includes('invitation')) ||
                   contentLower.includes('book your interview slot') || contentLower.includes('select your preferred time slot') ||
                   contentLower.includes('booking link')) {
          // Interview invitation email (booking link sent)
          type = 'interview_invitation';
          label = 'Interview Invitation';
          color = '#3B82F6'; // blue to indicate action needed
          phase = 'interview';
        } else if (subjectLower.includes('interview') || subjectLower.includes('schedule') ||
                   contentLower.includes('interview') || r.interview_status) {
          // Generic interview-related email
          type = 'interview_scheduled';
          label = 'Interview Scheduled';
          color = '#22C55E'; // green
          phase = 'interview';
        } else if (isNudge) {
          type = 'followup_sent';
          label = 'Follow-up Sent';
          color = secondaryColor; // teal
          phase = 'followup';
        } else if (isFirstSent) {
          type = 'outreach_sent';
          label = 'Outreach Sent';
          color = primaryColor; // blue
          phase = 'outreach';
        } else {
          // Reply to candidate's question or general reply
          type = 'reply_sent';
          label = 'Reply Sent';
          color = primaryColor;
          phase = 'replies';
        }
        isFirstSent = false;
      } else {
        // Received email — classify by interview_status field first, then sentiment
        const interviewStatus = (r.interview_status || '').toLowerCase();
        if (interviewStatus === 'confirmed' || interviewStatus === 'scheduled') {
          type = 'interview_confirmed';
          label = 'Interview Confirmed';
          color = '#22C55E'; // green
          phase = 'interview';
        } else if (interviewStatus === 'reschedule' || interviewStatus === 'reschedule_requested' ||
                   subjectLower.includes('reschedule') || contentLower.includes('reschedule')) {
          // Candidate requesting a reschedule
          type = 'reschedule_requested';
          label = 'Reschedule Requested';
          color = '#F59E0B'; // amber
          phase = 'interview';
        } else {
          type = 'reply_received';
          label = 'Reply Received';
          color = '#8B5CF6'; // purple
          phase = 'replies';
        }
      }

      // Only show attachment info for received messages (candidate replies), not sent emails
      // The sent_emails_tracking may have doc_id/attachment_location from the reply but it belongs to the candidate's message
      const showAttachment = !isSent && !!(r.attachment_location || r.attachment);
      
      return {
        date,
        type,
        label,
        color,
        phase,
        threadId: r.thread_id || null,
        content: cleanEmailContent(r.content) || (isSent ? '[Email sent]' : 'No content'),
        subject: r.subject || '',
        hasAttachment: showAttachment,
        attachmentInfo: showAttachment ? (r.attachment_location ? r.attachment_location.split('/').pop() : 'Attachment') : null,
        hasResumeDoc: !isSent && !!r.doc_id,
        docId: !isSent ? (r.doc_id || null) : null,
        isViewed: r.is_read || r.is_opened,
        sentiment: r.sentiment || null,
        isSent,
      };
    });

    // Group messages by thread_id so replies always appear under their parent thread,
    // even if a reply to an earlier thread arrives after a later thread was created.
    // e.g. Outreach(threadA) → Followup1(threadB) → Followup2(threadC) → Reply-to-Followup1(threadB)
    // should show: [Outreach] [Followup1 + its reply] [Followup2] — NOT a separate "Conversation" section.
    const threadGroups = new Map(); // threadId → { firstDate, events[], firstType, firstPhase }
    const noThreadEvents = []; // events without a thread_id

    for (const evt of events) {
      const tid = evt.threadId;
      if (!tid) {
        noThreadEvents.push(evt);
        continue;
      }
      if (!threadGroups.has(tid)) {
        threadGroups.set(tid, {
          firstDate: new Date(evt.date || 0),
          events: [],
          firstType: evt.type,
          firstPhase: evt.phase,
        });
      }
      threadGroups.get(tid).events.push(evt);
    }

    // Sort thread groups by the date of their first message (preserves original thread order)
    const sortedThreads = [...threadGroups.values()].sort(
      (a, b) => a.firstDate - b.firstDate
    );

    // Build final list with dividers between thread groups
    const withDividers = [];
    let threadIndex = 0;

    for (const group of sortedThreads) {
      // Sort messages within each thread chronologically
      group.events.sort(
        (a, b) => new Date(a.date || 0) - new Date(b.date || 0)
      );

      // Determine a label for this thread group based on the first message's type
      const threadLabel = group.firstType === 'outreach_sent' ? 'outreach'
        : group.firstType === 'followup_sent' ? 'followup'
        : group.firstType === 'interview_scheduled' ? 'interview'
        : group.firstType === 'interview_invitation' ? 'interview'
        : group.firstType === 'interview_confirmed' ? 'interview'
        : group.firstType === 'interview_rescheduled' ? 'reschedule'
        : group.firstType === 'reschedule_requested' ? 'reschedule'
        : group.firstType === 'resume_request_sent' ? 'resume'
        : threadIndex === 0 ? 'outreach'
        : 'conversation';

      // Skip divider for the very first thread at the top — it's already obvious
      if (threadIndex > 0) {
        withDividers.push({ isDivider: true, label: threadLabel, phase: group.firstPhase });
      }
      withDividers.push(...group.events);
      threadIndex++;
    }

    // Append any events without a thread_id at the end
    if (noThreadEvents.length > 0) {
      if (threadIndex > 0) {
        withDividers.push({ isDivider: true, label: 'conversation', phase: 'replies' });
      }
      withDividers.push(...noThreadEvents);
    }

    return withDividers;
  };

  // ── Engagement summary ──
  const getEngagementSummary = () => {
    const outreachSent = replies.filter((r) => r.is_sent_email).length;
    const repliesReceived = replies.filter((r) => !r.is_sent_email).length;
    const hasResume = !!candidate.doc_id || replies.some((r) => r.doc_id || r.attachment_location || r.attachment);
    const firstSent = replies.find((r) => r.is_sent_email);
    const firstReply = replies.find((r) => !r.is_sent_email);
    let responseTime = '—';
    if (firstSent && firstReply) {
      const diff = Math.abs(new Date(firstReply.received_at || firstReply.created_at) - new Date(firstSent.sent_at || firstSent.created_at));
      const hours = Math.floor(diff / 3600000);
      responseTime = hours < 24 ? `${hours} hours` : `${Math.floor(hours / 24)} days`;
    }
    return { outreachSent, repliesReceived, hasResume, responseTime, hasOutreach: outreachSent > 0 };
  };

  const getResolvedCandidateStage = () => {
    const stageRank = {
      sourced: 0,
      identified: 0,
      pending: 0,
      contacted: 1,
      responded: 1,
      interested: 2,
      shortlisted: 3,
      interview: 3,
      'interview scheduled': 3,
      interviewed: 4,
      finalised: 4,
      hired: 4,
    };

    const funnelStage = (candidate?.funnel_stage || '').toLowerCase().trim();
    const statusStage = (candidate?.status || '').toLowerCase().trim();
    const completedStatuses = ['completed', 'recording_completed', 'ended', 'left', 'done', 'finished', 'disconnected', 'closed'];
    const bookedStatus = String(bookedInterviewData?.status || '').trim().toLowerCase();
    const noteTakerStatus = String(bookedInterviewData?.ai_note_taker_status || '').trim().toLowerCase();
    const hasCompletedBookedRow = Boolean(
      bookedInterviewData && (
        bookedInterviewData.meeting_completed ||
        completedStatuses.includes(bookedStatus) ||
        completedStatuses.includes(noteTakerStatus)
      )
    );
    // hasCompletedAIResult is computed once at component scope (completion-gated, not mere presence).
    const hasCompletedInterview =
      hasCompletedBookedRow ||
      hasCompletedAIResult ||
      aiNotes.some((note) => isInterviewNoteCompleted(note));

    if (funnelStage === 'rejected' || statusStage === 'rejected') {
      return 'rejected';
    }
    if (funnelStage === 'declined' || statusStage === 'declined') {
      return 'declined';
    }

    let bestRank = Math.max(stageRank[funnelStage] ?? 0, stageRank[statusStage] ?? 0);
    if (hasCompletedInterview) {
      bestRank = Math.max(bestRank, stageRank.interviewed);
    }

    const resolvedByRank = ['sourced', 'contacted', 'interested', 'shortlisted', 'interviewed'];
    return resolvedByRank[bestRank] || 'sourced';
  };

  const formatCandidateStageLabel = (stage) => {
    const normalized = String(stage || '').trim().toLowerCase().replace(/_/g, ' ');
    if (!normalized) return 'Pending';
    return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // ── Funnel stage — derived from DB funnel_stage (updated at each transition) ──
  const getFunnelStage = () => {
    const effectiveStage = getResolvedCandidateStage();
    const isDeclined = effectiveStage === 'declined';
    const isRejected = effectiveStage === 'rejected';

    // Sequential progression: Sourced → Contacted → Interested → Shortlisted → Interviewed
    const allStages = ['Sourced', 'Contacted', 'Interested', 'Shortlisted', 'Interviewed'];

    // Map DB values to stage index
    const stageMap = {
      'sourced': 0, 'identified': 0,
      'contacted': 1, 'responded': 1,
      'interested': 2,
      'shortlisted': 3, 'interview': 3,
      'interviewed': 4,
      'hired': 4, 'finalised': 4,
    };

    let progress = stageMap[effectiveStage] ?? 0;
    if (isDeclined) progress = 0; // will show Declined separately

    if (isDeclined || isRejected) {
      const hasSentEmail = replies.some((r) => r.is_sent_email === true);
      const hasReceivedReply = replies.some((r) => !r.is_sent_email);
      let terminalProgress = 0;
      if (hasSentEmail) terminalProgress = 1;
      if (hasReceivedReply) terminalProgress = 2;

      const priorStages = allStages.slice(0, terminalProgress + 1);
      const stages = [...priorStages, isRejected ? 'Rejected' : 'Declined'];
      return { stages, active: stages.length - 1, isDeclined, isRejected };
    }

    return { stages: allStages, active: progress, isDeclined: false, isRejected: false };
  };

  // ── Suggested next step ──
  const getSuggestedNextStep = () => {
    const s = (candidate?.status || '').toLowerCase();
    const funnelStage = getResolvedCandidateStage();
    const effectiveStage = funnelStage || s;

    if (effectiveStage === 'rejected') return 'Closed · Rejected';
    if (effectiveStage === 'declined') return 'Closed · Declined';

    // If candidate has submitted a resume, prioritize interview scheduling
    const candidateHasResume = replies.some((r) => r.doc_id || r.attachment_location || r.attachment);
    if (candidateHasResume) {
      if (['interviewed', 'interview', 'interview scheduled', 'finalised', 'hired'].includes(effectiveStage)) {
        return 'Conduct Interview · Prepare evaluation form';
      }
      return 'Review Resume & Schedule Interview';
    }

    // If there's a ready draft, reply takes priority
    if (hasQuestionDraft) return "Reply to Candidate's Question";
    // If candidate asked a question but no draft yet, create one
    if (hasCandidateQuestion) return 'Create Draft Reply for Candidate';
    if (effectiveStage === 'interested') return 'Schedule Technical Interview · Round 1';
    if (effectiveStage === 'shortlisted') return 'Schedule Interview · Round 1';
    if (['interviewed', 'interview', 'interview scheduled'].includes(effectiveStage)) return 'Conduct Interview · Prepare evaluation form';
    if (['finalised', 'hired'].includes(effectiveStage)) return 'Send Offer Letter';
    if (s === 'responded') return 'Review Response & Qualify';
    return 'Send Follow-up Email';
  };

  // ── Reschedule request approve/reject handlers ──
  const handleApproveReschedule = async () => {
    if (!rescheduleRequest?.id) return;
    setRescheduleActionLoading(true);
    try {
      const result = await aiAutomationApi.post(`/interviews/reschedule-requests/${rescheduleRequest.id}/approve`, {});
      syncCandidateStageLocally('Shortlisted');
      if (result?.gmail_auth_needed || result?.mail_auth_needed || isMailProviderAuthError(result)) {
        const interviewId = result?.interview_id || result?.interview?.id || rescheduleRequest?.interview_id;
        showToast('Reschedule approved. Confirmation email needs mail reconnected — opening reconnect…', 'warning');
        await handleMailProviderReconnect({
          cid: getCid(),
          error: result,
          onConnected: async () => {
            if (!interviewId) {
              showToast('Mail connected, but confirmation email could not be retried automatically.', 'error');
              return;
            }
            try {
              const rr = await aiAutomationApi.post(`/interviews/${interviewId}/resend-confirmation`, {});
              if (rr?.success) {
                showToast(`Confirmation email sent to ${rr.email || email || name}.`, 'success');
              } else if (rr?.gmail_auth_needed || rr?.mail_auth_needed || isMailProviderAuthError(rr)) {
                showToast('Mail provider is still not connected — please reconnect and try again.', 'error');
              } else {
                showToast('Could not send the confirmation email. Please try resending.', 'error');
              }
            } catch (e) {
              showToast(e?.message || 'Could not send the confirmation email. Please try resending.', 'error');
            }
          },
          onError: (msg) => showToast(msg || 'Mail reconnect was cancelled or failed.', 'error'),
        });
      } else {
        showToast('Reschedule request approved — candidate notified', 'success');
      }
      setRescheduleRequest(null);
      await Promise.allSettled([loadConversation(true), refreshBookedInterview(), refreshRescheduleRequest()]);
      window.dispatchEvent(new CustomEvent('candidateDetailRefresh'));
    } catch (err) {
      showToast(err?.message || 'Failed to approve reschedule', 'error');
    } finally {
      setRescheduleActionLoading(false);
    }
  };

  const handleRejectReschedule = async () => {
    if (!rescheduleRequest?.id) return;
    setRescheduleActionLoading(true);
    try {
      await aiAutomationApi.post(`/interviews/reschedule-requests/${rescheduleRequest.id}/reject`, {
        decline_reason: 'The interviewer is not available on the requested date. Please use your existing slot.',
      });
      syncCandidateStageLocally('Shortlisted');
      showToast('Reschedule request declined — candidate notified', 'success');
      setRescheduleRequest(null);
      await Promise.allSettled([loadConversation(true), refreshBookedInterview(), refreshRescheduleRequest()]);
      window.dispatchEvent(new CustomEvent('candidateDetailRefresh'));
    } catch (err) {
      showToast(err?.message || 'Failed to reject reschedule', 'error');
    } finally {
      setRescheduleActionLoading(false);
    }
  };

  // ── Mark As Priority handler — toggles priority flag ──
  const handleMarkAsPriority = async () => {
    if (!email || !resolvedSearchId) {
      showToast('Missing candidate email or search ID', 'error');
      return;
    }
    setPriorityLoading(true);
    try {
      const targetPriority = !isPriority;
      const result = await aiAutomationApi.post('/candidates/mark-priority', {
        candidate_email: email,
        search_id: resolvedSearchId,
        session_id: sessionId,
        priority: targetPriority,
      });

      const newPriority = result?.priority ?? targetPriority;
      setIsPriority(newPriority);
      // Persist to parent so navigating back and forth keeps the state
      if (onCandidateUpdate) onCandidateUpdate(email, { priority: newPriority });
      showToast(
        newPriority
          ? `${name} has been marked as priority!`
          : `${name} removed from priority.`,
        'success'
      );
    } catch (err) {
      showToast(err?.message || 'Failed to update priority', 'error');
    } finally {
      setPriorityLoading(false);
    }
  };

  // ── Send resume request email from modal ──
  const handleSendResumeRequest = async (subject, body) => {
    try {
      const metadata = getMetadata();
      const userId = metadata?.uid || metadata?.userId || metadata?.id;
      await aiAutomationApi.post('/outreach/send', {
        user_id: String(userId || ''),
        search_id: resolvedSearchId,
        cid: getCid() ? String(getCid()) : undefined,
        messages: [{
          candidateId: String(candidateRecordId || email),
          candidateName: name,
          email,
          subject,
          message: body,
          channel: 'email',
          purpose: 'resume_request',
        }],
      });
      setResumeRequestModal({ isOpen: false, draftSubject: '', draftBody: '', loading: false });
      showToast('Resume request email sent successfully!', 'success');
    } catch (err) {
      // Check if the active mail provider is not connected — auto-trigger OAuth
      if (isMailProviderAuthError(err)) {
        showToast('Mail provider is not connected. Opening authorization...', 'error');
        await handleMailProviderReconnect({
          cid: getCid(),
          error: err,
          onConnected: (connectedEmail, providerLabel) => showToast(`${providerLabel} connected${connectedEmail ? ` (${connectedEmail})` : ''}! Please click Send again.`, 'success'),
          onError: (msg) => showToast(`Mail connection failed: ${msg}`, 'error'),
        });
        return;
      }
      const googlePermMsg = getGooglePermissionError(err);
      if (googlePermMsg) {
        showToast(googlePermMsg, 'warning', 8000);
        return;
      }
      showToast(err?.message || 'Failed to send resume request', 'error');
    }
  };

  // ── Direct Book Interview ──
  const handleOpenDirectBook = async () => {
    if (!canUseInterviews) return;
    setDirectBookOpen(true);
    setDirectBookAiSetup(null);
    setDirectBookAiSetups([]);
    // Set default date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const yyyy = tomorrow.getFullYear();
    const mm = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const dd = String(tomorrow.getDate()).padStart(2, '0');
    setDirectBookForm(prev => ({ ...prev, date: `${yyyy}-${mm}-${dd}`, interviewType: 'manual' }));
    // Fetch interviewers
    try {
      const metadata = getMetadata();
      const userId = metadata?.uid || metadata?.userId || metadata?.id;
      if (userId) {
        const data = await aiAutomationApi.get(`/interviewers?user_id=${userId}&active_only=true`);
        // The shared Wiza AI interviewer (id "ai-interviewer-<cid>", email "ai-interviewer+<cid>@…")
        // only runs AI interviews — never offer it as a human interviewer in the Manual flow.
        const list = (data.interviewers || []).filter(iw =>
          !String(iw.id || '').startsWith('ai-interviewer')
          && !String(iw.email || '').startsWith('ai-interviewer+'));
        setDirectBookInterviewers(list);
        if (list.length > 0) {
          setDirectBookForm(prev => ({ ...prev, interviewerId: prev.interviewerId || list[0].id }));
        }
      }
    } catch (err) {
      console.error('Failed to load interviewers:', err);
    }
    // Fetch this job's saved AI blueprints (if any) so direct booking can use a specific setup.
    try {
      if (resolvedSearchId) {
        setDirectBookAiSetupsLoading(true);
        const data = await aiAutomationApi.get(
          `/interviews/search/${encodeURIComponent(resolvedSearchId)}/setups`
        );
        const setups = Array.isArray(data?.setups) ? data.setups : [];
        setDirectBookAiSetups(setups);
        if (setups.length > 0) {
          selectDirectBookAiSetup(setups[0]);
        } else {
          const aiSetup = await aiAutomationApi.get(
            `/interviews/search/${encodeURIComponent(resolvedSearchId)}/setup?interview_type=ai`
          );
          if (aiSetup && aiSetup.exists) {
            setDirectBookAiSetups([aiSetup]);
            selectDirectBookAiSetup(aiSetup);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load AI interview setup:', err);
    } finally {
      setDirectBookAiSetupsLoading(false);
    }
  };

  const handleDirectBookSubmit = async () => {
    if (!directBookForm.date || !directBookForm.startTime) {
      showToast('Please select a date and time.', 'error');
      return;
    }
    const _isAi = directBookForm.interviewType === 'ai';
    const selectedAiDuration = _isAi
      ? Number(directBookAiSetupDuration || directBookForm.duration || 15)
      : Number(directBookForm.duration || 30);
    if (_isAi && directBookAiSetups.length > 0 && !directBookAiSetup?.setup_id) {
      showToast('Please choose the AI interview setup to use.', 'error');
      return;
    }
    setDirectBookLoading(true);
    try {
      const result = await aiAutomationApi.post('/interviews/direct-book', {
        candidate_name: name,
        candidate_email: email,
        candidate_phone: candidate?.phone || candidate?.mobile_number || '',
        date: directBookForm.date,
        start_time: directBookForm.startTime,
        duration: selectedAiDuration,
        // AI is run by the shared Wiza interviewer, so no human interviewer is needed.
        interviewer_id: _isAi ? null : (directBookForm.interviewerId || null),
        search_id: resolvedSearchId,
        title: jobTitle || '',
        interview_type: _isAi ? 'ai' : 'manual',
        interview_setup_id: _isAi ? (directBookAiSetup?.setup_id || null) : null,
        ai_configuration_id: _isAi ? (directBookAiSetup?.ai_configuration_id || null) : null,
        // When AI is chosen for a job with NO saved setup, send the inline quick-setup so the backend
        // creates a real config for this job (used by this and future bookings). Ignored otherwise.
        ai_config_setup: (_isAi && !directBookAiSetup) ? {
          name: (directBookAiConfig.name || '').trim(),
          total_questions: directBookAiConfig.total_questions,
          duration_minutes: selectedAiDuration,
          description: (directBookAiConfig.description || '').trim(),
        } : null,
      });
      if (result.success) {
        syncCandidateStageLocally('Shortlisted', {
          scheduled_date: directBookForm.date,
          scheduled_time: directBookForm.startTime,
          duration: result?.duration || selectedAiDuration,
          interviewer_id: _isAi ? null : (directBookForm.interviewerId || null),
          interviewer_name: _isAi ? 'Wiza AI Interviewer' : null,
          interview_status: result?.status || 'scheduled',
          booking_status: 'booked',
        });
        setDirectBookOpen(false);
        setHasBookedInterview(true);
        window.dispatchEvent(new CustomEvent('candidateDetailRefresh'));

        if (result.gmail_auth_needed || result.mail_auth_needed || isMailProviderAuthError(result)) {
          // Booking is committed, but the confirmation/launch-link email couldn't be sent because the
          // company mail provider is expired/revoked. Prompt a reconnect, then AUTO-RESEND the email.
          showToast('Interview booked. The confirmation email needs mail reconnected — opening reconnect…', 'warning');
          await handleMailProviderReconnect({
            cid: getCid(),
            error: result,
            onConnected: async () => {
              try {
                const rr = await aiAutomationApi.post(`/interviews/${result.interview_id}/resend-confirmation`, {});
                if (rr?.success) {
                  showToast(`Confirmation email sent to ${rr.email || email || name}.`, 'success');
                } else if (rr?.gmail_auth_needed || rr?.mail_auth_needed || isMailProviderAuthError(rr)) {
                  showToast('Mail provider is still not connected — please reconnect and try again.', 'error');
                } else {
                  showToast('Could not send the confirmation email. Please try resending.', 'error');
                }
              } catch (e) {
                showToast('Could not send the confirmation email. Please try resending.', 'error');
              }
            },
            onError: (msg) => showToast(msg || 'Mail reconnect was cancelled or failed.', 'error'),
          });
        } else {
          showToast(`Interview booked for ${name} on ${directBookForm.date} at ${formatTime12hr(directBookForm.startTime)}!`, 'success');
        }
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.detail || err.message || 'Failed to book interview.';
      showToast(detail, 'error');
    } finally {
      setDirectBookLoading(false);
    }
  };

  // ── Open Initial Outreach Modal — generates a draft email ──
  const handleOpenInitialOutreach = async () => {
    // Check if candidate has an email address
    if (!email || !email.trim()) {
      setInitialOutreachModal({
        isOpen: true,
        draftSubject: '',
        draftBody: '',
        loading: false,
        sending: false,
        noEmail: true,
      });
      return;
    }
    setInitialOutreachModal({ isOpen: true, draftSubject: '', draftBody: '', loading: true, sending: false, noEmail: false });
    try {
      const metadata = getMetadata();
      const userId = metadata?.uid || metadata?.userId || metadata?.id;
      const activeSessionId = sessionId || '';
      const activeSearchId = resolvedSearchId || '';
      if (!activeSessionId) {
        console.warn('[CandidateDetail] No sessionId available for outreach template generation');
      }
      // Generate a personalized outreach draft for this candidate using proper backend fields
      const result = await aiAutomationApi.post('/outreach/generate-template', {
        session_id: activeSessionId,
        search_id: activeSearchId,
        candidate_ids: candidate?.id ? [String(candidate.id)] : [],
        channel: 'email',
      });
      // Backend TemplateGenerateResponse returns: template, email_template, job_title, company_name
      const jobTitle = result?.job_title || '';
      const companyName = result?.company_name || '';
      const subject = jobTitle
        ? `Exciting Opportunity — ${jobTitle}${companyName ? ` at ${companyName}` : ''}`
        : `Exciting Opportunity — ${name}`;
      const body = result?.email_template || result?.template || `Hi ${name},\n\nI came across your profile and was impressed by your background. I'd love to discuss an exciting opportunity that aligns with your experience.\n\nWould you be available for a brief conversation?\n\nBest regards`;
      setInitialOutreachModal((prev) => ({ ...prev, draftSubject: subject, draftBody: body, loading: false }));
    } catch (err) {
      console.error('[CandidateDetail] Failed to generate outreach template:', err?.message || err);
      // Fallback: provide a basic draft
      setInitialOutreachModal((prev) => ({
        ...prev,
        draftSubject: `Exciting Opportunity — ${name}`,
        draftBody: `Hi ${name},\n\nI came across your profile and was impressed by your background. I'd love to discuss an exciting opportunity that aligns with your experience.\n\nWould you be available for a brief conversation?\n\nBest regards`,
        loading: false,
      }));
    }
  };

  // ── Send Initial Outreach email from modal ──
  const handleSendInitialOutreach = async (subject, body) => {
    setInitialOutreachModal((prev) => ({ ...prev, sending: true }));

    const sendAsFollowup = async () => {
      const metadata = getMetadata();
      const userId = metadata?.uid || metadata?.userId || metadata?.id;
      await aiAutomationApi.post('/email/send-followup', {
        candidate_email: email,
        candidate_id: String(candidate?.id || ''),
        search_id: resolvedSearchId,
        user_id: String(userId || ''),
        subject,
        body,
        cid: getCid() ? String(getCid()) : undefined,
      });
    };

    const handleDuplicateInitialOutreach = async (err) => {
      const detail = err?.response?.data?.detail || err?.detail || {};
      const code = detail?.error_code || err?.response?.data?.error_code || err?.error_code;
      if (code !== 'DUPLICATE_INITIAL_OUTREACH') return false;

      const ok = window.confirm('This candidate was already contacted. Do you want to send this as a follow-up instead?');
      if (!ok) {
        setInitialOutreachModal((prev) => ({ ...prev, sending: false }));
        return true;
      }

      try {
        await sendAsFollowup();
        setInitialOutreachModal({ isOpen: false, draftSubject: '', draftBody: '', loading: false, sending: false });
        showToast('Follow-up email sent successfully!', 'success');
        window.dispatchEvent(new CustomEvent('candidateDetailRefresh'));
      } catch (followErr) {
        setInitialOutreachModal((prev) => ({ ...prev, sending: false }));
        showToast(followErr?.message || 'Failed to send follow-up', 'error');
      }
      return true;
    };

    const doSend = async () => {
      const metadata = getMetadata();
      const userId = metadata?.uid || metadata?.userId || metadata?.id;
      const response = await aiAutomationApi.post('/outreach/send', {
        messages: [{
          candidateId: String(candidate?.id || ''),
          candidateName: name,
          email: email,
          message: body,
          subject: subject,
          channel: 'email',
        }],
        session_id: sessionId,
        search_id: resolvedSearchId,
        user_id: String(userId || ''),
        cid: getCid() ? String(getCid()) : undefined,
      });
      return assertOutreachEmailSent(response);
    };

    try {
      await doSend();
      syncCandidateStageLocally('Contacted');
      setInitialOutreachModal({ isOpen: false, draftSubject: '', draftBody: '', loading: false, sending: false });
      showToast('Initial outreach email sent successfully!', 'success');
      // Refresh conversation data to reflect the sent outreach
      if (resolvedSearchId) {
        try {
          const metadata2 = getMetadata();
          const userId2 = metadata2?.uid || metadata2?.userId || metadata2?.id;
          const data = await aiAutomationApi.get(`/email/reply-status/${userId2}/${resolvedSearchId}`);
          if (data?.all_replies) {
            // Trigger parent refresh
          }
        } catch { /* ignore */ }
      }
      // Trigger a refresh of conversation
      window.dispatchEvent(new CustomEvent('candidateDetailRefresh'));
    } catch (err) {
      // Check if the active mail provider is not connected — auto-trigger OAuth and auto-retry
      if (isMailProviderAuthError(err)) {
        showToast('Mail provider is not connected. Opening authorization...', 'warning');
        const connected = await handleMailProviderReconnect({
          cid: getCid(),
          error: err,
          onConnected: (connectedEmail, providerLabel) => showToast(`${providerLabel} connected${connectedEmail ? ` (${connectedEmail})` : ''}! Sending outreach...`, 'success'),
          onSwitchPending: (previousEmail, newEmail) => Promise.resolve(window.confirm(
            `Switch Gmail from ${previousEmail} to ${newEmail}?\n\n`
            + 'Past email conversations from the previous account will no longer sync.'
          )),
          onError: (msg) => {
            showToast(`Mail connection failed: ${msg}`, 'error');
            setInitialOutreachModal((prev) => ({ ...prev, sending: false }));
          },
        });
        if (connected) {
          // Auto-retry sending after successful OAuth connection
          try {
            await doSend();
            syncCandidateStageLocally('Contacted');
            setInitialOutreachModal({ isOpen: false, draftSubject: '', draftBody: '', loading: false, sending: false });
            showToast('Initial outreach email sent successfully!', 'success');
            window.dispatchEvent(new CustomEvent('candidateDetailRefresh'));
          } catch (retryErr) {
            if (await handleDuplicateInitialOutreach(retryErr)) return;
            setInitialOutreachModal((prev) => ({ ...prev, sending: false }));
            showToast(retryErr?.message || 'Failed to send outreach after connecting mail provider', 'error');
          }
        } else {
          setInitialOutreachModal((prev) => ({ ...prev, sending: false }));
        }
        return;
      }
      if (await handleDuplicateInitialOutreach(err)) return;
      setInitialOutreachModal((prev) => ({ ...prev, sending: false }));
      const googlePermMsg = getGooglePermissionError(err);
      showToast(googlePermMsg || err?.message || 'Failed to send initial outreach', googlePermMsg ? 'warning' : 'error', googlePermMsg ? 8000 : 4000);
    }
  };

  const timelineEvents = getTimelineEvents();
  const engagement = getEngagementSummary();
  const funnel = getFunnelStage();
  // Imported (Excel) candidates have no outreach/resume by design but are ready to interview, so they
  // may be booked directly regardless of the outreach/resume/funnel gate below.
  const isImported = String(candidate?.source_type || candidate?._source_type || '').toLowerCase() === 'imported';

  // Meeting Analysis (shared modal): AI interviews surface the scored AI result; human meetings
  // surface the primary note's analysis. Reject is offered unless the candidate is already terminal.
  const primaryManualNote =
    aiNotes.find((n) => n?.meeting_analysis_json?.success)
    || aiNotes.find((n) => n?.meeting_analysis_json)
    || aiNotes.find((n) => n?.notes?.transcript)
    || null;
  const primaryManualAnalysis = primaryManualNote?.meeting_analysis_json || null;
  const resolvedCandidateStage = getResolvedCandidateStage();
  const terminalStageLower = String(resolvedCandidateStage || candidate?.funnel_stage || candidate?._funnel_stage || candidate?.status || '').toLowerCase();
  const canReject = !['declined', 'rejected', 'hired'].includes(terminalStageLower);
  const isRejected = terminalStageLower === 'rejected';
  // Terminal (out of the job): hide all active actions (outreach / priority / schedule) for these.
  const isTerminalOut = ['rejected', 'declined'].includes(terminalStageLower);
  const rejectionReason = candidate?.rejection_reason || candidate?._rejection_reason || fetchedRejectionReason || '';
  const suggestedStep = getSuggestedNextStep();
  const effectiveHasBookedInterview =
    hasBookedInterview || Boolean(bookedInterviewData?.id) || interviewSessions.length > 0 || aiNotes.length > 0;
  const completedInterviewStatuses = ['completed', 'recording_completed', 'ended', 'left', 'done', 'finished', 'disconnected', 'closed'];
  const hasCompletedInterviewSession = interviewSessions.some(
    (session) => String(session?.status || '').trim().toLowerCase() === 'completed'
  );
  const hasCompletedBookedInterview = Boolean(
    bookedInterviewData && (
      completedInterviewStatuses.includes(String(bookedInterviewData?.ai_note_taker_status || '').trim().toLowerCase()) ||
      String(bookedInterviewData?.status || '').trim().toLowerCase() === 'completed' ||
      bookedInterviewData?.meeting_completed
    )
  );
  const hasCompletedInterviewNotes = aiNotes.some((note) => isInterviewNoteCompleted(note));
  // Show the Interview Report only once an interview has actually been COMPLETED — not merely booked or
  // invited. A booked-but-unattended interview (e.g. an imported candidate who was just sent an invite)
  // must NOT surface a report, so gate strictly on completion signals (completed notes / session /
  // booked row, a scored AI result, or a resolved "interviewed" stage) — never on mere booking.
  const canShowMeetingAnalysisButton =
    (isFullyAIInterview && Boolean(bookedInterviewData?.id) &&
      (hasCompletedAIResult || bookedInterviewData?.meeting_completed)) ||
    hasCompletedInterviewNotes ||
    hasCompletedInterviewSession ||
    hasCompletedBookedInterview ||
    resolvedCandidateStage === 'interviewed';

  // While a scheduled interview's time window is still open (upcoming or in progress) and it hasn't been
  // completed, hide Reject — you shouldn't reject a candidate mid-interview. `canJoinBookedInterview` is
  // true from now until scheduled_time + duration. Once the window passes (no-show) or the interview
  // completes, Reject returns so the recruiter can act on the outcome.
  const interviewWindowOpen =
    canJoinBookedInterview && !hasCompletedBookedInterview && !hasCompletedAIResult;

  // ── Timeline event icon SVGs ──
  const TimelineIcon = ({ type, color }) => {
    if (type === 'outreach_sent' || type === 'reply_sent') {
      // Paper plane icon (sent mail)
      return (
        <svg className="w-4 h-4" fill="none" stroke={color} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
      );
    }
    if (type === 'followup_sent') {
      // Refresh icon (follow-up)
      return (
        <svg className="w-4 h-4" fill="none" stroke={color} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
      );
    }
    if (type === 'resume_request_sent') {
      // Document icon (resume request)
      return (
        <svg className="w-4 h-4" fill="none" stroke={color} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
      );
    }
    if (type === 'interview_scheduled' || type === 'interview_confirmed' || type === 'interview_invitation') {
      // Calendar check icon (interview)
      return (
        <svg className="w-4 h-4" fill="none" stroke={color} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
      );
    }
    if (type === 'interview_rescheduled' || type === 'reschedule_requested') {
      // Refresh calendar icon (reschedule)
      return (
        <svg className="w-4 h-4" fill="none" stroke={color} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
      );
    }
    // reply_received — incoming arrow
    return (
      <svg className="w-4 h-4" fill="none" stroke={color} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
    );
  };

  return (
    <div className={`flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-950 min-h-0 w-full`}>
      <div className={`w-full ${inline ? 'px-6 py-4 space-y-4' : 'px-8 py-6 space-y-6'}`}>

          {/* Breadcrumb — hidden in inline/accordion mode */}
          {!inline && (
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <button
              onClick={onBack}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full font-medium transition-colors"
              style={{
                color: '#3B5BDB',
                background: isDark ? 'rgba(59, 91, 219, 0.18)' : 'rgba(59, 91, 219, 0.1)',
                border: `1px solid ${isDark ? 'rgba(59, 91, 219, 0.28)' : 'rgba(59, 91, 219, 0.18)'}`,
              }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {backLabel}
            </button>
            <span className="text-gray-400 dark:text-gray-600">&gt;</span>
            <span className="text-gray-900 dark:text-white font-medium">{name} Details</span>
          </div>
          )}

          {/* Header */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <h1 className={`${inline ? 'text-lg' : 'text-2xl'} font-bold text-gray-900 dark:text-white truncate`}>{name}</h1>
              {isPriority && (
                <svg className="w-5 h-5 text-amber-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              )}
              {/* Job role — muted, shown next to name (especially useful in inline/accordion mode where the info cards are hidden) */}
              {(candidate?.title || jobTitle) && (
                <span className="text-sm text-gray-400 dark:text-gray-500 truncate max-w-[280px]" title={candidate?.title || jobTitle}>
                  — {candidate?.title || jobTitle}
                </span>
              )}
              {!inline && candidate?.company && (
                <span className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[200px]" title={candidate.company}>
                  · {candidate.company}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              {/* View LinkedIn Profile - prominent button */}
              {candidate?.linkedin_profile_url && (
                <a
                  href={candidate.linkedin_profile_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all hover:shadow-sm"
                  style={{
                    borderColor: `${primaryColor}40`,
                    color: primaryColor,
                    backgroundColor: 'transparent',
                  }}
                  title="View LinkedIn Profile"
                >
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  LinkedIn
                </a>
              )}

              {/* Sync button — hidden in inline/accordion mode (sync is on overview level) */}
              {!inline && (
                <button
                  onClick={handleSyncEmails}
                  disabled={syncing}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all hover:shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{
                    borderColor: `${primaryColor}40`,
                    color: primaryColor,
                    backgroundColor: 'transparent',
                  }}
                  title="Sync Emails"
                >
                  <svg
                    className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      stroke="currentColor"
                    />
                  </svg>
                  {syncing ? 'Syncing…' : 'Sync'}
                </button>
              )}

              {/* Join Meet — visible when candidate has an upcoming interview with meet link */}
              {canJoinBookedInterview && bookedInterviewData?.google_meet_link && (
                <button
                  onClick={handleJoinMeet}
                  disabled={joinMeetLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs  text-white font-medium rounded-lg border transition-all hover:shadow-sm"
                  style={{
                    borderColor:`${primaryColor}40`,
                    backgroundColor: primaryColor,
                  }}
                  title="Join Google Meet"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {joinMeetLoading ? 'Joining...' : 'Join Meet'}
                </button>
              )}

              {!inline && syncMessage && (
                <span className={`text-xs font-medium ${syncMessage === 'Sync complete' ? 'text-green-500' : 'text-red-500'}`}>
                  {syncMessage}
                </span>
              )}
            </div>
          </div>

          {/* 4 Info Cards — hidden in inline/accordion mode (already shown in table row) */}
          {!inline && <div className="flex flex-wrap lg:flex-nowrap gap-4">
            {/* Status */}
            <div className="flex items-center gap-4 py-9 px-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 w-full sm:w-[calc(50%-8px)] lg:w-auto lg:flex-[2]">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
              </div>
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">Status</div>
                <div className="text-base font-bold text-gray-900 dark:text-white capitalize">
                  {formatCandidateStageLabel(resolvedCandidateStage || candidate?.status)}
                </div>
              </div>
            </div>

            {/* Resume match */}
            <div className="flex items-center gap-4 py-9 px-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 w-full sm:w-[calc(50%-8px)] lg:w-auto lg:flex-[1]">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
              </div>
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">Resume match</div>
                <div className="flex items-center gap-1.5">
                  <div className="text-base font-bold" style={{ color: Number(candidate?.match_percentage) >= 80 ? '#22C55E' : '#374151' }}>
                    {candidate?.match_percentage ? `${candidate.match_percentage}%` : '—'}
                  </div>
                  <ScoreDetailsInfo details={candidate?.score_details} candidateName={candidate?.name || candidate?.candidate_name || candidate?.full_name} align="left" title="Resume Match Details" />
                </div>
                <div className="mt-0.5 text-[10px] text-gray-400 dark:text-gray-500">Pre-interview fit score</div>
              </div>
            </div>

            {/* Source */}
            <div className="flex items-center gap-4 py-9 px-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 w-full sm:w-[calc(50%-8px)] lg:w-auto lg:flex-[1]">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>
              </div>
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">Source</div>
                <div className="text-base font-bold text-gray-900 dark:text-white">
                  {candidate?.source || 'Unknown'}
                </div>
              </div>
            </div>

            {/* Job Role */}
            <div className="flex items-start gap-4 py-9 px-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 w-full sm:w-[calc(50%-8px)] lg:w-auto lg:flex-[3]">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">Job Role</div>
                <div className="text-base font-bold text-gray-900 dark:text-white whitespace-normal break-words [overflow-wrap:anywhere]">
                  {candidate?.title || '—'}
                </div>
              </div>
            </div>
          </div>}



          {/* 4 Detail Sections */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Contact Information */}
            <div className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Contact Information</h3>
              <div className="space-y-2 text-sm">
                {email && (
                  <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                    <span className="text-gray-400">•</span>
                    <span>Email: <a href={`mailto:${email}`} className="hover:underline" style={{ color: primaryColor }}>{email}</a></span>
                  </div>
                )}
                {candidate?.phone && (
                  <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                    <span className="text-gray-400">•</span>
                    <span>Phone: {candidate.phone}</span>
                  </div>
                )}
                {candidate?.location && (
                  <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                    <span className="text-gray-400">•</span>
                    <span>Location: {candidate.location}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Candidate Details */}
            <div className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Candidate Details</h3>
              <div className="space-y-2 text-sm">
                {candidate?.company && (
                  <div className="text-gray-700 dark:text-gray-300">
                    <span className="text-gray-400">•</span> Current Company: <span className="font-medium">{candidate.company}</span>
                  </div>
                )}
                {candidate?.title && (
                  <div className="text-gray-700 dark:text-gray-300">
                    <span className="text-gray-400">•</span> Current Role: <span className="font-medium break-words [overflow-wrap:anywhere]">{candidate.title}</span>
                  </div>
                )}
                {candidate?.experience_years && (
                  <div className="text-gray-700 dark:text-gray-300">
                    <span className="text-gray-400">•</span> Experience: <span className="font-medium">{candidate.experience_years}+ years</span>
                  </div>
                )}
                {candidate?.education && candidate.education.length > 0 && (
                  <div className="text-gray-700 dark:text-gray-300">
                    <span className="text-gray-400">•</span> Education: <span className="font-medium">{candidate.education[0]?.degree || candidate.education[0]?.school || 'N/A'}</span>
                  </div>
                )}
                {candidate?.skills && candidate.skills.length > 0 && (
                  <div className="text-gray-700 dark:text-gray-300">
                    <span className="text-gray-400">•</span> Primary Skills: <span className="font-medium">{candidate.skills.slice(0, 4).map(s => typeof s === 'string' ? s : s?.name || '').filter(Boolean).join(', ')}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Engagement Summary */}
            <div className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Engagement Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="text-gray-700 dark:text-gray-300">
                  <span className="text-gray-400">•</span> {isImported ? 'Invitation Sent' : 'Outreach Sent'}: <span className="font-medium">{engagement.outreachSent}</span>
                </div>
                <div className="text-gray-700 dark:text-gray-300">
                  <span className="text-gray-400">•</span> Replies: <span className="font-medium">{engagement.repliesReceived}</span>
                </div>
                <div className="text-gray-700 dark:text-gray-300">
                  <span className="text-gray-400">•</span> Resume Uploaded: <span className="font-medium">{engagement.hasResume ? 'Yes' : 'No'}</span>
                </div>
                <div className="text-gray-700 dark:text-gray-300">
                  <span className="text-gray-400">•</span> Response Time: <span className="font-medium">{engagement.responseTime}</span>
                </div>
                {candidate?.source && (
                  <div className="text-gray-700 dark:text-gray-300">
                    <span className="text-gray-400">•</span> Source: <span className="font-medium">{candidate.source}</span>
                  </div>
                )}
                {candidate?.experience_years && (
                  <div className="text-gray-700 dark:text-gray-300">
                    <span className="text-gray-400">•</span> Experience: <span className="font-medium">{candidate.experience_years}+ years</span>
                  </div>
                )}
                {candidate?.skills && candidate.skills.length > 0 && (
                  <div className="text-gray-700 dark:text-gray-300">
                    <span className="text-gray-400">•</span> Top Skills: <span className="font-medium">{candidate.skills.slice(0, 3).map(s => typeof s === 'string' ? s : s?.name || '').filter(Boolean).join(', ')}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Internal Notes (Team-only) */}
            <div className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Internal Notes (Team-only)</h3>
              <div className="space-y-2 text-sm max-h-32 overflow-y-auto">
                {notes.length > 0 ? notes.map((n, i) => (
                  <div key={i} className="text-gray-700 dark:text-gray-300">
                    <span className="text-gray-400">•</span> {n.text}
                    {n.created_at && (
                      <span className="text-xs text-gray-400 ml-1">
                        ({new Date(n.created_at).toLocaleDateString()})
                      </span>
                    )}
                  </div>
                )) : (
                  <p className="text-xs text-gray-400 dark:text-gray-500 italic">No notes yet</p>
                )}
              </div>
              <button
                onClick={() => setNotesModalOpen(true)}
                className="text-xs font-medium hover:underline"
                style={{ color: primaryColor }}
              >
                Add Note
              </button>
            </div>
          </div>

          {/* Conversation History */}
          <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 ${inline ? 'p-4' : 'p-6'}`}>
            <h4 className={`text-sm font-semibold text-gray-900 dark:text-white ${inline ? 'mb-2' : 'mb-4'} flex items-center gap-2`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              Conversation History
              {timelineEvents.length > 0 && (
                <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-1.5 py-0.5 rounded-full">{timelineEvents.filter(e => !e.isDivider).length}</span>
              )}
            </h4>
            {loadingReplies ? (
              <MessageHistorySkeleton />
            ) : timelineEvents.length > 0 ? (
              <div className="relative">
                {/* Vertical timeline line */}
                <div className="absolute left-[15px] top-3 bottom-3 w-0.5 bg-gray-200 dark:bg-gray-700" />

                <div className={inline ? 'space-y-2' : 'space-y-4'}>
                  {timelineEvents.map((evt, idx) => {
                    // Render phase divider
                    if (evt.isDivider) {
                      const dividerConfig = {
                        outreach: {
                          text: 'Initial Outreach',
                          bg: 'bg-blue-50 dark:bg-blue-900/30',
                          border: 'border-blue-200 dark:border-blue-700',
                          textColor: 'text-blue-600 dark:text-blue-400',
                          lineColor: 'bg-blue-200 dark:bg-blue-700',
                          icon: (<svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" /></svg>),
                        },
                        followup: {
                          text: 'Follow-up',
                          bg: 'bg-amber-50 dark:bg-amber-900/30',
                          border: 'border-amber-200 dark:border-amber-700',
                          textColor: 'text-amber-600 dark:text-amber-400',
                          lineColor: 'bg-amber-200 dark:bg-amber-700',
                          icon: (<svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" /></svg>),
                        },
                        interview: {
                          text: 'Interview Scheduling',
                          bg: 'bg-green-50 dark:bg-green-900/30',
                          border: 'border-green-200 dark:border-green-700',
                          textColor: 'text-green-600 dark:text-green-400',
                          lineColor: 'bg-green-200 dark:bg-green-700',
                          icon: (<svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" /></svg>),
                        },
                        resume: {
                          text: 'Resume Request',
                          bg: 'bg-purple-50 dark:bg-purple-900/30',
                          border: 'border-purple-200 dark:border-purple-700',
                          textColor: 'text-purple-600 dark:text-purple-400',
                          lineColor: 'bg-purple-200 dark:bg-purple-700',
                          icon: (<svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" /></svg>),
                        },
                        conversation: {
                          text: 'Conversation',
                          bg: 'bg-gray-50 dark:bg-gray-800',
                          border: 'border-gray-200 dark:border-gray-700',
                          textColor: 'text-gray-500 dark:text-gray-400',
                          lineColor: 'bg-gray-200 dark:bg-gray-700',
                          icon: (<svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" /></svg>),
                        },
                      };
                      const cfg = dividerConfig[evt.label] || dividerConfig.conversation;
                      return (
                        <div key={`divider-${idx}`} className="relative flex items-center gap-3 pl-0 py-2">
                          <div className="w-[30px] flex items-center justify-center flex-shrink-0">
                            <div className="w-2.5 h-2.5 rounded-full bg-gray-300 dark:bg-gray-600 ring-2 ring-white dark:ring-gray-900" />
                          </div>
                          <div className="flex-1 flex items-center gap-2">
                            <div className={`h-px flex-1 ${cfg.lineColor}`} />
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider border ${cfg.bg} ${cfg.border} ${cfg.textColor}`}>
                              {cfg.icon}
                              {cfg.text}
                            </span>
                            <div className={`h-px flex-1 ${cfg.lineColor}`} />
                          </div>
                        </div>
                      );
                    }

                    return (
                    <div key={idx} className="relative flex items-start gap-4 pl-0">
                      {/* Timeline dot */}
                      <div className="relative z-10 flex-shrink-0 flex items-center justify-center">
                        <div
                          className="w-[30px] h-[30px] rounded-full flex items-center justify-center border-2"
                          style={{ borderColor: evt.color, backgroundColor: isDark ? '#1F2937' : '#FFFFFF' }}
                        >
                          <TimelineIcon type={evt.type} color={evt.color} />
                        </div>
                      </div>

                      {/* Event card with colored left border — clickable for detail */}
                      <div
                        className="flex-1 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-800 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                        style={{ borderLeft: `4px solid ${evt.color}` }}
                        onClick={() => setSelectedMessage(evt)}
                      >
                        <div className="px-4 py-3">
                          {/* Header row: Date - Event Type + direction badge */}
                          <div className="flex items-center justify-between flex-wrap gap-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                {formatDate(evt.date)} — {evt.label}
                              </span>
                              {evt.isSent && (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300">
                                  Sent
                                </span>
                              )}
                              {evt.isViewed && (
                                <span className="flex items-center gap-0.5 text-xs font-medium text-green-600 dark:text-green-400">
                                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M18 7l-1.41-1.41-6.34 6.34 1.41 1.41L18 7zm4.24-1.41L11.66 16.17 7.48 12l-1.41 1.41L11.66 19l12-12-1.42-1.41zM.41 13.41L6 19l1.41-1.41L1.83 12 .41 13.41z"/></svg>
                                  Viewed
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Content preview — skip greeting */}
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1.5 leading-relaxed whitespace-pre-wrap line-clamp-2">
                            {(() => {
                              const preview = getPreviewContent(evt.content);
                              return preview.length > 150 ? `${preview.slice(0, 150)}...` : preview;
                            })()}
                          </p>

                          {/* Attachment badge */}
                          {evt.hasAttachment && (
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-xs text-gray-500 dark:text-gray-400">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                                <span>Resume Attached</span>
                              </div>
                              {/* Resume parsed indicator — just a small badge, no button */}
                              {evt.hasResumeDoc && (
                                <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
                                  style={{ backgroundColor: '#22C55E20', color: '#22C55E' }}>
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  Analyzed
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className={`text-center ${inline ? 'py-4' : 'py-12'}`}>
                <svg className={`${inline ? 'w-8 h-8 mb-2' : 'w-16 h-16 mb-4'} mx-auto text-gray-300 dark:text-gray-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">No conversation history</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  {email ? 'Conversations will appear here once emails are synced.' : 'No email address available.'}
                </p>
              </div>
            )}
          </div>

          {/* Interview Questions — separate card below conversation */}
          {resolvedCandidateStage === 'shortlisted'
            && !isFullyAIInterview
            && !hasCompletedInterviewSession
            && !hasCompletedBookedInterview
            && (() => {
              if (loadingInterview) {
                return (
                  <div id="interview-questions-section" className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                      Interview Questions
                    </h4>
                    <div className="flex flex-col items-center justify-center py-12">
                      <svg className="w-8 h-8 animate-spin text-gray-400 mb-3" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Loading interview…</p>
                    </div>
                  </div>
                );
              }

              if (!activeSession || !activeSession.questions?.length) {
                return (
                  <div id="interview-questions-section" className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                      Interview Questions
                    </h4>
                    <div className="text-center py-12">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">No interview questions yet</p>
                    <button
                      onClick={() => handleGenerateInterview(false)}
                      disabled={generatingInterview}
                      className="mt-3 px-4 py-2 text-sm font-semibold rounded-lg text-white transition-all hover:shadow-md"
                      style={{ backgroundColor: secondaryColor }}
                    >
                      📋 Generate AI Interview
                    </button>
                  </div>
                  </div>
                );
              }

              const filteredQs = getFilteredQuestions();
              const displayQs = showAllQuestions ? filteredQs : filteredQs.slice(0, 5);
              const allQs = activeSession.questions || [];
              const isCompleted = activeSession.status === 'completed';
              const isPending = activeSession.status === 'sent' || activeSession.status === 'in_progress';

              return (
                <div id="interview-questions-section" className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    Interview Questions
                    {activeSession?.questions?.length > 0 && (
                      <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-1.5 py-0.5 rounded-full">{activeSession.questions.length}</span>
                    )}
                  </h4>
                  {/* Header with session selector */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {/* Status badge */}
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                        isCompleted ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        isPending ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                        'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      }`}>
                        {isCompleted ? '✅ Completed' : isPending ? '⏳ Pending' : '📝 Draft'}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{allQs.length} questions</span>
                    </div>
                    {/* Session selector (if multiple) */}
                    {interviewSessions.length > 1 && (
                      <select
                        value={activeSession.interview_id}
                        onChange={(e) => {
                          const s = interviewSessions.find(s => s.interview_id === parseInt(e.target.value));
                          if (s) { setActiveSession(s); setShowAllQuestions(false); setInterviewSourceTab('all'); setInterviewSectionFilter('all'); }
                        }}
                        className="text-xs border rounded-lg px-2 py-1 bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600"
                      >
                        {interviewSessions.map((s, i) => (
                          <option key={s.interview_id} value={s.interview_id}>
                            Session #{s.session_number || (interviewSessions.length - i)} — {s.status} — {new Date(s.created_at).toLocaleDateString()}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Completed interview banner */}
                  {isCompleted && (
                    <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-200 dark:border-green-800">
                      <p className="text-sm text-green-700 dark:text-green-400 font-medium">✅ This interview has been completed.</p>
                    </div>
                  )}

                  {/* Pending interview banner */}
                  {isPending && (
                    <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/10 rounded-lg border border-amber-200 dark:border-amber-800">
                      <p className="text-sm text-amber-700 dark:text-amber-400 font-medium">⏳ Interview link has been sent. Waiting for candidate to complete.</p>
                    </div>
                  )}

                  {/* Answer type stats */}
                  <div className="mb-3 flex items-center gap-2">
                    {allQs.filter(q => q.answer_type === 'mcq').length > 0 && (
                      <span className="text-[10px] font-bold bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 px-1.5 py-0.5 rounded">MCQ: {allQs.filter(q => q.answer_type === 'mcq').length}</span>
                    )}
                    {allQs.filter(q => q.answer_type === 'text').length > 0 && (
                      <span className="text-[10px] font-bold bg-orange-50 dark:bg-orange-900/20 text-orange-600 px-1.5 py-0.5 rounded">Text: {allQs.filter(q => q.answer_type === 'text').length}</span>
                    )}
                    {allQs.filter(q => q.answer_type === 'code').length > 0 && (
                      <span className="text-[10px] font-bold bg-gray-800 text-green-400 px-1.5 py-0.5 rounded">Code: {allQs.filter(q => q.answer_type === 'code').length}</span>
                    )}
                  </div>

                  {/* Section Tabs */}
                  {activeSession.sections && activeSession.sections.length > 0 && (
                    <div className="flex gap-1 mb-4 p-1 bg-gray-100 dark:bg-gray-700 rounded-lg flex-wrap">
                      {[
                        { key: 'all', label: 'All', count: allQs.length },
                        ...(activeSession.sections || []).map(sec => ({
                          key: sec.name,
                          label: sec.name,
                          count: allQs.filter(q => (q.section || q.category || '') === sec.name).length
                        }))
                      ].map(tab => (
                        <button
                          key={tab.key}
                          onClick={() => { setInterviewSectionFilter(tab.key); setShowAllQuestions(false); }}
                          className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-all ${
                            interviewSectionFilter === tab.key
                              ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-900 dark:text-white'
                              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                          }`}
                        >
                          {tab.label} ({tab.count})
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Questions List */}
                  <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
                    {displayQs.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No questions match the selected filters.</p>
                    ) : displayQs.map((q, idx) => (
                      <div key={q.id || idx} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-100 dark:border-gray-600">
                        <div className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
                            style={{ backgroundColor: (q.difficulty || '').toLowerCase() === 'easy' ? '#10B981' : (q.difficulty || '').toLowerCase() === 'medium' ? '#F59E0B' : '#EF4444' }}>
                            {q.question_number || idx + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="text-xs font-medium px-2 py-0.5 rounded"
                                style={{
                                  backgroundColor: (q.difficulty || '').toLowerCase() === 'easy' ? '#10B98115' : (q.difficulty || '').toLowerCase() === 'medium' ? '#F59E0B15' : '#EF444415',
                                  color: (q.difficulty || '').toLowerCase() === 'easy' ? '#10B981' : (q.difficulty || '').toLowerCase() === 'medium' ? '#F59E0B' : '#EF4444'
                                }}>
                                {q.difficulty}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{q.type}</span>
                              {q.answer_type && (
                                <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                                  q.answer_type === 'mcq' ? 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400' :
                                  q.answer_type === 'code' ? 'bg-gray-800 dark:bg-gray-900 text-green-400' :
                                  'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400'
                                }`}>{q.answer_type === 'mcq' ? 'MCQ' : q.answer_type === 'code' ? '</>' : 'Text'}</span>
                              )}
                              {q.skill && (
                                <span className="text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded">{q.skill}</span>
                              )}
                              <span className={`text-xs px-2 py-0.5 rounded ${q.source === 'jd_based' ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400' : 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400'}`}>
                                {q.source === 'jd_based' ? 'JD' : 'Candidate'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-900 dark:text-white font-medium leading-relaxed">{q.question}</p>

                            {/* MCQ options preview */}
                            {q.answer_type === 'mcq' && q.options && showAllQuestions && (
                              <div className="mt-2 space-y-1">
                                {q.options.map((opt, oi) => (
                                  <div key={oi} className={`flex items-center gap-2 text-xs px-2 py-1 rounded ${
                                    opt.is_correct
                                      ? 'bg-green-50 dark:bg-green-900/10 text-green-700 dark:text-green-400 font-semibold'
                                      : 'text-gray-600 dark:text-gray-400'
                                  }`}>
                                    <span className={`w-4 h-4 rounded-full border flex items-center justify-center text-[10px] ${
                                      opt.is_correct ? 'border-green-500 bg-green-500 text-white' : 'border-gray-300'
                                    }`}>
                                      {opt.is_correct ? '✓' : String.fromCharCode(65 + oi)}
                                    </span>
                                    <span>{opt.label}</span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Expected Answer */}
                            {showAllQuestions && q.expected_answer && (
                              <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/10 rounded border border-green-100 dark:border-green-800">
                                <p className="text-xs font-medium text-green-700 dark:text-green-400 mb-1">Expected Answer:</p>
                                <p className="text-xs text-green-600 dark:text-green-300">{q.expected_answer}</p>
                              </div>
                            )}

                            {/* Evaluation hints */}
                            {showAllQuestions && q.evaluation_hints && q.evaluation_hints.length > 0 && (
                              <div className="mt-1.5 p-2 bg-blue-50 dark:bg-blue-900/10 rounded border border-blue-100 dark:border-blue-800">
                                <p className="text-xs font-medium text-blue-700 dark:text-blue-400 mb-1">💡 Evaluation Hints:</p>
                                <ul className="list-disc list-inside space-y-0.5">
                                  {q.evaluation_hints.map((hint, hi) => (
                                    <li key={hi} className="text-xs text-blue-600 dark:text-blue-300">{hint}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Marking scheme */}
                            {showAllQuestions && q.marking_scheme && (
                              <div className="mt-1 flex items-center gap-3 flex-wrap">
                                <span className="text-xs text-gray-500 dark:text-gray-400">Marks: {q.marking_scheme.total_marks || 10}</span>
                                {q.time_limit_seconds && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400">Time: {Math.ceil(q.time_limit_seconds / 60)} min</span>
                                )}
                                {q.marking_scheme.criteria && q.marking_scheme.criteria.length > 0 && (
                                  <span className="text-xs text-gray-400 dark:text-gray-500">Criteria: {q.marking_scheme.criteria.join(' · ')}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Show more / less toggle */}
                  {filteredQs.length > 5 && (
                    <button
                      onClick={() => setShowAllQuestions(!showAllQuestions)}
                      className="mt-3 text-sm font-medium hover:underline w-full text-center py-2"
                      style={{ color: primaryColor }}
                    >
                      {showAllQuestions ? 'Show less' : `+ View all ${filteredQs.length} questions`}
                    </button>
                  )}

                  {/* Actions Bar */}
                  <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all hover:shadow-md"
                      style={{ backgroundColor: primaryColor }}
                      onClick={() => {
                        const qs = getFilteredQuestions();
                        const text = qs.map((q, i) =>
                          `Q${q.question_number || i + 1} [${q.difficulty}] [${q.type}] [${q.source === 'jd_based' ? 'JD' : 'Candidate'}]:\n${q.question}`
                        ).join('\n\n');
                        navigator.clipboard.writeText(text).then(() => showToast('Questions copied!', 'success')).catch(() => showToast('Failed to copy', 'error'));
                      }}
                    >
                      📋 Copy Questions
                    </button>

                    <button
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:shadow-md"
                      style={{ backgroundColor: '#10B981', color: 'white' }}
                      onClick={() => setShowAllQuestions(!showAllQuestions)}
                    >
                      {showAllQuestions ? 'Collapse' : `👁️ View All ${filteredQs.length}`}
                    </button>

                    <button
                      className="px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all hover:shadow-sm disabled:opacity-50"
                      style={{ borderColor: secondaryColor, color: secondaryColor }}
                      disabled={generatingInterview}
                      onClick={() => handleGenerateInterview(true)}
                    >
                      {generatingInterview ? '⏳ Generating…' : '➕ New Interview Session'}
                    </button>

                    {activeSession.interview_token && (
                      <button
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:shadow-md"
                        style={{ backgroundColor: '#6366F1', color: 'white' }}
                        onClick={() => {
                          const link = `${window.location.origin}/interview/take/${activeSession.interview_token}`;
                          navigator.clipboard.writeText(link)
                            .then(() => showToast('Interview link copied! Share with the candidate.', 'success'))
                            .catch(() => showToast('Failed to copy link', 'error'));
                        }}
                      >
                        🔗 Copy Interview Link
                      </button>
                    )}
                  </div>

                  {/* Interview History (if multiple sessions) */}
                  {interviewSessions.length > 1 && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">Interview History ({interviewSessions.length} sessions)</h4>
                      <div className="space-y-2">
                        {interviewSessions.map((s, i) => (
                          <button
                            key={s.interview_id}
                            onClick={() => { setActiveSession(s); setShowAllQuestions(false); setInterviewSourceTab('all'); setInterviewSectionFilter('all'); }}
                            className={`w-full text-left flex items-center justify-between p-2 rounded-lg border transition-all text-xs ${
                              activeSession?.interview_id === s.interview_id
                                ? 'border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700'
                                : 'border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-700 dark:text-gray-300">
                                Session #{s.session_number || (interviewSessions.length - i)}
                              </span>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                s.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                s.status === 'sent' || s.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {s.status}
                              </span>
                              <span className="text-gray-400">{s.question_count || s.questions?.length || 0} questions</span>
                            </div>
                            <span className="text-gray-400">{new Date(s.created_at).toLocaleDateString()}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

          {/* Rejected banner — shows the recruiter's rejection reason */}
          {isRejected && (
            <div className="mb-4 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 dark:border-rose-900/50 dark:bg-rose-900/15 dark:text-rose-300">
              <svg viewBox="0 0 24 24" className="mt-0.5 h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" /></svg>
              <div className="min-w-0">
                <span className="font-semibold">Rejected by recruiter</span>
                {rejectionReason
                  ? <p className="mt-0.5 break-words text-rose-700 dark:text-rose-300/90">{rejectionReason}</p>
                  : <p className="mt-0.5 text-rose-600/80 dark:text-rose-300/70">No reason recorded.</p>}
              </div>
            </div>
          )}

          {/* Funnel Stage + Suggested Next Step */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Current Funnel Stage */}
            <div
              className="p-4 rounded-xl border"
              style={{ 
                backgroundColor: `${secondaryColor}15`, 
                borderColor: secondaryColor
              }}
            >
              <h5 className="text-xs font-semibold mb-2" style={{ color: '#111827' }}>Current Funnel Stage</h5>
              <div className="flex items-center gap-2 text-sm flex-wrap">
                {funnel.stages.map((stage, idx) => {
                  const isActive = idx === funnel.active;
                  const isPast = idx < funnel.active;
                  const isDeclinedStage = funnel.isDeclined && stage === 'Declined';
                  const isRejectedStage = funnel.isRejected && stage === 'Rejected';
                  const stageColor = (isDeclinedStage || isRejectedStage) ? '#EF4444' : secondaryColor;
                  
                  return (
                    <React.Fragment key={stage}>
                      <span
                        className={`whitespace-nowrap ${isActive ? 'font-bold' : isPast ? 'font-medium' : 'font-normal'}`}
                        style={{ 
                          color: isActive || isPast ? stageColor : '#9CA3AF'
                        }}
                      >
                        {stage}
                      </span>
                      {idx < funnel.stages.length - 1 && (
                        <span style={{ color: isPast ? stageColor : '#D1D5DB' }}>→</span>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* Suggested Next Step */}
            <div
              className="p-4 rounded-xl border"
              style={{ 
                backgroundColor: `${secondaryColor}15`, 
                borderColor: secondaryColor
              }}
            >
              <h5 className="text-xs font-semibold mb-2" style={{ color: '#111827' }}>Suggested Next Step</h5>
              <p className="text-sm font-medium mb-3" style={{ color: primaryColor }}>
                {suggestedStep}
              </p>
              {/* Generate / View AI Interview Button — show only at Shortlisted stage so interviewer can prepare questions */}
              {resolvedCandidateStage === 'shortlisted'
                && !isFullyAIInterview
                && !hasCompletedInterviewSession
                && !hasCompletedBookedInterview
                && (
              <button
                onClick={() => {
                  if (!activeSession && !generatingInterview && !loadingInterview) {
                    handleGenerateInterview(false);
                  }
                  document.getElementById('interview-questions-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                disabled={generatingInterview || loadingInterview}
                className="flex items-center gap-2 w-full px-4 py-2.5 text-sm font-semibold rounded-lg transition-all hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: (generatingInterview || loadingInterview) ? `${secondaryColor}30` : secondaryColor,
                  color: '#fff',
                }}
                title={activeSession ? 'View existing interview questions' : 'Generate AI Interview Questions using GPT-4o'}
              >
                <svg
                  className={`w-4 h-4 ${(generatingInterview || loadingInterview) ? 'animate-spin' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {(generatingInterview || loadingInterview) ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  )}
                </svg>
                {generatingInterview ? 'Generating Questions…' : loadingInterview ? 'Loading…' : activeSession ? `📋 View Interview (${activeSession.question_count || activeSession.questions?.length || 0} Qs)` : '📋 Generate AI Interview'}
              </button>
              )}
            </div>
          </div>

          {/* Reschedule Request Banner */}
          {rescheduleRequest && (
            <div className="mb-4 p-4 rounded-xl border-2 border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-bold text-amber-700 dark:text-amber-400">Reschedule Request</span>
                {rescheduleRequest.status === 'needs_manual_action' && (
                  <span className="ml-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-red-100 text-red-700">Needs Action</span>
                )}
              </div>
              {rescheduleRequest.requested_date && (
                <p className="text-xs text-amber-800 dark:text-amber-300 mb-1">
                  <strong>Requested Date:</strong> {rescheduleRequest.requested_date}
                </p>
              )}
              {rescheduleRequest.extracted_info && (() => {
                const info = typeof rescheduleRequest.extracted_info === 'string'
                  ? (() => { try { return JSON.parse(rescheduleRequest.extracted_info); } catch { return {}; } })()
                  : rescheduleRequest.extracted_info;
                return info.reason ? (
                  <p className="text-xs text-amber-800 dark:text-amber-300 mb-1">
                    <strong>Reason:</strong> {info.reason}
                  </p>
                ) : null;
              })()}
              {rescheduleRequest.original_date && (
                <p className="text-xs text-amber-700 dark:text-amber-400 mb-2">
                  <strong>Original Slot:</strong> {rescheduleRequest.original_date} at {rescheduleRequest.original_time || '—'}
                </p>
              )}
              <div className="flex gap-3 mt-2">
                <button
                  onClick={handleApproveReschedule}
                  disabled={rescheduleActionLoading}
                  className="px-5 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:shadow-md disabled:opacity-60"
                  style={{ backgroundColor: '#10B981' }}
                >
                  {rescheduleActionLoading ? 'Processing…' : '✓ Approve Reschedule'}
                </button>
                <button
                  onClick={handleRejectReschedule}
                  disabled={rescheduleActionLoading}
                  className="px-5 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:shadow-md disabled:opacity-60"
                  style={{ backgroundColor: '#EF4444' }}
                >
                  {rescheduleActionLoading ? 'Processing…' : '✗ Decline'}
                </button>
              </div>
            </div>
          )}

          {missedInterviewData && !bookedInterviewData && (
            <div className="p-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-900/20 dark:text-amber-200">
              <p className="text-sm font-semibold">Previous interview was not attended</p>
              <p className="mt-1 text-xs">
                {missedInterviewData.scheduled_date || 'Scheduled interview'}
                {missedInterviewData.scheduled_time ? ` at ${formatTime12hr(missedInterviewData.scheduled_time)}` : ''} was marked missed. You can reject the candidate or book another interview.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-center gap-4 pb-6 flex-wrap">
              {/* Seamless loader: hold the action row until the booked-interview state is known, so the
                  primary button doesn't flash "Book Interview" and then swap to "Interview Report". */}
              {bookedInterviewLoading ? (
                <div className="flex items-center justify-center gap-4 py-1" aria-busy="true" aria-label="Loading actions">
                  {[0, 1].map((i) => (
                    <div key={i} className="h-11 w-40 rounded-lg bg-gray-200/70 dark:bg-gray-700/60 animate-pulse" />
                  ))}
                </div>
              ) : (
              <>
              {/* Meeting Analysis Button - Show once the candidate reaches interview stage, even if notes are still loading */}
              {canShowMeetingAnalysisButton && (
                <button
                  onClick={() => setMeetingAnalysisModalOpen(true)}
                  className="px-8 py-3 rounded-lg border-2 text-sm font-semibold transition-all hover:shadow-md"
                  style={{ borderColor: secondaryColor, color: secondaryColor, backgroundColor: 'transparent' }}
                >
                  <span className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {visibleMeetingAnalysisCount > 0 ? `Interview Report (${visibleMeetingAnalysisCount})` : 'Interview Report'}
                  </span>
                </button>
              )}

            {!isTerminalOut && (
            <button
              onClick={handleMarkAsPriority}
              disabled={priorityLoading}
              className="px-8 py-3 rounded-lg border-2 text-sm font-semibold transition-all hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                borderColor: primaryColor,
                color: isPriority ? '#fff' : primaryColor,
                backgroundColor: isPriority ? primaryColor : 'transparent',
              }}
            >
              {priorityLoading ? 'Updating…' : isPriority ? '★ Priority' : 'Mark As Priority'}
            </button>
            )}

            {/* Scene 2: Reply to Question — visible when a draft is ready */}
            {!isTerminalOut && hasQuestionDraft && (
              <button
                onClick={handleOpenReplyDraft}
                className="relative px-8 py-3 rounded-lg text-sm font-semibold transition-all hover:shadow-md text-white"
                style={{ backgroundColor: primaryColor }}
              >
                Reply to Question
                {/* Red badge */}
                <span className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold shadow">
                  1
                </span>
              </button>
            )}

            {/* Send Follow-up — only visible when initial outreach has been sent */}
            {canUseOutreach && !isTerminalOut && engagement.hasOutreach && (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleOpenFollowupDraft()}
                  className="px-8 py-3 rounded-lg border-2 text-sm font-semibold transition-all hover:shadow-md"
                  style={{ borderColor: primaryColor, color: primaryColor, backgroundColor: 'transparent' }}
                >
                  Send Follow-up
                </button>
                {/* Outreach Info Card */}
                <div className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="text-xs text-blue-700 dark:text-blue-300 font-medium">
                    {engagement.outreachSent} {isImported ? 'invitation sent' : 'outreach sent'}
                  </div>
                  <div className="text-xs text-blue-600 dark:text-blue-400">
                    {engagement.repliesReceived} replies received
                  </div>
                </div>
              </div>
            )}

            {/* View Resume — visible when candidate has a resume (doc_id present) */}
            {engagement.hasResume && (() => {
              const replyWithDoc = replies.find((r) => r.doc_id);
              const replyWithAttachment = replies.find((r) => r.attachment_location);
              const docId = replyWithDoc?.doc_id || candidate.doc_id;
              const hasAttachmentButNoDoc = !docId && (replyWithAttachment?.attachment_location);

              if (docId) {
                return (
                  <button
                    onClick={() => {
                      handleViewResume(docId, replyWithAttachment?.attachment_location);
                    }}
                    className="px-8 py-3 rounded-lg border-2 text-sm font-semibold transition-all hover:shadow-md flex items-center gap-2"
                    style={{ borderColor: primaryColor, color: primaryColor, backgroundColor: 'transparent' }}
                    title="View parsed resume & match score"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    View Resume
                  </button>
                );
              }

              if (hasAttachmentButNoDoc) {
                return (
                  <div className="relative group">
                    <button
                      className="px-8 py-3 rounded-lg border-2 text-sm font-semibold transition-all hover:shadow-md flex items-center gap-2 border-amber-400 text-amber-600 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-600 dark:text-amber-400"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Resume Pending
                    </button>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 p-3 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-xl shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                      <p className="font-semibold mb-1">Resume Not Yet Processed</p>
                      <p className="text-gray-300">This candidate's resume was received but hasn't been parsed yet due to API quota limits.</p>
                      <p className="text-gray-400 mt-1">Retry email sync from the chat or check your AI service quota.</p>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 w-2 h-2 bg-gray-900 dark:bg-gray-700 rotate-45" />
                    </div>
                  </div>
                );
              }

              return null;
            })()}

            {!isTerminalOut && resolvedCandidateStage === 'interested' && !engagement.hasResume && (
              <button
                onClick={openResumeRequestModal}
                className="px-8 py-3 rounded-lg border-2 text-sm font-semibold transition-all hover:shadow-md flex items-center gap-2"
                style={{ borderColor: primaryColor, color: primaryColor, backgroundColor: 'transparent' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Request Resume
              </button>
            )}

            {/* Schedule Interview button — hidden (use chat scheduling or bulk scheduling instead)
            {engagement.hasOutreach && funnel.active >= 2 && engagement.hasResume && !effectiveHasBookedInterview && (
              <Button
                variant="primary"
                size="lg"
                onClick={() => setScheduleModalOpen(true)}
              >
                Schedule Interview
              </Button>
            )}
            */}

            {/* Book Interview Directly — same conditions as Schedule Interview, plus imported candidates. */}
            {canUseInterviews && !isTerminalOut && !effectiveHasBookedInterview && (
              <button
                onClick={handleOpenDirectBook}
                className="px-8 py-3 rounded-lg border-2 text-sm font-semibold transition-all hover:shadow-md flex items-center gap-2"
                style={{ borderColor: primaryColor, color: primaryColor, backgroundColor: 'transparent' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Book Interview
              </button>
            )}

            {/* Send Initial Outreach — visible when NO outreach has been sent yet */}
            {canUseOutreach && !isTerminalOut && !engagement.hasOutreach && (
              <button
                onClick={handleOpenInitialOutreach}
                className="px-8 py-3 rounded-lg border-2 text-sm font-semibold transition-all hover:shadow-md flex items-center gap-2"
                style={{ borderColor: primaryColor, color: primaryColor, backgroundColor: 'transparent' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Send Initial Outreach
              </button>
            )}

            {/* Reject — hidden while an interview is scheduled/pending: its time window is open
                (interviewWindowOpen) OR the stage is Shortlisted (booking advances here; it returns once
                the interview completes → the stage advances to Interviewed). */}
            {canReject && !interviewWindowOpen && resolvedCandidateStage !== 'shortlisted' && (
              <button
                onClick={() => setRejectTarget(candidate)}
                className="px-8 py-3 rounded-lg border-2 text-sm font-semibold transition-all hover:shadow-md flex items-center gap-2 border-rose-300 text-rose-600 hover:bg-rose-50 dark:border-rose-900/60 dark:text-rose-400 dark:hover:bg-rose-900/20"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Reject
              </button>
            )}
              </>
              )}
          </div>

          {/* Declined banner */}
          {funnel.isDeclined && (
            <div className="p-4 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-center">
              <span className="text-sm font-semibold text-red-600 dark:text-red-400">⛔ Candidate Declined</span>
              <p className="text-xs text-red-500 dark:text-red-400 mt-1">This candidate has declined the opportunity.</p>
            </div>
          )}

          {/* Resume Request Modal — rendered via portal to cover full viewport */}
          {resumeRequestModal.isOpen && createPortal(
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl mx-4 p-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Request Resume from {name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">No resume found for this candidate. Send a request email:</p>
                {resumeRequestModal.loading ? (
                  <div className="flex flex-col items-center justify-center py-8 gap-3">
                    <div className="w-10 h-10 rounded-full animate-spin" style={{ border: `3px solid ${primaryColor}30`, borderTopColor: primaryColor }} />
                    <p className="text-sm text-gray-400">Generating draft…</p>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Subject</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        value={resumeRequestModal.draftSubject}
                        onChange={(e) => setResumeRequestModal((prev) => ({ ...prev, draftSubject: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Body</label>
                      <textarea
                        rows={6}
                        className="w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        value={resumeRequestModal.draftBody}
                        onChange={(e) => setResumeRequestModal((prev) => ({ ...prev, draftBody: e.target.value }))}
                      />
                    </div>
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => setResumeRequestModal({ isOpen: false, draftSubject: '', draftBody: '', loading: false })}
                        className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSendResumeRequest(resumeRequestModal.draftSubject, resumeRequestModal.draftBody)}
                        className="px-4 py-2 text-sm rounded-lg text-white font-medium"
                        style={{ backgroundColor: primaryColor }}
                      >
                        Send Request
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>,
            document.body
          )}

          {/* Draft Message Modal (Scenes 2 & 3) */}

          {/* Initial Outreach Modal */}
          {initialOutreachModal.isOpen && createPortal(
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl mx-4 p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Send Initial Outreach to {name}</h3>
                  <button
                    onClick={() => setInitialOutreachModal({ isOpen: false, draftSubject: '', draftBody: '', loading: false, sending: false, noEmail: false })}
                    className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                {initialOutreachModal.noEmail ? (
                  <div className="flex flex-col items-center justify-center py-8 gap-4">
                    <div className="w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                      <svg className="w-7 h-7 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <div className="text-center space-y-2">
                      <p className="text-base font-semibold text-gray-900 dark:text-white">No Email Address Found</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
                        This candidate doesn't have an email address on file. You can add one using the
                        <strong> Change Mail</strong> option, or reach out to them via LinkedIn if a profile URL is available.
                      </p>
                    </div>
                    <button
                      onClick={() => setInitialOutreachModal({ isOpen: false, draftSubject: '', draftBody: '', loading: false, sending: false, noEmail: false })}
                      className="mt-2 px-6 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  To: <span className="font-medium text-gray-700 dark:text-gray-300">{email}</span>
                </p>
                {initialOutreachModal.loading ? (
                  <div className="flex flex-col items-center justify-center py-8 gap-3">
                    <div className="w-10 h-10 rounded-full animate-spin" style={{ border: `3px solid ${primaryColor}30`, borderTopColor: primaryColor }} />
                    <p className="text-sm text-gray-400">Generating outreach draft…</p>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Subject</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                        value={initialOutreachModal.draftSubject}
                        onChange={(e) => setInitialOutreachModal((prev) => ({ ...prev, draftSubject: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Body</label>
                      <textarea
                        rows={8}
                        className="w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                        value={initialOutreachModal.draftBody}
                        onChange={(e) => setInitialOutreachModal((prev) => ({ ...prev, draftBody: e.target.value }))}
                      />
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                      <button
                        onClick={() => setInitialOutreachModal({ isOpen: false, draftSubject: '', draftBody: '', loading: false, sending: false, noEmail: false })}
                        className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSendInitialOutreach(initialOutreachModal.draftSubject, initialOutreachModal.draftBody)}
                        disabled={initialOutreachModal.sending || !initialOutreachModal.draftSubject.trim() || !initialOutreachModal.draftBody.trim()}
                        className="px-6 py-2 text-sm rounded-lg text-white font-medium transition-all hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                        style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                      >
                        {initialOutreachModal.sending ? (
                          <>
                            <div className="w-4 h-4 rounded-full animate-spin border-2 border-white/30 border-t-white" />
                            Sending…
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                            Send Outreach
                          </>
                        )}
                      </button>
                    </div>
                  </>
                )}
                  </>
                )}
              </div>
            </div>,
            document.body
          )}

          {/* Followup Cooldown Warning Modal */}
          {followupWarningModal.isOpen && createPortal(
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Follow-up Detected</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Last email sent {followupWarningModal.lastSentAt
                        ? (() => {
                            const hrs = Math.floor((Date.now() - new Date(followupWarningModal.lastSentAt).getTime()) / 3600000);
                            return hrs < 1 ? 'less than 1 hour ago' : `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
                          })()
                        : 'recently'}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  You sent a follow-up email to this candidate within the last {followupCooldownHours} hour{followupCooldownHours === 1 ? '' : 's'}. Sending too frequently may reduce engagement. Are you sure you want to send another follow-up?
                </p>
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    onClick={() => setFollowupWarningModal({ isOpen: false, lastSentAt: null })}
                    className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setFollowupWarningModal({ isOpen: false, lastSentAt: null });
                      handleOpenFollowupDraft(true); // Skip warning on retry
                    }}
                    className="px-4 py-2 text-sm rounded-lg text-white font-medium"
                    style={{ backgroundColor: '#F59E0B' }}
                  >
                    Send Anyway
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

          <DraftMessageModal
            isOpen={draftModal.isOpen}
            onClose={() => setDraftModal((prev) => ({ ...prev, isOpen: false }))}
            title={draftModal.title}
            subject={draftModal.subject}
            body={draftModal.body}
            candidateEmail={email}
            candidateName={name}
            candidateQuestion={draftModal.scene === 'reply' ? draftModal.candidateQuestion : ''}
            loading={draftLoading}
            sending={draftSending}
            onSend={handleDraftSend}
            onRegenerate={handleRegenerate}
            scene={draftModal.scene}
            onMoveToInterested={draftModal.scene === 'reply' ? handleMoveToInterested : null}
          />

          {/* Toast notification */}
          <Toast
            message={toast.message}
            type={toast.type}
            visible={toast.visible}
            onClose={closeToast}
          />

          {/* Interview Scheduler — single candidate only */}
          <InterviewScheduler
            isOpen={scheduleModalOpen}
            onClose={() => setScheduleModalOpen(false)}
            searchId={resolvedSearchId}
            candidates={[{ name, email, phone: candidate?.phone || '' }]}
            onScheduleComplete={(scheduledResult) => {
              setScheduleModalOpen(false);
              syncCandidateStageLocally('Shortlisted', {
                interview_status: scheduledResult?.status || 'scheduled',
                booking_status: scheduledResult?.booking_status || 'pending',
                session_id: scheduledResult?.session_id || sessionId || null,
              });
              setHasBookedInterview(true);
              showToast(`Interview scheduled for ${name}!`, 'success');
              window.dispatchEvent(new CustomEvent('candidateDetailRefresh'));
            }}
            jobTitle={jobTitle}
            enableInterviewTypeChoice
            featureAccess={featureAccess}
          />

          {/* Direct Book Interview Modal */}
          {directBookOpen && createPortal(
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className={`rounded-2xl shadow-2xl w-full max-w-xl mx-4 max-h-[90vh] overflow-y-auto ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                {/* Header */}
                <div className="px-6 py-4 border-b" style={{ borderColor: isDark ? '#374151' : '#E5E7EB', background: isDark ? '#1F2937' : '#F9FAFB' }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Direct Interview Booking</h3>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Skips slot invitation — books immediately</p>
                      </div>
                    </div>
                    <button onClick={() => setDirectBookOpen(false)} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Candidate Info Card */}
                <div className="mx-6 mt-4 mb-2 p-3 rounded-xl border" style={{ borderColor: isDark ? '#374151' : '#E5E7EB', backgroundColor: isDark ? '#111827' : '#F3F4F6' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold text-white flex-shrink-0" style={{ backgroundColor: primaryColor }}>
                      {(name || 'C').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{name}</p>
                      <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{email}</p>
                    </div>
                  </div>
                </div>

                {/* Form */}
                <div className="px-6 py-4 space-y-4">
                  {/* Interview type: Manual (human + Google Meet) or AI (Wiza, launch link) */}
                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Interview type</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { key: 'manual', label: 'Manual', sub: 'Human + Google Meet' },
                        {
                          key: 'ai',
                          label: 'AI (Wiza)',
                          sub: directBookAiSetups.length > 0 ? 'Choose saved setup' : 'Set up below',
                        },
                      ].filter((opt) => (opt.key === 'manual' ? canUseManualInterview : canUseAiInterview)).map(opt => {
                        const active = directBookForm.interviewType === opt.key;
                        const disabled = false;   // AI is always selectable — if the job has no saved setup, a quick inline setup is shown
                        return (
                          <button
                            key={opt.key}
                            type="button"
                            disabled={disabled}
                            onClick={() => setDirectBookForm(prev => ({
                              ...prev,
                              interviewType: opt.key,
                              duration: opt.key === 'ai' && directBookAiSetupDuration
                                ? directBookAiSetupDuration
                                : prev.duration,
                            }))}
                            className={`text-left p-3 rounded-xl border transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                            style={{
                              borderColor: active ? primaryColor : (isDark ? '#374151' : '#E5E7EB'),
                              backgroundColor: active ? `${primaryColor}12` : 'transparent',
                            }}
                          >
                            <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{opt.label}</div>
                            <div className={`text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{opt.sub}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Date & Time Row */}
                  <div className="grid grid-cols-2 gap-4">
                    <InputBox
                      label="Date"
                      type="date"
                      value={directBookForm.date}
                      onChange={e => setDirectBookForm(prev => ({ ...prev, date: e.target.value }))}
                      required
                      min={new Date().toISOString().split('T')[0]}
                    />
                    <InputBox
                      label="Start Time"
                      type="time"
                      value={directBookForm.startTime}
                      onChange={e => setDirectBookForm(prev => ({ ...prev, startTime: e.target.value }))}
                      required
                    />
                  </div>

                  {/* AI setup picker — selected setup controls duration and booking payload. */}
                  {directBookForm.interviewType === 'ai' && directBookAiSetupsLoading ? (
                    <div className={`rounded-lg border px-3 py-2 text-sm ${isDark ? 'border-gray-700 bg-gray-900 text-gray-300' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
                      Loading saved AI setups...
                    </div>
                  ) : directBookForm.interviewType === 'ai' && directBookAiSetups.length > 0 ? (
                    <div>
                      <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>AI interview setup</label>
                      <select
                        value={directBookAiSetup?.setup_id ? String(directBookAiSetup.setup_id) : ''}
                        onChange={(event) => {
                          const selected = directBookAiSetups.find(setup => String(setup.setup_id) === String(event.target.value));
                          selectDirectBookAiSetup(selected || null);
                        }}
                        disabled={directBookAiSetupsLoading}
                        className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 ${isDark ? 'border-gray-600 bg-gray-900 text-gray-200 focus:ring-indigo-500/40' : 'border-gray-200 bg-white text-gray-800 focus:ring-indigo-200'}`}
                      >
                        <option value="">{directBookAiSetupsLoading ? 'Loading setups...' : 'Choose setup'}</option>
                        {directBookAiSetups.map((setup) => {
                          const duration = Number(setup?.configuration?.duration_minutes || setup?.duration_minutes || 0);
                          return (
                            <option key={setup.setup_id} value={String(setup.setup_id)}>
                              {getAiSetupName(setup)}{duration ? ` - ${duration} min` : ''}
                            </option>
                          );
                        })}
                      </select>
                      <div className={`mt-2 px-3 py-2 rounded-lg border text-xs ${isDark ? 'border-gray-700 text-gray-300 bg-gray-900/60' : 'border-gray-200 text-gray-600 bg-gray-50'}`}>
                        <span className="font-semibold">{getAiSetupName(directBookAiSetup)}</span>
                        {directBookAiSetupDuration ? <span> · Duration: {directBookAiSetupDuration} min</span> : null}
                        {directBookAiSetup?.total_questions != null ? <span> · {directBookAiSetup.total_questions} questions</span> : null}
                      </div>
                    </div>
                  ) : (
                    <DropdownBox
                      label="Duration"
                      value={`${directBookForm.duration} minutes`}
                      options={['15 minutes', '30 minutes', '45 minutes', '60 minutes', '90 minutes']}
                      onChange={(val) => setDirectBookForm(prev => ({ ...prev, duration: parseInt(val) }))}
                      required
                    />
                  )}

                  {/* Quick AI setup — ONLY when booking AI for a job that has no saved AI interview yet.
                      Creates a real config on booking (the AI covers the job's JD/resume). */}
                  {directBookForm.interviewType === 'ai' && directBookAiSetups.length === 0 && !directBookAiSetup && (
                    <div className={`rounded-xl border p-3 space-y-3 ${isDark ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50'}`}>
                      <div className={`text-xs font-semibold ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Set up the AI interview for this job
                        <span className={`ml-1 font-normal ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>— this job has no saved AI setup yet</span>
                      </div>
                      <InputBox
                        label="Interview name"
                        type="text"
                        value={directBookAiConfig.name}
                        placeholder={`${jobTitle || 'Role'} — AI Interview`}
                        onChange={e => setDirectBookAiConfig(prev => ({ ...prev, name: e.target.value }))}
                      />
                      <DropdownBox
                        label="Number of questions"
                        value={`${directBookAiConfig.total_questions} questions`}
                        options={['5 questions', '8 questions', '10 questions', '12 questions', '15 questions']}
                        onChange={(val) => setDirectBookAiConfig(prev => ({ ...prev, total_questions: parseInt(val) }))}
                      />
                      <div>
                        <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>What should the AI focus on? <span className="opacity-60">(optional)</span></label>
                        <textarea
                          rows={3}
                          value={directBookAiConfig.description}
                          placeholder="e.g. Core React & JavaScript, problem solving, and past project experience. Leave blank to cover the job description."
                          onChange={e => setDirectBookAiConfig(prev => ({ ...prev, description: e.target.value }))}
                          className={`w-full px-3 py-2 rounded-lg border text-sm resize-none focus:outline-none ${isDark ? 'border-gray-600 bg-gray-900 text-gray-200 placeholder-gray-500' : 'border-gray-200 bg-white text-gray-800 placeholder-gray-400'}`}
                        />
                      </div>
                    </div>
                  )}

                  {/* Interviewer — human only; AI is run by the shared Wiza interviewer */}
                  {directBookForm.interviewType !== 'ai' && (
                    <DropdownBox
                      label="Interviewer"
                      value={
                        directBookForm.interviewerId
                          ? (() => { const iw = directBookInterviewers.find(i => i.id === directBookForm.interviewerId); return iw ? `${iw.name} (${iw.email})` : ''; })()
                          : ''
                      }
                      placeholder="Select interviewer"
                      options={directBookInterviewers.map(iw => `${iw.name} (${iw.email})`)}
                      onChange={(val) => {
                        const matched = directBookInterviewers.find(iw => `${iw.name} (${iw.email})` === val);
                        setDirectBookForm(prev => ({ ...prev, interviewerId: matched?.id || '' }));
                      }}
                    />
                  )}

                  {/* Info note — differs by interview type */}
                  <div className={`flex items-start gap-2 p-3 rounded-lg text-xs ${isDark ? 'bg-blue-900/20 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                    <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span>
                      {directBookForm.interviewType === 'ai'
                        ? (directBookAiSetup
                          ? `The selected setup (${getAiSetupName(directBookAiSetup)}) will run automatically. The candidate gets an email with a launch link for the chosen time — no Google Meet or interviewer needed.`
                          : "The AI interview (Wiza) will be set up from the details above and run automatically. The candidate gets an email with a launch link — no Google Meet or interviewer needed.")
                        : 'A Google Meet link will be created and a confirmation email with the meeting details will be sent to the candidate.'}
                    </span>
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="px-6 py-4 border-t flex justify-end gap-3" style={{ borderColor: isDark ? '#374151' : '#E5E7EB', background: isDark ? '#1F2937' : '#F9FAFB' }}>
                  <Button variant="outline" size="md" onClick={() => setDirectBookOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="md"
                    onClick={handleDirectBookSubmit}
                    disabled={directBookLoading || directBookAiSetupsLoading || !directBookForm.date || !directBookForm.startTime}
                    loading={directBookLoading}
                  >
                    {directBookLoading ? 'Booking...' : 'Confirm Booking'}
                  </Button>
                </div>
              </div>
            </div>,
            document.body,
          )}

          {/* Notes Modal — rendered via portal */}
          {notesModalOpen && createPortal(
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl mx-4 p-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Add Note for {name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Add an internal note about this candidate. Notes are visible to your team only.</p>
                <textarea
                  rows={4}
                  className="w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2"
                  style={{ focusRingColor: primaryColor }}
                  placeholder="Type your note here..."
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                  autoFocus
                />
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => { setNotesModalOpen(false); setNoteInput(''); }}
                    className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddNote}
                    disabled={!noteInput.trim() || noteSaving}
                    className="px-4 py-2 text-sm rounded-lg text-white font-medium disabled:opacity-50"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {noteSaving ? 'Saving...' : 'Save Note'}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Resume Check Modal — rendered via portal */}
          {resumeCheckModalOpen && createPortal(
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl mx-4 p-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Resume Required</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <strong>{name}</strong> has not submitted a resume yet. Would you like to request their resume before scheduling an interview?
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setResumeCheckModalOpen(false)}
                    className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      setResumeCheckModalOpen(false);
                      openResumeRequestModal();
                    }}
                    className="px-4 py-2 text-sm rounded-lg text-white font-medium"
                    style={{ backgroundColor: primaryColor }}
                  >
                    Request Resume
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Message Detail Modal — rendered via portal */}

          {/* Resume View Modal — shows parsed resume data + match score */}
          {resumeViewModal.isOpen && createPortal(
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setResumeViewModal({ isOpen: false, loading: false, data: null, error: null, attachmentUrl: null })}>
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-7xl mx-4 max-h-[95vh] flex flex-col overflow-hidden border border-gray-200/50 dark:border-gray-700/50" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between flex-shrink-0 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800/80 dark:to-gray-900">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: `linear-gradient(135deg, ${primaryColor}20, ${primaryColor}08)`, border: `1px solid ${primaryColor}30` }}>
                      <svg className="w-5 h-5" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white tracking-tight">Resume Details</h3>
                      <p className="text-xs text-gray-400 dark:text-gray-500">Analyzed candidate profile</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {resumeViewModal.attachmentUrl && (
                      <a
                        href={resumeViewModal.attachmentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold text-white transition-all hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
                        style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)` }}
                        title="View original resume PDF"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        View PDF
                      </a>
                    )}
                    <button onClick={() => setResumeViewModal({ isOpen: false, loading: false, data: null, error: null, attachmentUrl: null })} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                      <svg className="w-5 h-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                </div>

                {/* Body */}
                <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 space-y-5 bg-gray-50/50 dark:bg-gray-900/50">
                  {resumeViewModal.loading && (
                    <div className="flex flex-col items-center justify-center py-24 gap-4">
                      <div className="w-12 h-12 rounded-full animate-spin" style={{ border: `3px solid ${primaryColor}20`, borderTopColor: primaryColor }} />
                      <p className="text-sm text-gray-400 font-medium">Loading resume data…</p>
                    </div>
                  )}

                  {resumeViewModal.error && (
                    <div className="text-center py-20">
                      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                        <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                      </div>
                      <p className="text-sm text-red-500 font-medium">{resumeViewModal.error}</p>
                    </div>
                  )}

                  {resumeViewModal.data && (() => {
                    const { resume, basic_detail, skills, work_experience, education, certifications, match_score } = resumeViewModal.data;
                    const scoreValue = typeof match_score === 'number' ? match_score : match_score?.overall_score;
                    const scorePercent = scoreValue != null ? Math.round(scoreValue * 100) : null;
                    const scoreColor = scorePercent >= 80 ? '#22C55E' : scorePercent >= 60 ? '#F59E0B' : scorePercent >= 40 ? '#EF8A21' : '#EF4444';
                    const expMonths = basic_detail?.total_experience_in_months || 0;
                    const expYears = expMonths >= 12 ? `${Math.floor(expMonths / 12)} yr${Math.floor(expMonths / 12) !== 1 ? 's' : ''} ${expMonths % 12 ? `${expMonths % 12} mo` : ''}` : `${expMonths} mo`;

                    return (
                      <>
                    {/* Top Section: Candidate Header Card + Resume Match */}
                        <div className="flex gap-4">
                          {/* Candidate Profile Card */}
                          <div className="flex-1 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/60 p-5 shadow-sm">
                            <div className="flex items-start gap-4">
                              <div className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold text-white flex-shrink-0 shadow-md" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}bb)` }}>
                                {(resume?.name || '?')[0]?.toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-lg font-bold text-gray-900 dark:text-white truncate">{resume?.name || 'Unknown Candidate'}</h4>
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
                                  {resume?.email && (
                                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                                      {resume.email}
                                    </span>
                                  )}
                                  {resume?.phone && (
                                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                                      {resume.phone}
                                    </span>
                                  )}
                                  {resume?.location && (
                                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                      {resume.location}
                                    </span>
                                  )}
                                </div>
                                {/* Info pills */}
                                <div className="flex flex-wrap gap-2 mt-3">
                                  {expMonths > 0 && (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-blue-50 dark:bg-blue-900/20 text-xs font-medium text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800/30">
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                                      {expYears}
                                    </span>
                                  )}
                                  {basic_detail?.education && (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-purple-50 dark:bg-purple-900/20 text-xs font-medium text-purple-700 dark:text-purple-300 border border-purple-100 dark:border-purple-800/30">
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 14l9-5-9-5-9 5 9 5z" /><path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" /></svg>
                                      {basic_detail.education}
                                    </span>
                                  )}
                                  {resume?.resume_source && (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-700/40 text-xs font-medium text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600/30">
                                      Source: {resume.resume_source}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                      {/* Resume Match Card */}
                          {scorePercent != null && (
                            <div className="w-44 flex-shrink-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/60 p-4 shadow-sm flex flex-col items-center justify-center text-center">
                              <div className="relative">
                                <svg className="w-[80px] h-[80px] -rotate-90" viewBox="0 0 64 64">
                                  <circle cx="32" cy="32" r="28" fill="none" stroke={isDark ? '#1F2937' : '#F3F4F6'} strokeWidth="5" />
                                  <circle cx="32" cy="32" r="28" fill="none" stroke={scoreColor} strokeWidth="5" strokeDasharray={`${scorePercent * 1.76} 176`} strokeLinecap="round" />
                                </svg>
                                <span className="absolute inset-0 flex items-center justify-center text-xl font-extrabold" style={{ color: scoreColor }}>{scorePercent}%</span>
                              </div>
                              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mt-2">JD Match</p>
                              <p className="text-[10px] font-medium mt-0.5" style={{ color: scoreColor }}>
                                {scorePercent >= 80 ? 'Excellent' : scorePercent >= 60 ? 'Good' : scorePercent >= 40 ? 'Moderate' : 'Weak'}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Achievements / Profile Summary */}
                        {resume?.achievements && (
                          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/60 p-5 shadow-sm">
                            <h4 className="text-xs uppercase tracking-widest font-bold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
                              <svg className="w-3.5 h-3.5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>
                              Key Achievements
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">{resume.achievements}</p>
                          </div>
                        )}

                        {/* Skills */}
                        {skills && skills.length > 0 && (
                          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/60 p-5 shadow-sm">
                            <h4 className="text-xs uppercase tracking-widest font-bold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
                              <svg className="w-3.5 h-3.5" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
                              Skills <span className="text-gray-400 dark:text-gray-500 ml-1">(({skills.length})</span>
                            </h4>
                            <div className="flex flex-wrap gap-1.5">
                              {skills.map((s, idx) => (
                                <span key={idx} className="px-2.5 py-1 rounded-md text-xs font-medium transition-colors" style={{ backgroundColor: `${primaryColor}0a`, color: primaryColor, border: `1px solid ${primaryColor}20` }}>
                                  {typeof s === 'string' ? s : s.skill_name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Two-column layout for Education + Certifications */}
                        {((education && education.length > 0) || (certifications && certifications.length > 0)) && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Education */}
                            {education && education.length > 0 && (
                              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/60 p-5 shadow-sm">
                                <h4 className="text-xs uppercase tracking-widest font-bold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
                                  <svg className="w-3.5 h-3.5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 14l9-5-9-5-9 5 9 5z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" /></svg>
                                  Education <span className="text-gray-400 dark:text-gray-500 ml-1">(({education.length})</span>
                                </h4>
                                <div className="space-y-3">
                                  {education.map((edu, idx) => (
                                    <div key={idx} className="pl-3 border-l-2 border-indigo-300 dark:border-indigo-600">
                                      <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{edu.degree || 'Degree'}</p>
                                      {edu.college_name && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{edu.college_name}</p>}
                                      {edu.graduation_date && <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{edu.graduation_date}</p>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Certifications */}
                            {certifications && certifications.length > 0 && (
                              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/60 p-5 shadow-sm">
                                <h4 className="text-xs uppercase tracking-widest font-bold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
                                  <svg className="w-3.5 h-3.5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg>
                                  Certifications <span className="text-gray-400 dark:text-gray-500 ml-1">(({certifications.length})</span>
                                </h4>
                                <div className="space-y-3">
                                  {certifications.map((cert, idx) => (
                                    <div key={idx} className="pl-3 border-l-2 border-amber-300 dark:border-amber-600">
                                      <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{cert.name || 'Certification'}</p>
                                      {cert.issued_by && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Issued by {cert.issued_by}</p>}
                                      {cert.date && <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{cert.date}</p>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Work Experience */}
                        {work_experience && work_experience.length > 0 && (
                          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/60 p-5 shadow-sm">
                            <h4 className="text-xs uppercase tracking-widest font-bold text-gray-700 dark:text-gray-200 mb-4 flex items-center gap-2">
                              <svg className="w-3.5 h-3.5" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                              Work Experience <span className="text-gray-400 dark:text-gray-500 ml-1">(({work_experience.length})</span>
                            </h4>
                            <div className="space-y-3">
                              {work_experience.map((we, idx) => (
                                <div key={idx} className="relative pl-4 border-l-2 dark:border-gray-700" style={{ borderLeftColor: primaryColor }}>
                                  <div className="absolute -left-[5px] top-1 w-2 h-2 rounded-full" style={{ backgroundColor: primaryColor }} />
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{we.position || 'Position'}</p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{we.employer || '—'}{we.location ? ` · ${we.location}` : ''}</p>
                                      {(we.start_date || we.end_date) && (
                                        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{we.start_date || '?'} – {we.end_date || 'Present'}</p>
                                      )}
                                    </div>
                                    {(we.duration || we.experience_in_months) && (
                                      <span className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700/60 text-gray-500 dark:text-gray-400 flex-shrink-0">{we.duration || `${we.experience_in_months} mo`}</span>
                                    )}
                                  </div>
                                  {we.responsibilities && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 leading-relaxed">{we.responsibilities}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Additional Details */}
                        {(resume?.potential_roles || resume?.industry_backgrounds || resume?.companies_worked_at) && (
                          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/60 p-5 shadow-sm">
                            <h4 className="text-xs uppercase tracking-widest font-bold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
                              <svg className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              Additional Details
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                              {resume.potential_roles && (
                                <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/30">
                                  <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500 mb-1">Potential Roles</p>
                                  <p className="text-xs text-gray-700 dark:text-gray-300">{resume.potential_roles}</p>
                                </div>
                              )}
                              {resume.industry_backgrounds && (
                                <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/30">
                                  <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500 mb-1">Industries</p>
                                  <p className="text-xs text-gray-700 dark:text-gray-300">{resume.industry_backgrounds}</p>
                                </div>
                              )}
                              {resume.companies_worked_at && (
                                <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/30">
                                  <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500 mb-1">Companies</p>
                                  <p className="text-xs text-gray-700 dark:text-gray-300">{resume.companies_worked_at}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>,
            document.body
          )}
          {selectedMessage && createPortal(
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setSelectedMessage(null)}>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0" style={{ borderLeft: `4px solid ${selectedMessage.color}` }}>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center border-2 flex-shrink-0"
                      style={{ borderColor: selectedMessage.color, backgroundColor: isDark ? '#1F2937' : '#FFFFFF' }}
                    >
                      <TimelineIcon type={selectedMessage.type} color={selectedMessage.color} />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">{selectedMessage.label}</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(selectedMessage.date)}</p>
                    </div>
                  </div>
                  <button onClick={() => setSelectedMessage(null)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                {/* Subject */}
                {selectedMessage.subject && (
                  <div className="px-6 py-2 bg-gray-50 dark:bg-gray-700 border-b border-gray-100 dark:border-gray-600 flex-shrink-0">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Subject: </span>
                    <span className="text-sm text-gray-900 dark:text-white">{selectedMessage.subject}</span>
                  </div>
                )}
                {/* Body — full message content with proper HTML rendering */}
                <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
                  {selectedMessage.content && selectedMessage.content.includes('<') && selectedMessage.content.includes('>') ? (
                    <div
                      className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed break-words [&_p]:mb-3 [&_p]:leading-relaxed [&_br]:leading-loose [&_a]:text-blue-600 [&_a]:underline"
                      dangerouslySetInnerHTML={{ __html: selectedMessage.content }}
                    />
                  ) : (
                    <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap break-words">{selectedMessage.content}</div>
                  )}
                </div>
                {/* Footer */}
                {selectedMessage.hasAttachment && (
                  <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-xs text-gray-600 dark:text-gray-400">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                      <span>{selectedMessage.attachmentInfo}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>,
            document.body
          )}
          
          {/* Meeting Analysis — shared modal (AI interview result, or manual meeting analysis) */}
          {meetingAnalysisModalOpen && (
            <MeetingAnalysisModal
              open
              onClose={() => setMeetingAnalysisModalOpen(false)}
              candidate={candidate}
              scheduledInterviewId={isFullyAIInterview ? bookedInterviewData?.id : null}
              aiResult={isFullyAIInterview ? aiInterviewResult : null}
              analysis={isFullyAIInterview ? null : primaryManualAnalysis}
              transcript={isFullyAIInterview ? '' : primaryManualNote?.notes?.transcript || ''}
              manualInterviewId={isFullyAIInterview ? null : primaryManualNote?.interview_id}
              loading={isFullyAIInterview ? aiInterviewResultLoading : false}
              error={isFullyAIInterview ? aiInterviewResultError : ''}
              onRecalculate={handleRecalculateAIInterviewResult}
              recalculating={aiInterviewRecalculating}
              onReject={canReject ? () => { setMeetingAnalysisModalOpen(false); setRejectTarget(candidate); } : null}
            />
          )}

          <RejectCandidateModal
            open={!!rejectTarget}
            candidate={rejectTarget}
            searchId={resolvedSearchId}
            sessionId={sessionId || candidate?.session_id || candidate?.sessionId || ''}
            onClose={() => setRejectTarget(null)}
            onRejected={() => {
              setRejectTarget(null);
              // Parent decides how to refresh (overview reloads its list; chat drops the candidate
              // from the sourcing panel). Then clear/collapse this view — the record is now terminal.
              onRejected?.(candidate);
              onBack?.();
            }}
          />
          {activeHighlightModal && createPortal(
            <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setActiveHighlightModal(null)}>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">{activeHighlightModal.title}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Starts at {activeHighlightModal.timestampLabel}
                    </p>
                  </div>
                  <button onClick={() => setActiveHighlightModal(null)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="p-6">
                  <video
                    ref={highlightModalVideoRef}
                    controls
                    preload="metadata"
                    playsInline
                    className="w-full rounded-xl bg-black"
                    style={{ maxHeight: '75vh' }}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>,
            document.body
          )}

        </div>
      </div>
  );
};

export default CandidateDetail;
